import type { Translations } from './es';

export const en: Translations = {
  gatePage: {
    title: 'Gate',
    subtitle: 'Check a crew attendance list and log vehicle passes.',
    people: 'People',
    vehicles: 'Vehicles',
    codeLabel: 'Pass code (scanner or typed)',
    codeHint: 'With a USB or Bluetooth scanner, keep the cursor here and scan the QR.',
    search: 'Look up',
    camera: 'Use camera',
    cameraOff: 'Close camera',
    cameraUnsupported: 'Could not open the camera. Check the browser permission, or use a scanner or type the code.',
    invalidCode: 'Invalid code, or the list has expired (24 h).',
    group: 'Group',
    closedAt: 'List closed',
    present: 'Present on the list',
    countedLabel: 'People you counted',
    entry: 'Entry',
    exit: 'Exit',
    confirm: 'Confirm count',
    match: 'Match: the count is correct.',
    mismatch: (exp: number, got: number) => `Mismatch: the list says ${exp} and you counted ${got}. Logged as an incident.`,
    plate: 'Plate',
    driver: 'Driver',
    company: 'Company',
    occupants: 'Occupants',
    note: 'Note',
    register: 'Log pass',
    allowed: 'Authorized vehicle.',
    visitor: 'Vehicle not registered: logged as a visitor.',
    authorizeTitle: 'Register authorized vehicle',
    validUntil: 'Valid until',
    save: 'Save vehicle',
    saved: 'Vehicle saved.',
    logTitle: "Today's log",
    colTime: 'Time',
    colType: 'Type',
    colDetail: 'Detail',
    colResult: 'Result',
    emptyLog: 'No movements today.',
    faceTitle: 'Face recognition',
    inactiveInList: '{n} person(s) on the list were terminated. Notify HR.',
    status: { ACTIVE: 'Active', PRE_TERMINATION: 'Pre-termination', INACTIVE: 'Terminated', EXTRA: 'Extra' },
    cameraTitle: 'Camera',
    cameraHint: 'Open it once: it reads a QR or barcode by itself; for a face, use Identify.',
    openCameraFirst: 'Open the camera to identify a face.',
    faceHint: 'Open the camera, take the photo and confirm who it is. The photo is not stored.',
    faceCamera: 'Open face camera',
    identify: 'Identify',
    faceError: 'Could not identify. Move closer to the camera and try again.',
    letIn: 'Log entry',
    letOut: 'Log exit',
    pickCandidate: 'Not sure who this is. Pick the person:',
    terminatedPending: (n: string) => `${n} was terminated: request sent, someone with permission must authorize it.`,
    terminatedDenied: (n: string) => `${n} was terminated: access denied and logged.`,
    authReason: 'Reason for authorization',
    logAsVisitor: 'Log as visitor',
    authorizePass: 'Authorize pass',
    requestAuth: 'Request authorization',
    logDenied: 'Deny and log',
    resolved: 'Resolved',
    authorizedOk: 'Pass authorized.',
    saveRules: 'Save rules',
    rulesTitle: 'Rules for terminated people',
    rulesHint: 'Checked in order; the first match wins. Strict: not allowed. Visitor: logged as a visitor. Authorization: waits for someone with permission.',
    ruleRole: 'Role',
    ruleDirection: 'Entry/exit',
    ruleAction: 'Action',
    anyRole: 'Any role',
    anyDirection: 'Entry and exit',
    moveUp: 'Move up',
    moveDown: 'Move down',
    removeRule: 'Remove',
    addRule: 'Add rule',
    otherwise: 'If none match',
    rulesSaved: 'Rules saved.',
    policyLabels: { STRICT: 'Strict', VISITOR: 'Visitor', AUTHORIZE: 'With authorization' },
    terminated: '{name} has been terminated. Do not let them in and notify HR.',
    notActive: 'inactive',
    visitorName: 'Visitor name',
    logVisitor: 'Log visitor',
    tooSmall: 'The face looks too small: move closer to the camera.',
    personLogged: (n: string) => `Pass logged: ${n}.`,
    visitorLogged: (n: string) => `Visitor logged: ${n}.`,
    typePerson: 'Person',
    invalidVehicle: 'Check the data: the plate needs 4 to 10 letters or digits and occupants can be at most 200.',
    typeList: 'Attendance list',
    typeVehicle: 'Vehicle',
    results: { MATCH: 'Match', MISMATCH: 'Mismatch', ALLOWED: 'Authorized', VISITOR: 'Visitor', DENIED: 'Denied', PENDING: 'Pending', AUTHORIZED: 'Authorized' },
  },
  attendancePage: {
    personnelTitle: 'Personnel',
    attendanceTitle: 'Attendance',
    subtitlePersonnel: 'Staff roster, face enrollment, pre-terminations and biometric data deletion.',
    subtitleAttendance: 'Daily attendance lists by crew. Present, missing and the photo of each record.',
    addPerson: 'Add person',
    edit: 'Edit',
    editTitle: 'Edit person',
    save: 'Save',
    numberShort: 'Employee no.',
    fullName: 'Full name',
    employeeNumber: 'Employee number (optional)',
    colCrew: 'Crew',
    noCrew: 'No crew',
    colUser: 'System user',
    noUser: 'No user',
    crewHint: 'Crew',
    newCrewName: 'New crew (name)',
    createCrew: 'Create crew',
    deleteCrewConfirm: (n: string) => `Delete crew “${n}”? Past lists keep its name.`,
    add: 'Add',
    importTitle: 'Import from Excel',
    importHint: 'First sheet with a name column (Nombre, Empleado or Trabajador). Optional: number, crew.',
    chooseFile: 'Choose .xlsx file',
    preview: 'Review',
    confirmImport: 'Confirm import',
    toCreate: 'To create',
    alreadyExist: 'Already existed',
    importDone: 'People imported',
    errorsFound: 'Fix these errors and try again',
    rowLabel: (n: number) => `Row ${n}`,
    methods: { AUTO: 'Automatic', CONFIRMED: 'Confirmed', MANUAL: 'Manual', EXTRA: 'Extra person' },
    errors: {
      FEATURE_DISABLED: 'Face attendance is not enabled for your company.',
      FORBIDDEN: 'You do not have permission for this action.',
      NOT_FOUND: 'Not found. It may no longer exist.',
      DUPLICATE_NUMBER: 'That employee number already exists. Use another.',
      REASON_REQUIRED: 'Enter a reason to authorize.',
      HAS_ATTENDANCE: 'Cannot delete: they already have attendance. Terminate them instead.',
      INVALID_INPUT: 'Check the data and try again.',
      INVALID_BODY: 'The request is not valid.',
      INVALID_DECISION: 'The decision is not valid.',
      INVALID_FILE: 'The file is not valid.',
      FILE_TOO_LARGE: 'The file is too large.',
      INVALID_RANGE: 'The date range is not valid.',
      RANGE_TOO_LARGE: 'The date range is too large (93 days maximum).',
      CONSENT_REQUIRED: 'The person must sign the consent first.',
      SESSION_CLOSED: 'The list is already closed.',
      MISSING_UNDECIDED: 'Decide what happened with some people before closing.',
      NO_FACE: 'No face is visible. Move closer and check the light.',
      MULTIPLE_FACES: 'More than one person appeared.',
      FACE_TOO_SMALL: 'The face looks too small. Move closer.',
      EYES_NOT_VISIBLE: 'Both eyes must be visible.',
      LOW_QUALITY: 'The photo quality is not good enough. Take it again.',
      TOO_MANY_FACES: 'More people appeared than allowed per photo.',
      GATE_CODE_INVALID: 'Invalid code, or the list has expired (24 h).',
      USER_ALREADY_LINKED: 'That user is already linked to another person.',
      CREW_HAS_PEOPLE: 'This crew still has people. Reassign them first.',
    },
    genericError: 'The action could not be completed. Try again.',
    listLabel: 'List',
    pickList: (n: number) => `Pick a list (${n})`,
    filterSearch: 'Search by name or number',
    allCrews: 'All crews',
    allStatuses: 'All statuses',
    allEnrollment: 'Any enrollment',
    filterPending: 'Enrollment incomplete',
    clearFilters: 'Clear filters',
    noMatches: 'No one matches the filters.',
    validating: 'Checking…',
    importing: 'Importing…',
    colRow: 'Row',
    template: 'Download template',
    warnings: 'Warnings',
    colName: 'Name',
    colStatus: 'Status',
    colEnrollment: 'Enrollment',
    colMisses: 'Absences',
    colActions: 'Actions',
    statusACTIVE: 'Active',
    statusPRE_TERMINATION: 'Pre-termination',
    statusINACTIVE: 'Terminated',
    enrolled: 'Enrolled',
    noConsent: 'No consent',
    photosOf: 'photos',
    keep: 'Keep',
    terminate: 'Terminate',
    deleteBiometrics: 'Delete biometric data',
    deleteTitle: 'Delete biometric data of',
    deleteBody: 'The registered face, enrollment photos and attendance photos of this person are deleted. In group photos only their face is blacked out. Their name and attendance dates and times are kept. This cannot be undone.',
    typeName: 'Type the full name to confirm',
    reason: 'Reason (required)',
    cancel: 'Cancel',
    deletePerson: 'Delete person',
    deletePersonTitle: 'Delete',
    deletePersonBody: 'This removes the person\'s record, face and enrollment photos. Only possible if they never had attendance; otherwise terminate them. This cannot be undone.',
    confirmDelete: 'Delete biometric data',
    receiptTitle: 'Deletion receipt',
    receiptFaces: 'Faces deleted',
    receiptEnroll: 'Enrollment photos deleted',
    receiptAttendance: 'Attendance photos deleted',
    receiptId: 'Receipt',
    close: 'Close',
    loading: 'Loading…',
    empty: 'No people yet.',
    disabled: 'Face attendance is not enabled for your company. Ask a Super Admin to enable it.',
    date: 'Date',
    lote: 'Plot',
    sessionStatus: 'Status',
    open: 'Open',
    closed: 'Closed',
    present: 'Present',
    missing: 'Missing',
    extras: 'Extra people',
    noSessions: 'No lists for that date.',
    viewStamped: 'View stamped photo',
    viewEnrollPhotos: 'View enrollment photos',
    enrollPhotosTitle: 'Enrollment photos of',
    noEnrollPhotos: 'No enrollment photos.',
    sessionPhotos: 'Photos of this list',
    allStaff: 'All staff',
    destination: 'Destination',
    sessionNotes: 'Notes',
    noSessionPhotos: 'This list has no photos yet.',
    tabDay: "Daily lists",
    tabReport: "Report",
    from: "From",
    to: "To",
    allLotes: "All plots",
    download: "Download Excel",
    byPerson: "By person",
    byDay: "By day",
    totalSessions: "Lists",
    colPresent: "Present",
    colAbsent: "Absences",
    colJustified: "Justified",
    colUndecided: "Undecided",
    colPct: "Attendance %",
    colLate: "Late",
    colHours: "Hours",
    colExpected: "Expected",
    colExtras: "Extra",
    colDate: "Date",
    colLote: "Plot",
    reportHint: "Percentage = days present / (present + absences). Justified days count neither for nor against.",
    reportEmpty: "No lists in that period.",
    rangeError: "Check the dates: 93 days maximum and the start date cannot be after the end date.",
    extrasTotal: "Extra people",
    undecided: 'Undecided',
    absent: 'Absent',
    justified: 'Justified',
    back: 'Back',
  },
  evidencePage: {
    open: 'Open',
    download: 'Download',
    folders: 'Folders',
    empty: 'No evidence photos yet.',
  },
  invalidRequest: 'Invalid request',
  shipmentEvidence: {
    title: 'Shipment evidence',
    createTitle: 'Create link',
    label: 'Link name',
    days: 'Days valid (1 to 30)',
    stages: 'Stages included (empty = all)',
    stageNames: { LOADING: 'Loading', SEALING: 'Sealing', DEPARTURE: 'Departure', CHECKPOINT: 'Checkpoint', INSPECTION: 'Inspection', DELIVERY: 'Delivery', INCIDENT: 'Incident' },
    create: 'Create link',
    created: 'Link created. Copy it now: the token is not shown again.',
    copy: 'Copy link',
    shareTitle: 'Shared links',
    none: 'This shipment has no links.',
    active: 'Active',
    revoked: 'Revoked',
    expired: 'Expired',
    revoke: 'Revoke',
    expiresOn: 'Expires on',
    loading: 'Loading…',
    public: 'Shared evidence',
    invalid: 'The link is not valid or has expired.',
    noEvidence: 'No evidence.',
    stageLabel: 'Stage',
    takenAt: 'Taken on',
    open: 'Open',
  },
  budgetNotice: {
    title: 'Budget notice',
  },
  footer: {
    socialTitle: "Social media",
    networks: {
      facebook: "Facebook",
      instagram: "Instagram",
      linkedin: "LinkedIn",
      youtube: "YouTube",
      x: "X",
    },
    openBot: "Open the Agrovid bot",
    openQr: "Open on your phone",
    qrTitle: "Open Agrovid on your phone",
    qrHint: "Scan the code with your phone camera.",
    qrClose: "Close",
    legalNav: "Legal information",
    usagePolicy: "Usage policies",
    terms: "Terms and conditions",
    privacy: "Privacy notice",
    rights: (year: number) => `© ${year} Agrovid. All rights reserved.`,
  },
  legal: {
    contactLabel: "Contact",
    back: "← Back to start",
    updated: "Last updated: October 2026",
    draftNotice: "Draft pending legal review. This text is informational, does not replace legal advice and must not be considered final until validated.",
    docs: {
      usage: {
        title: "Usage policies",
        sections: [
          {
            heading: "1. Permitted use",
            body: "The platform is used to manage your company's farm operations: field records, inventory, finance and reports. Only people authorized by the company administrator may access it.",
          },
          {
            heading: "2. Prohibited conduct",
            body: "You may not share credentials, try to access other companies' data, knowingly enter false information, extract data in bulk or interfere with the service.",
          },
          {
            heading: "3. Responsibility for data",
            body: "Each company is responsible for the accuracy of what it records, including pesticide applications, harvests and evidence.",
          },
          {
            heading: "4. Suspension",
            body: "We may suspend accounts that breach these policies or put the security of the service or other users at risk.",
          },
        ],
      },
      copyright: {
        title: "Copyright",
        sections: [
          {
            heading: "1. Ownership",
            body: "The Agrovid software, design, trademarks, logos and texts belong to their owners and are protected by applicable copyright and industrial property laws.",
          },
          {
            heading: "2. License",
            body: "Each company receives a limited, non-exclusive, non-transferable license to use the platform for the term of its agreement. Copying, modifying, decompiling or redistributing the software is not allowed.",
          },
          {
            heading: "3. Company data",
            body: "The data each company records remains theirs. Agrovid only processes it to provide the service.",
          },
          {
            heading: "4. Claims",
            body: "If you believe content infringes copyright, write to the contact email describing the content and your ownership.",
          },
        ],
      },
      terms: {
        title: "Terms and conditions",
        sections: [
          {
            heading: "1. Acceptance",
            body: "By accessing or using Agrovid you accept these terms. If you use the platform on behalf of a company, you represent that you have authority to bind it.",
          },
          {
            heading: "2. Service and availability",
            body: "The web platform and Telegram bots are provided as is; we aim for high availability but do not guarantee uninterrupted or error-free service.",
          },
          {
            heading: "3. Accounts and security",
            body: "You are responsible for keeping your access confidential and for activity under your account. Tell us immediately if you suspect unauthorized use.",
          },
          {
            heading: "4. Limitation of liability",
            body: "Agronomic recommendations and calculations support decisions; the final decision and regulatory compliance rest with the user. To the extent permitted by law we are not liable for indirect damages.",
          },
          {
            heading: "5. Changes and governing law",
            body: "We may update these terms and will notify you on the platform. They are governed by the laws of the United Mexican States.",
          },
        ],
      },
      privacy: {
        title: "Privacy notice",
        sections: [
          {
            heading: "1. Controller",
            body: "Agrovid is responsible for processing your personal data under the Mexican Federal Law on Protection of Personal Data Held by Private Parties. Contact: the contact email shown at the end of this page.",
          },
          {
            heading: "2. Data we process",
            body: "Name, phone, email, Telegram identifier, role and activity on the platform, plus the operational information you record (fields, applications, inventory, finance) and photos or locations you attach as evidence.",
          },
          {
            heading: "3. Purposes",
            body: "To provide the service, authenticate you, keep traceability and regulatory compliance, send operational notices and improve the platform. We do not sell your personal data.",
          },
          {
            heading: "4. Transfers and providers",
            body: "We use cloud infrastructure and artificial intelligence providers to run the service, under confidentiality and security measures.",
          },
          {
            heading: "5. ARCO rights",
            body: "You may access, rectify, cancel or object to the processing of your data by writing to the contact email. We will reply within the legal deadlines.",
          },
          {
            heading: "6. Changes",
            body: "We will publish any change to this notice on this page.",
          },
        ],
      },
    },
  },
  nav: {
    myDay: "My day",
    groups: {
      campo: "Field",
      cosechaVenta: "Harvest & sales",
      recursos: "Resources",
      finanzas: "Finance",
      cumplimiento: "Compliance & reports",
      admin: "Administration",
    },
    sections: {
      campoOperacion: 'Field Operations',
      finanzasInversion: 'Finance & Investment',
      comercialLogistica: 'Commercial & Logistics',
      riesgoCumplimiento: 'Risk & Compliance',
      admin: 'Administration',
    },
    dashboard:  'Home',
    investorOverview: 'Overview',
    investorModules: 'Zones',
    investorYield: 'Yield',
    investorLogistics: 'Logistics',
    investorFunding: 'Investment and Funding',
    investorProduction: 'Production',
    tasks:      'Tasks',
    finance:    'Finance',
    fieldLots:  'Fields & Lots',
    irrigation: 'Irrigation',
    harvest:    'Harvest',
    inventory:  'Inventory',
    inventories: 'Bulk Upload',
    machinery:  'Machinery',
    spray:      'Applications',
    porcino:    'Swine',
    payroll:    'Payroll',
    reports:    'Reports',
    map:        'GIS Map',
    chat:       'AI Chat',
    users:      'Users',
    settings:   'Settings',
    config:     'Settings',
    gettingStarted: 'Getting started',
    companies:  'Companies',
    ranchos:    'Ranchos',
    system:     'System',
    bulkImport: 'Bulk Import',
    merma:       'Waste',
    crop:        'Crop Mgmt',
    // Sprint B
    export:      'Exports',
    transport:   'Transport',
    traceability:'Traceability',
    fsmaExport:  'FSMA 204 delivery',
    protocol:    'Protocols',
    permisos:    'Permissions',
    vocabulary:  'Vocabulary',
    drones:      'Drones',
    devices:     'Devices',
    sales:       'Sales',
    documents:   'Documents',
    evidence:    'Evidence',
    personnel: 'Personnel',
    attendance: 'Attendance',
    gate: 'Gate',
    riesgos:     'Risks',
    complianceRules: 'Compliance rules',
    costos:      'Costs',
    calculators: 'Calculators',
    expensesDashboard: 'Expenses',
    expenseApprovals: 'To approve',
    incompleteDrafts: 'Incomplete Records',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    changeLanguage: 'Change language',
    changeTheme: 'Change theme',
    viewProfile: 'View profile',
  },

  telegramHelp: {
    tooltip: 'How to do this via Telegram',
    title: 'Do it via Telegram',
    intro: 'You can record this same operation by talking or writing to the Agrovid bot on Telegram, without using this form.',
    asCommand: 'As a command',
    naturalLanguage: 'In natural language (voice or text)',
    defaultDesc: 'The AI understands the instruction, prepares a draft and asks you to confirm before saving any data.',
    gotIt: 'Got it',
    openBot: 'Open in Telegram',
    botOnline: 'online',
    examples: {
      machinery: [
        { voice: "I started the John Deere tractor in lot 4, tillage", desc: "The bot starts the machine's timer in that lot with the stated activity." },
        { voice: "I'm done with the tractor, used 40 liters of diesel and worked 6 hectares", desc: "Closes the session and calculates cost per hectare from fuel and hours." },
      ],
      sales: [
        { voice: "They deposited 20 thousand dollars for invoice 123", desc: "The bot drafts the payment and lowers the balance once you confirm." },
        { voice: "We collected 150 thousand pesos from customer Frutas del Norte", desc: "Works with pesos or dollars; always asks for confirmation before saving." },
      ],
      cropPhenology: [
        { voice: "The bitter melon in lot 4 just started flowering, day 35 after sowing", desc: "The bot records the crop's current phenological stage." },
        { voice: "Lot 2 is now in fruiting, day 60", desc: "Updates the lot's stage and recalculates days to harvest." },
        { voice: "The green beans in lot 7 just emerged, day 8", desc: "Useful for tracking newly sown crops." },
      ],
      cropPest: [
        { voice: "Found whiteflies in lot 4, medium pressure, about 25% of plants", desc: "The bot records the pest or disease and calculates the pressure level." },
        { voice: "There are thrips in lot 2, high pressure, almost half the lot", desc: "High pressure can trigger an alert for the agronomist." },
        { voice: "Saw downy mildew spots in lot 7, just a few plants", desc: "Diseases are recorded like pests, with their incidence." },
      ],
      finance: [
        { voice: "Set the budget for lot 4 cycle 2026-A at 40,000 pesos per hectare", desc: "The bot can record a base budget for the cycle, with estimated production and price." },
        { voice: "Lot 7 cycle 2026-B budget: 55,000 pesos, expecting 12 tons at 18 pesos per kilo", desc: "Includes estimated production and price to project the margin." },
        { voice: "How much of lot 4's budget have we spent so far?", desc: "You can also check progress against the budget." },
      ],
      harvest: [
        { voice: "We packed 40 boxes of grade A green beans and 5 of grade B from lot 4, medium size", desc: "The bot records the packing with the grade breakdown and calculates packout %." },
        { voice: "Lot 2 harvest: 120 boxes grade A, 30 grade B and 10 seconds", desc: "You can dictate several grades in a single message." },
        { voice: "Today we picked 300 kilos of bitter melon in lot 7", desc: "Without boxes, the bot can record the harvested weight." },
      ],
      inventory: [
        { voice: "20 bags of Novatec fertilizer arrived at the warehouse, invoice $15,000", desc: "The bot records the stock in or out automatically, validating the product against the catalog." },
        { voice: "5 liters of Abamectin left the warehouse for lot 4", desc: "Withdrawals deduct from inventory and are linked to the lot." },
        { voice: "How much Novatec is left in the warehouse?", desc: "You can also check stock without opening the system." },
      ],
      irrigation: [
        { voice: "We irrigated the North lot for 4 hours with the motorized backpack, added 2 liters of nitrogen", desc: "The bot records the irrigation (manual, meter or simplified) and fertigation if applicable." },
        { voice: "Drip irrigation in lot 4, from 6 to 10 in the morning", desc: "Uses start and end times to calculate irrigation duration." },
        { voice: "Lot 7 meter reading: 1,250 cubic meters", desc: "For metered irrigation, the bot calculates usage against the previous reading." },
      ],
      merma: [
        { voice: "There was a 50 kilo loss of green beans in lot 4 due to rot", desc: "The bot records the loss with its cause and calculates the economic impact." },
        { voice: "12 boxes of bitter melon from lot 2 were lost to heat stress", desc: "You can state boxes instead of kilos." },
        { voice: "We discarded 30 kilos of product at packing due to mechanical damage", desc: "Losses can also be recorded after harvest." },
      ],
      payroll: [
        { voice: "Juan Pérez worked 3 day-wages in lot 4 at $250 per day", desc: "The bot records the payment (day wage, piecework or contract) in the open payroll period." },
        { voice: "María López picked 80 boxes of green beans in lot 2 at 6 pesos per box", desc: "For piecework, state the quantity and the rate per unit." },
        { voice: "Pedro Ramírez, $3,000 contract to clean the canal in lot 7", desc: "Contracts are recorded with a fixed amount." },
      ],
      porcinoEntry: [
        { voice: "80 pigs entered pen 3, average weight 25 kilos, supplier Granja del Norte", desc: "The bot records the fattening batch and marks the pen as in use." },
        { voice: "120 piglets arrived at pen 1, 18 kilos on average", desc: "Each entry opens a new fattening batch." },
        { voice: "We received 60 pigs from supplier Los Pinos in pen 5, 30 kilos average", desc: "Include the supplier for traceability." },
      ],
      porcinoEvent: [
        { voice: "We weighed pen 3's batch, they now average 55 kilos", desc: "The bot detects whether it's weighing, feed, mortality or sale and records the matching event." },
        { voice: "2 pigs died in pen 3 today", desc: "Records mortality and deducts heads from the batch." },
        { voice: "We gave 400 kilos of feed to pen 3", desc: "Feed is used for the conversion calculation." },
        { voice: "We sold 50 pigs from pen 3, 6,000 kilos total at 32 pesos per kilo", desc: "The sale closes out heads and calculates income." },
      ],
      protocol: [
        { voice: "Here is the fertilization protocol for bitter melon", desc: "Can be sent with a photo or PDF of the protocol; the AI does not invent doses, it only extracts what is in the document." },
        { voice: "This is the pest control program for green beans, cycle 2026-A", desc: "Works with phytosanitary and nutrition protocols." },
        { voice: "Sending you the supplier's agrochemical price list", desc: "It also extracts the price catalog from the document." },
      ],
      spray: [
        { voice: "I applied Abamectin in lot 4 with the drone, 2 loads, went in at 7 and out at 8", desc: "The bot automatically validates MRL/PHI compliance before saving any phytosanitary application." },
        { voice: "I sprayed lot 2 with a backpack, 3 tanks of Spinosad, operator Luis", desc: "Records equipment, tanks and operator." },
        { voice: "I applied copper in lot 7, 4 liters per hectare, US market", desc: "If the product doesn't meet the market's MRL, the bot warns you before saving." },
      ],
      tasks: [
        { voice: "Tomorrow we need to apply fertilizer in lot 4, let Everardo know", desc: "The bot schedules the task for the named worker and notifies them at 7am." },
        { voice: "Friday is pruning in lot 2, assign it to Manuel", desc: "You can state the day and the person in the same sentence." },
        { voice: "Remind the whole irrigation crew to check the hoses on Monday", desc: "A task can be sent to a whole group." },
      ],
      transport: [
        { voice: "A shipment leaves lot 4 for McAllen, 200 boxes of grade A green beans, driver Manuel, plates ABC-123", desc: "The bot records the shipment with destination, boxes and transport details." },
        { voice: "We sent 150 boxes of bitter melon to the Guadalajara wholesale market", desc: "Destination and boxes are enough; the rest can be completed later." },
        { voice: "The McAllen shipment arrived, we delivered 198 boxes", desc: "You can report arrival and delivered boxes." },
      ],
    },
    cropPhenologyVoice: 'The bitter melon in lot 4 has started flowering, it\'s on day 35 after planting',
    cropPhenologyDesc: 'The bot records the crop\'s current phenological stage.',
    cropPestVoice: 'I found whitefly in lot 4, medium pressure, about 25% of the plants',
    cropPestDesc: 'The bot records the pest or disease and calculates the pressure level.',
    financeVoice: 'Set the budget for lot 4, cycle 2026-A, at 40,000 pesos per hectare',
    financeDesc: 'The bot can record a base budget for the cycle, with estimated production and price.',
    harvestVoice: 'We packed 40 boxes of green beans grade A and 5 of grade B from lot 4, medium size',
    harvestDesc: 'The bot records the packing with the grade breakdown and calculates the packout %.',
    inventoryVoice: '20 bags of Novatec fertilizer came into the warehouse, invoice for $15,000',
    inventoryDesc: 'The bot records the warehouse entry or exit automatically, validating the product against the catalog.',
    irrigationVoice: 'We irrigated the North lot for 4 hours with the motorized backpack, and added 2 liters of nitrogen',
    irrigationDesc: 'The bot records the irrigation (manual, meter or simplified) and the fertigation if applicable.',
    mermaVoice: 'There was a loss of 50 kilos of green beans in lot 4 due to rot',
    mermaDesc: 'The bot records the loss with its cause and calculates the economic impact.',
    payrollVoice: 'Juan Pérez worked 3 day-shifts in lot 4 at $250 per day',
    payrollDesc: 'The bot records the payment (day wage, piecework or contract) in the open payroll period.',
    porcinoEntryVoice: '80 pigs came into pen 3, average weight 25 kilos, supplier Granja del Norte',
    porcinoEntryDesc: 'The bot records the fattening batch and marks the pen as in use.',
    porcinoEventVoice: 'We weighed the pen 3 batch, they\'re now at 55 kilos on average',
    porcinoEventDesc: 'The bot detects whether it\'s a weighing, feeding, mortality or sale and records the matching event.',
    protocolCommandDesc: 'The agronomist sends a photo or document of the input protocol and the AI automatically extracts applications, doses and the price catalog.',
    protocolVoice: 'Here is the fertilization protocol for bitter melon',
    protocolDesc: 'It can be sent along with a photo or PDF of the protocol; the AI does not invent doses, it only extracts what is in the document.',
    sprayVoice: 'I applied Abamectin in lot 4 with the drone, 2 loads, went in at 7 and out at 8',
    sprayDesc: 'The bot automatically validates MRL/PHI compliance before saving any phytosanitary application.',
    tasksVoice: 'Tomorrow we need to apply fertilizer in lot 4, let Everardo know',
    tasksDesc: 'The bot schedules the task for the specified worker and notifies them at 7am.',
    transportVoice: 'A shipment leaves lot 4 for McAllen, 200 boxes of green beans grade A, driver Manuel, plates ABC-123',
    transportDesc: 'The bot records the shipment with destination, boxes and transport details.',
  },

  inventoryUpload: {
    dragDrop: 'Drag & drop Excel file here',
    orBrowse: 'or click to browse',
    selected: 'Selected:',
    upload: 'Upload',
    historyTitle: 'Upload history',
    colFile: 'File',
    colStatus: 'Status',
    colRecords: 'Records',
    colErrors: 'Errors',
    colDate: 'Date',
  },

  common: {
    colorLabel: 'Map color',
    colorCustom: 'Custom color',
    colorClear: 'Clear color',
    loading:    'Loading...',
    save:       'Save',
    cancel:     'Cancel',
    edit:       'Edit',
    delete:     'Delete',
    search:     'Search',
    noData:     'No data.',
    yes:        'Yes',
    no:         'No',
    of:         'of',
    actions:    'Actions',
    status:     'Status',
    date:       'Date',
    total:      'Total',
    name:       'Name',
    type:       'Type',
    method:     'Method',
    notes:      'Notes',
    reloadPage: 'Reload page',
    errorUnexpected: 'An unexpected error occurred in the application.',
    somethingWrong:  'Something went wrong',
    searchNoResults: 'No results',
    createNamed: 'Create “{name}”',
    close: 'Close',
    zoneCycleClosed: 'Cycle closed',
    searchRanchoPlaceholder: 'Search ranch...',
    searchLotePlaceholder: 'Search lot...',
    searchBatchPlaceholder: 'Search cycle...',
    validation: {
      fieldRequired: 'This field is required',
      nameRequired:       'Name is required',
      ranchoRequired:     'Ranch is required',
    },
    valueLabel: 'Value',
    rangeLabel: 'Range',
    configSaveError: 'Could not save the configuration.',
  },

  phoneField: {
    countryLabel: 'Country',
    phoneLabel: 'Phone',
    placeholder: '4421234567',
    favorites: 'Favorites',
    allCountries: 'All countries',
    invalidTen: 'Enter your 10-digit number, without country code or a leading 0. E.g. 4421234567',
    invalidGeneric: 'Invalid number for the selected country (8 to 15 digits in total).',
    hint: 'Just your number, without the country code.',
  },
  passwordField: {
    show: 'Show password',
    hide: 'Hide password',
    confirmLabel: 'Confirm password',
    mismatch: 'Passwords do not match',
    rulesTitle: 'Your password must have:',
    ruleLength: 'At least {n} characters',
    ruleUpper: 'An uppercase letter',
    ruleLower: 'A lowercase letter',
    ruleNumber: 'A number',
    ruleSpecial: 'A special character (e.g. ! @ # $)',
    rulePass: 'Met',
    ruleFail: 'Missing',
  },
  auth: {
    title:        'Agrovid',
    subtitle:     'Intelligent farm management',
    loginGoogle:  'Sign in with your Google account',
    googleBtn:    'Sign in with Google',
    demoBtn:      'Enter as Demo (no account)',
    otpSent:      'We sent a code to your Telegram',
    otpPlaceholder: '000000',
    verify:       'Verify',
    back:         '← Back',
    errorGoogle:  'Error authenticating with Google',
    errorOtp:     'Incorrect code',
    errorDemo:    'Error starting demo',
    logOut:       'Log out',
    hasPasswordLink: 'Have a password? Sign in here',
    emailPasswordTitle: 'Or sign in with your email and password',
    emailLoginPlaceholder: 'youremail@example.com',
    passwordSubtitle: 'Sign in with phone and password',
    passwordPlaceholder: 'Password',
    phonePlaceholder: '4421234567',
    enter:        'Sign in',
    errorPassword: 'Invalid credentials',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    forgotPassword: 'Forgot your password?',
    passwordResetSubtitle: 'Recover your password',
    resetViaTelegram: 'Via Telegram',
    resetViaEmail: 'Via Email',
    resetEmailPlaceholder: 'you@example.com',
    phoneCountryHint: 'Enter your number with country code, e.g. +52…',
    phoneInvalid: 'Invalid phone. Enter your 10-digit number, e.g. 4421234567',
    emailInvalid: 'Invalid email',
    resetCodeSentHint: 'If the details are registered and the method is linked, you will receive a code.',
    resetNotArrived: "Didn't get it: try another method",
    resendCode: 'Resend code',
    resendIn: 'Resend in {n} s',
    resetLocked: 'You have used all code requests. Ask your administrator to unlock password recovery.',
    otpWait: 'Wait {n} s before requesting another code.',
    otpLocked: 'You have used all code requests. Ask your administrator to unlock access.',
    googleEmailNotRegistered: 'The email {email} is not registered.',
    googleUseOtherAccount: 'Use another account',
    sendCode: 'Send code',
    enterResetCode: 'Enter the code and your new password',
    newPasswordPlaceholder: 'New password',
    resetPassword: 'Set password',
    tenantNotFound: 'Company not found',
    tenantNotFoundHint: 'Check the URL or contact your administrator.',
    inviteBanner: 'You have an invitation to join as',
    // F0.10 — invitation acceptance page
    acceptInviteTitle: 'Complete your sign-up',
    acceptInviteSubtitle: 'You were invited to join as',
    acceptInviteName: 'Full name',
    acceptInvitePhone: 'Phone',
    acceptInvitePhoneOptional: "Phone (optional)",
    acceptInviteIdentityRequired: "Enter a phone or an email, or continue with Google.",
    acceptInvitePasswordNeedsEmail: "To use a password, enter your email too.",
    acceptInviteEmail: 'Email (to sign in with Google)',
    acceptInvitePassword: 'Password (optional)',
    acceptInvitePasswordHint: 'Leave it blank to sign in with Google or Telegram.',
    acceptInviteSubmit: 'Create my account',
    acceptInviteLoading: 'Checking invitation...',
    acceptInviteInvalid: 'This invitation is not valid or has expired. Ask your administrator for a new one.',
    acceptInviteUsed: 'This invitation was already used. If you already have an account, sign in.',
    acceptInvitePhoneTaken: 'An account with that phone already exists. Sign in instead of signing up.',
    acceptInviteLinkTelegram: 'Link my Telegram',
    acceptInviteError: 'We could not create your account. Check the details and try again.',
    acceptInviteNameRequired: 'Enter your name',
    acceptInvitePhoneRequired: 'Enter a valid phone (Mexico and US: 10 digits)',
    acceptInviteEmailInvalid: 'Invalid email',
    acceptInvitePasswordShort: 'Password must be at least 8 characters',
    acceptInviteGoToLogin: 'Go to sign in',
    // F0.10 — Google requires OTP but the account has no Telegram linked
    telegramNotLinked: 'Your account has no Telegram linked, so we cannot send you the code.',
    telegramNotLinkedHint: 'Ask your administrator for the Telegram linking link.',
    telegramNotLinkedPassword: 'You can also sign in with your password.',
    roleSelectionTitle: 'Select your role',
    roleSelectionSubtitle: 'Choose the role you want to use to access',
    selectRoleError: 'Error selecting role. Please try again.',
    noRoleAssigned: 'Your account has no assigned role. Please contact your administrator.',
    noRoleSignOut: 'Sign out',
    roles: {
      fieldWorker: 'Field Worker',
      supervisor: 'Supervisor',
      admin: 'Administrator',
      agronomist: 'Agronomist',
      superAdmin: 'Super Administrator',
      auditor: 'Auditor',
      warehouseManager: 'Warehouse Manager',
      investor: 'Investor',
    },
  },

  demo: {
    fieldWorker:      'Access to operations and activity registration',
    supervisor:       'Authority in operations and task management',
    warehouseManager: 'Inventory management, stock tracking, and FEFO control',
    agronomist:       'Crop protocols, phenology, and pest monitoring',
    investor:         'Financial KPIs, reports and investment overview (read-only)',
    admin:            'Full administrative access including users and finance',
  },

  dashboard: {
    title:          'Dashboard',
    kpiCostToday:   "Today's cost",
    kpiActivities:  'Activities',
    kpiActivitiesSub: 'last 24h',
    kpiPendingTasks: 'Pending tasks',
    kpiOverdueTasks: 'Overdue tasks',
    kpiActiveLots:   'Active lots',
    kanbanTitle:    "Kanban — Today's tasks",
    activityTitle:  'Recent activity',
    loadingKpis:    'Loading KPIs...',
    moveTo:         'Move to',
  },

  onboarding: {
    title:          'Getting Started',
    celebration:    "Congratulations! You're ready to go 🎉",
    stepPotreros:   'Load your fields',
    stepCultivos:   'Set up your crops',
    stepInsumos:    'Load your initial inventory',
    go:             'Go',
    bulkImportHint: 'Setting up a whole ranch? Upload ranchos, lotes, users and catalog from a single Excel file.',
    bulkImportCta:  'Go to bulk import',
    required:       'Required',
    optional:       'Optional',
    optionalTitle:  'Optional',
    markReviewed:   'Mark as reviewed',
    blocks: { structure: 'Structure', people: 'People', operation: 'Operation', finance: 'Finance', system: 'System' },
    items: {
      rancho: { label: 'Create your ranch', desc: 'Add at least one ranch.' },
      lotes_geocerca: { label: 'Lots with geofence', desc: 'Draw the outline of your lots on the map.' },
      cultivo: { label: 'Crop and planting', desc: 'Assign a crop and planting date.' },
      usuarios_accesos: { label: 'Users and access', desc: 'Invite users and set their ranches and lots.' },
      telegram: { label: 'Telegram linked', desc: 'Link your Telegram to use the field bot.' },
      grupo_telegram: { label: 'Telegram group', desc: 'Connect your ranch Telegram group.' },
      productos_reglas: { label: 'Products and rules', desc: 'Load products and PHI/REI/MRL rules.' },
      bodega_stock: { label: 'Warehouse and stock', desc: 'Record the initial stock of your warehouse.' },
      maquinaria: { label: 'Machinery', desc: 'Register your tractors and equipment.' },
      nomina: { label: 'Payroll', desc: 'Set up payroll for your staff.' },
      presupuesto: { label: 'Budget', desc: 'Define a budget for a cycle.' },
      locale: { label: 'Language and time zone', desc: 'Review language, time zone and currency.' },
      proveedores_ia: { label: 'AI providers', desc: 'Check that the AI providers respond.' },
      reportes: { label: 'Scheduled reports', desc: 'Schedule report delivery.' },
    },
  },

  tour: {
    next:     'Next',
    prev:     'Back',
    done:     'Done',
    progress: '{{current}} of {{total}}', // driver.js's own template syntax — not our i18n interpolation
    dashboard: {
      title: 'Home',
      description: "Here you see your operation's overview: KPIs, recent activity, and your setup progress.",
    },
    camposLotes: {
      title: 'Fields and Lotes',
      description: 'Add your lotes, draw their boundaries on the map, and assign the crop or use for each one.',
    },
    tareas: {
      title: 'Tasks',
      description: 'Schedule and assign field work — planting, spraying, fertilizing — with a due date and assignee.',
    },
    insumos: {
      title: 'Inputs',
      description: 'Track your agrochemical, fertilizer, and other input inventory, with low-stock alerts.',
    },
    finanzas: {
      title: 'Finance',
      description: 'Log expenses and income, and check cost-per-hectare and cash flow reports.',
    },
  },

  adoption: {
    countSingular: '{count} record logged this week',
    countPlural:   '{count} records logged this week',
    teamRankingTitle: 'Team adoption this week',
  },

  tasks: {
    title:             'Tasks',
    loading:           'Loading tasks...',
    taskUnit:          'task',
    taskUnitPlural:    'tasks',
    overdueLabel:      'overdue',
    viewKanban:        'Kanban view',
    viewList:          'List view',
    searchPlaceholder: 'Search task...',
    filterAllLots:     'All fields',
    filterPriority:    'Priority',
    filterType:        'Type',
    clearFilters:      'Clear filters',
    emptyColumn:       'No tasks',
    emptyFilter:       'No tasks match the current filters.',
    overdueBadge:      'Overdue',
    tableTitle:        'Task',
    tableLot:          'Field',
    tableType:         'Type',
    tablePriority:     'Priority',
    tableDate:         'Date',
    tableStatus:       'Status',
    newTask:           '+ New task',
    newTaskTitle:      'New task',
    editTaskTitle:     'Edit task',
    deleteTaskTitle:   'Delete task',
    deleteTaskConfirm: 'Delete this task? This action cannot be undone.',
    fieldLote:         'Lot *',
    fieldZone:         'Zone (optional)',
    zoneAllLote:       'Whole lot',
    fieldTaskTitle:    'Title *',
    fieldTaskTitlePlaceholder: 'Apply fertilizer on V9...',
    fieldDescription:  'Description',
    fieldActivityType: 'Activity type *',
    fieldScheduledDate: 'Scheduled date *',
    fieldDueDate:      'Due date',
    fieldPriority:     'Priority',
    fieldAssignedTo:   'Assigned to (phone)',
    loteRequired:      'Select a lot',
    noLoteOption: 'No lot (general task)',
    generalLabel: 'General',
    audTitle: 'Send to',
    audIndividual: 'Individual (no audience)',
    audAll: 'Everyone (with ranch access)',
    audRoles: 'By role',
    audLotes: 'By plot',
    audCrops: 'By crop',
    audPickRoles: 'Roles',
    audPickLotes: 'Plots',
    audPickCrops: 'Crops',
    audReachCount: 'Will reach {n} people',
    audReachOne: 'Will reach 1 person',
    audNoRecipients: 'This audience has no recipients',
    audTooMany: 'Exceeds the maximum of {max} recipients: remove people',
    audRemove: 'Remove',
    audAdd: 'Add person',
    audAddPlaceholder: 'Search person…',
    audNoTelegram: 'No Telegram',
    audLoading: 'Calculating recipients…',
    audPreviewError: 'Could not calculate the recipient list',
    audSnapshotHint: 'This list is saved as a fixed snapshot: access or crop changes do not alter it unless you press “Recalculate recipients”.',
    audRecompute: 'Recalculate recipients',
    audRecomputed: '{n} new recipients added',
    audRecomputeNone: 'No new recipients',
    audRecomputeError: 'Could not recalculate recipients',
    audNoRecipientsError: 'Add at least one recipient',
    audRoleNames: {
      SUPER_ADMIN: 'Super Admin', ADMIN: 'Admin', AGRONOMIST: 'Agronomist', SUPERVISOR: 'Supervisor',
      WAREHOUSE_MANAGER: 'Warehouse', FIELD_WORKER: 'Field', AUDITOR: 'Auditor', HR: 'HR', GUARD: 'Guard', INVESTOR: 'Investor',
    },
    ranchoRequired: 'Select a ranch for the general task',
    fieldRancho: 'Ranch *',
    titleRequired:     'Title is required',
    dateRequired:      'Scheduled date is required',
    createTaskError:   'Error creating task',
    updateTaskError:   'Error updating task',
    deleteTaskError:   'Error deleting task',
    assignedToPlaceholder: 'Select a person...',
    fieldNotifyAt: 'Schedule notice (optional)',
    notifyAtHint: 'Empty = Telegram notice is sent on save. With a date and time, the notice goes out then.',
      notifyScheduledLabel: 'Scheduled notice', notifyCancelButton: 'Cancel notice', notifyKeepButton: 'Keep notice', notifyCancelledHint: 'The scheduled notice will be cancelled on save.',
    noAssigneesHint:   'No users registered on Telegram yet.',
    telegramHelpTooltip: 'How to onboard someone on Telegram',
    telegramHelpTitle:  'Onboard a user on Telegram',
    telegramHelpIntro:  'For someone to show up in this list, they first need to register with the Agrovid bot on Telegram. There are two ways to do it:',
    telegramHelpCommandLabel: 'As a command',
    telegramHelpCommandDesc:  'The person opens the chat with the bot and types /start. The bot will ask them to share their phone number to authenticate.',
    telegramHelpVoiceLabel:   'With natural language (voice or text)',
    telegramHelpVoiceExample: "Hi, I'd like to register",
    telegramHelpVoiceDesc:    'The AI recognizes the intent and guides through the same authentication flow step by step, no need to remember the exact command.',
    confirmDialog:            'Got it',
    linkedShipment:           'Linked to shipment',
    linkedPayroll:            'Linked to payroll',
    groupPending:             'Pending',
    groupCompleted:           'Completed',
    markComplete:             'Mark as completed',
    alreadyCompleted:         'Already completed',
  },

  taskStatus: {
    PENDING:     'Pending',
    IN_PROGRESS: 'In progress',
    COMPLETED:   'Completed',
    CANCELLED:   'Cancelled',
  },

  taskPriority: {
    HIGH:   'High',
    MEDIUM: 'Medium',
    LOW:    'Low',
  },

  // ── Incomplete registration — web wizard (Fase 2) ────────────────────────────
  incompleteDrafts: {
    pageTitle:            'Incomplete records',
    pageDescription:      'Finish the records that were left pending because a field was missing.',
    emptyState:           'You have no pending records to complete. 🎉',
    reasonNotSpecified:   "You didn't specify the field.",
    reasonNotFound:       "The field you mentioned isn't available right now.",
    reasonRestricted:     "The field you mentioned isn't available right now.",
    reasonAmbiguous:      'There is more than one field with that name — pick the right one.',
    fieldLote:            'Field',
    fieldQuantity:        'Quantity / cost',
    fieldDate:            'Date',
    fieldNotes:           'Notes',
    completeButton:       'Complete',
    saveButton:           'Save changes',
    cancelButton:         'Cancel record',
    cancelConfirmTitle:   'Cancel this record?',
    cancelConfirmBody:    "This can't be undone. The dictated record will be lost.",
    cancelConfirmConfirm: 'Yes, cancel',
    cancelConfirmDismiss: 'Go back',
    completeSuccess:      'Record completed! You can now confirm it from Telegram.',
    stillIncomplete:      "Saved, but there's still another piece of data to complete.",
    cancelSuccess:        'Record cancelled.',
    myTab:                'My records',
    teamTab:               'Team',
    teamEmptyState:       'No pending records in your team. 🎉',
    ownerLabel:           "{name}'s record",
  },

  field: {
    title:          'Fields & Lots',
    searchPlaceholder: 'Search field or crop...',
    lots:           'field',
    lotsPlural:     'fields',
    loadingLots:    'Loading fields...',
    hectares:       'ha',
    lotStatus: {
      ACTIVE:   'Active',
      FALLOW:   'Fallow',
      INACTIVE: 'Inactive',
    },
    detail: {
      accumulatedCost: 'Accumulated cost',
      costByType:      'Cost by type',
      recentActivity:  'Recent activity',
      noActivities:    'No recent activities.',
      loadingCosts:    'Loading costs...',
    },
    // Form
    editTitle:              'Edit — {name}',
    newLoteTitle:           'New Field / Lot',
    lotNameLabel:           'Lot Name *',
    ranchoIdLabel:          'Ranch *',
    ranchoIdHint:           'ID of the ranch this lot belongs to',
    searchRanchoPlaceholder: 'Search ranch...',
    cropTypeLabel:          'Current crop',
    hectaresLabel:          'Hectares',
    usoLabel:               'Lot use',
    usoCampo:               'Field',
    usoAdministrativo:      'Administrative (office, not counted as field)',
    latitudeLabel:          'Latitude',
    goToMapAfterCreate:     'After saving, go to the map to draw the geofence',
    addGeofence:            'Geofence',
    geofenceCaptured: 'Geofence captured',
    geofenceMissing:  'No geofence',
    editGeofence:     'Edit geofence',
    addModules:             'Zones',
    pickOnMapBtn:           'Pick on map',
    locationDialogTitle:    'Location of {name}',
    locationDialogTitleGeneric: 'Lot location',
    longitudeLabel:         'Longitude',
    noResultsSearch:        'No results for your search.',
    noLotsRegistered:       'No lots registered.',
    createFirst:            'Create first lot',
    editLot:                'Edit lot',
    newLot:                 'New lot',
    deleteTitle:            'Delete Lot',
    deleteMessagePrefix:    'Delete ',
    deleteMessageSuffix:    '? This action cannot be undone. Only possible if the lot has no activities registered.',
    saveChanges:            'Save Changes',
    createLote:             'Create Lot',
  },

  irrigation: {
    lotesLoadError: 'Could not load the plots.',
    title:         'Irrigation & Fertigation',
    selectLot:     'Select field',
    zoneFilter:    'Field or zone',
    allLotes:      'All fields',
    tableLote:     'Field',
    kpiVolume:     'Total volume (L)',
    kpiVolumeSub:  'irrigations',
    kpiEfficiency: 'Avg L/kg',
    kpiEfficiencySub: 'water efficiency',
    kpiLastIrrigation: 'Last irrigation',
    chartVolume:   'Volume per session (last 30)',
    chartEfficiency: 'Water efficiency (L/kg)',
    tableTitle:    'Irrigation records',
    tableDate:     'Date',
    tableMethod:   'Method',
    tableVolume:   'Volume (L)',
    tableDuration: 'Duration (min)',
    tableDepthMm:  'Depth (mm)',
    fieldDepthMm:  'Depth (mm, optional)',
    tableLPerKg:   'L/kg',
    tableFert:     'Fert.',
    tableFertCost: 'Fert. Cost',
    tableTimestamps: 'Times',
    tableRecordedBy: 'Recorded by',
    sourceTelegram: 'Telegram',
    sourceWeb:      'Web',
    chartByLote:    'Volume by field (L)',
    flowUnit:       'L/h',
    noRecords:     'No records for this field.',
    methods: {
      MANUAL:     'Manual',
      METER:      'Meter',
      SIMPLIFIED: 'Simplified',
    },
    timestamps: {
      title:               'Irrigation times',
      inicioHidratacion:   'Hydration start',
      inicioFertilizacion: 'Fertigation start',
      finFertilizacion:    'Fertigation end',
      finLavadoCintilla:   'Tape rinse end',
      duracionFert:        'Fert. duration',
    },
    newRecord:          '+ Log irrigation',
    newRecordTitle:     'Log irrigation',
    fieldBatch:         'Cycle (batchId) *',
    fieldMethod:        'Method *',
    methodManual:       'Manual',
    methodMedidor:      'Meter',
    methodSimplificado: 'Simplified',
    fieldDurationMin:   'Duration (minutes)',
    fieldFlowRate:      'Estimated flow (L/h)',
    fieldMeterStart:    'Starting reading',
    fieldMeterEnd:      'Ending reading',
    fieldDescription:   'Description',
    fieldNotes:         'Notes',
    batchRequired:      'Cycle (batchId) is required',
    createError:        'Error logging irrigation',
    create:             'Log',
    kpiLoadError: 'Could not load the irrigation KPI.',
    tsHydrationStart: 'Hydration start',
    tsFertStart: 'Fert. start',
    tsFertEnd: 'Fert. end',
    tsWashEnd: 'Flush end',
    stockDeductWarnTitle: 'Inventory deduction incomplete',
    stockDeductFailed: 'The irrigation was saved, but the automatic input deduction could not be done. Check and adjust the stock in Inventory.',
    stockDeductPartial: 'The irrigation was saved, but the automatic input deduction was only partial (some input was not deducted). Check the stock in Inventory.',
  },

  spray: {
    title:            'Chemical Applications',
    selectLot:        'Select field',
    zoneFilter:    'Field or zone',
    kpiTotal:         'Total applications',
    kpiLastApp:       'Last application',
    tableTitle:       'Application history',
    tableDate:        'Date',
    tableLote:        'Field',
    tableProduct:     'Product',
    tableDosis:       'Dose',
    tableOperator:    'Operator',
    tableEquipo:      'Equipment',
    tableCargas:      'Loads',
    tableVelocidad:   'Speed (km/h)',
    tableLitrosAgua:  'Water liters',
    tableHoraEntrada: 'Entry time',
    tableHoraSalida:  'Exit time',
    noRecords:        'No applications recorded.',
    newApplicationBtn:  '+ Log application',
    newApplicationTitle: 'Log chemical application',
    fieldOccurredAt:    'Application date/time *',
    fieldProductName:   'Product (name) *',
    fieldQty:           'Quantity *',
    fieldUnit:          'Unit *',
    fieldProductId:     'Product ID (catalog, for compliance) *',
    searchProductPlaceholder: 'Search product...',
    fieldCrop:          'Crop (compliance) *',
    fieldMarket:        'Destination market *',
    fieldPlannedHarvest: 'Planned harvest date',
    fieldBatchId:       'Cycle (batchId)',
    sprayDataTitle:     'Equipment application data (optional)',
    fieldEquipo:        'Equipment (tractor, drone, backpack)',
    fieldCargas:        'Loads/tanks',
    fieldVelocidad:     'Speed (km/h)',
    fieldLitrosAgua:    'Water liters',
    fieldHoraEntrada:   'Entry time (HH:MM)',
    fieldHoraSalida:    'Exit time (HH:MM)',
    fieldOperador:      'Operator',
    fieldNotes:         'Notes',
    dateRequired:       'Date is required',
    productRequired:    'Product name is required',
    productIdRequired:  'Product ID is required to validate compliance',
    cropRequired:       'Crop is required to validate compliance',
    complianceBlockedTitle: 'Blocked by MRL/PHI Compliance',
    createError:        'Error logging application',
    create:             'Log',
  },

  finance: {
    title:            'Finance',
    totalCost:        'Total accumulated cost',
    costByLot:        'Cost by field',
    weeklyTrend:      'Weekly trend',
    byLotTable:       'Breakdown by field',
    tableLot:         'Field',
    tableActivities:  'Activities',
    tableCost:        'Cost',
    tableTotal:       'Total',
    loadingSummary:   'Loading financial summary...',
    costLabel:        'Cost',
    protocolsTitle:   'Loaded input protocols',
    protocolsDesc:    'Sent via Telegram. The cost/ha is used as base budget when applied to a lot.',
    protocolsLoading: 'Loading protocols...',
    protocolsEmpty:   'No protocols registered yet.',
    protocolCropCycle: 'Crop / Cycle',
    protocolType:     'Type',
    protocolChemHa:   'Chemical/ha',
    protocolFertHa:   'Fertilizer/ha',
    protocolTotalHa:  'Total/ha',
    protocolOrigin:   'Source',
    protocolTypeQuimico:     'Chemical',
    protocolTypeFertilizante: 'Fertilizer',
    protocolTypeAmbos:       'Both',
    protocolSourceDoc:   'DOCX',
    protocolSourcePhoto: 'Photo',
    protocolSourceText:  'Text',
    tabResumen:          'Summary',
    tabPL:               'P&L',
    tabCashflow:         'Cash Flow',
    tabPresupuesto:      'Budget',
    plTitle:             'Profit & Loss',
    plRevenue:           'Estimated Revenue',
    plRevenueSub:        'Based on yield × price in active budgets',
    plCosts:             'Real Costs',
    plMargin:            'Gross Margin',
    plMarginPct:         'Margin %',
    plCostByCategory:    'Costs by Category',
    plLoading:           'Loading P&L...',
    cashflowTitle:       'Cash Flow',
    cashflowInflow:      'Inflow',
    cashflowOutflow:     'Outflow',
    cashflowBalance:     'Balance',
    cashflowWeek:        'Weekly',
    cashflowMonth:       'Monthly',
    cashflowLoading:     'Loading cash flow...',
    budgetTitle:         'Budget vs Actual',
    budgetCategory:      'Category',
    budgetPlanned:       'Planned',
    budgetExecuted:      'Executed',
    budgetVariance:      'Variance',
    budgetPct:           '%',
    budgetCommitted:     'Committed',
    budgetTotalPlanned:  'Total planned',
    budgetTotalExecuted: 'Total executed',
    budgetTotalCommitted: 'Total committed',
    editCommittedTitle:  'Edit committed amount',
    editCommittedLabel:  'Committed amount (MXN)',
    editCommittedSave:   'Save',
    noActiveBudget:      'This lot has no active budget',
    budgetLoading:       'Loading budget...',
    budgetAllLots:       'All fields',
    newBudgetBtn:        '+ New budget',
    newBudgetTitle:      'New budget',
    fieldRanchoId:       'Ranch *',
    fieldLoteId:         'Lot *',
    searchRanchoPlaceholder: 'Search ranch...',
    searchLotePlaceholder: 'Search lot...',
    fieldCiclo:          'Cycle *',
    fieldCurrency:       'Budget currency',
    fieldTotalBudget:    'Total budget ($) *',
    fieldEstimatedYield: 'Estimated yield',
    fieldEstimatedPrice: 'Estimated price ($/unit)',
    fieldNotes:          'Notes',
    ranchoRequired:      'Ranch is required',
    loteRequired:        'Lot is required',
    cicloRequired:       'Cycle is required',
    totalBudgetRequired: 'Total budget must be greater than 0',
    createBudgetError:   'Error creating budget',
    create:              'Create',
    categories: {
      PREPARACION: 'Preparation',
      SIEMBRA: 'Planting',
      RIEGO: 'Irrigation',
      FERTILIZACION: 'Fertilization',
      FITOSANITARIO: 'Phytosanitary',
      COSECHA: 'Harvest',
      MAQUINARIA: 'Machinery',
      LABORES: 'Field work',
      OTRO: 'Other',
    },
  },

  topbar: {
    project: 'Project',
    allProjects: 'All projects',
    rancho: 'Ranch',
    allRanchos: 'All ranches',
  },

  terrain: {
    cropAssignedInCultivo: 'Crop and planting date are set on the Crop screen.',
    module: 'Zone',
    modules: 'Zones',
    valve: 'Valve',
    areaHa: 'Area (ha)',
    crop: 'Crop',
    noModules: 'This farm has no zones registered yet.',
    legend: 'Zone status',
    lastClosedCycle: 'Last closed cycle',
    status: {
      PREPARACION: 'Preparation',
      NORMAL: 'Normal',
      ATENCION: 'Attention',
      CRITICO: 'Critical',
      COSECHANDO: 'Harvesting',
    },
    createModuleBtn: 'Create zone',
    editLoteBtn: 'Edit lot',
    newModuleTitle: 'New zone',
    editModuleTitle: 'Edit zone',
    editModuleBtn: 'Edit',
    saveModuleBtn: 'Save',
    updateModuleError: 'Could not save the zone',
    fieldModuleCode: 'Code',
    fieldModuleCodePlaceholder: 'e.g. M-01',
    fieldModuleCodeReadonlyHint: 'The code cannot be changed after the zone is created',
    fieldParentModule: 'Parent zone',
    attendanceZoneLabel: 'Valid place for attendance',
    attendanceZoneHint: 'Attendance can be taken here (entrances, office, field...). With no zones flagged the lote is used.',
    fieldLoteReadonly: 'Lot',
    parentModuleNone: 'None (top level)',
    fieldModuleName: 'Name',
    fieldCropName: 'Crop',
    fieldValves: 'Valves',
    fieldPlantingDate: 'Planting date',
    createModuleSuccess: 'Zone created',
    createModuleError: 'Could not create the zone',
    duplicateModuleCode: 'A zone with that code already exists',
    selectLote: 'Select a lot',
    noProjectSelectedHint: 'Select an active project/season before creating a zone',
    farmModulesDisabledTooltip: 'This rancho does not use Zone subdivision',
    noPermissionHint: 'You do not have permission to modify zones',
    deleteModuleBoundaryBtn: 'Delete geofence',
    deleteModuleBtn: 'Delete zone',
    deleteModuleConfirm: 'Delete the zone',
    deleteModuleHasChildren: 'This zone has subzones. Delete or move the subzones first.',
    deleteModuleError: 'Could not delete the zone.',
    newCropBtn: '+ New crop',
    newCropPlaceholder: 'Crop name',
  },

  map: {
    title:      'Field Map',
    loading:    'Loading fields...',
    lotesCount: 'fields',
    badgeGPS:   'GPS',
    badgePoly:  'Boundary',
    badgeNoPoly: 'No boundary',
    searchPlaceholder: 'Search lot, zone, code, status or crop',
    ranchoUnknown: 'Unnamed ranch',
    collapseLote: 'Collapse lot',
    expandLote: 'Expand lot',
    collapseSidebar: 'Collapse parcels panel',
    expandSidebar: 'Expand parcels panel',
    drawBtn:    'Draw boundary',
    deleteBtn:  'Delete boundary',
    saveBtn:    'Save boundary',
    saving:     'Saving...',
    cancelBtn:  'Cancel',
    saveError:  'Error saving boundary',
    areaTooLargeError: 'The polygon area is too large ({area} ha) — check the points you drew',
    inviteAfterGeofenceHint: 'Field set up. Want to invite someone to work on this ranch?',
    inviteAfterGeofenceCta:  'Invite user',
    drawHint:   'Click on the map to add points',
    drawPoints: 'points',
    drawReady:  'ready to save',
    satellite:  'Satellite',
    fieldLatitude:   'Latitude',
    fieldLongitude:  'Longitude',
    useMapPointBtn:  'Use map',
    saveLocationBtn: 'Save location',
    discardTitle:   'Discard changes?',
    discardBody:    'The drawing has not been saved. If you leave now, it will be lost.',
    discardConfirm: 'Discard',
    discardCancel:  'Keep editing',
    dateSliderLabel: 'Date',
    ndviLayerToggle: 'NDVI layer',
    ndviDisabled: 'NDVI is not enabled for this ranch; ask an administrator to enable it.',
    ndviSceneDate: 'Nearest satellite image',
    fillThemeLabel: 'Fill',
    fillThemeStatus: 'Status',
    fillThemeNdvi: 'NDVI',
    fillThemeStage: 'Growth stage',
    fillThemeCustom: 'Chosen color',
    stageNoData: 'No stage recorded',
    labelsToggle: 'Labels',
    labelFieldName: 'Name',
    labelFieldCrop: 'Crop',
    labelFieldHectares: 'Hectares',
    labelFieldCode: 'Code',
    ndviHigh: 'High NDVI (≥ 0.5)',
    ndviMid: 'Medium NDVI (0.3–0.5)',
    ndviLow: 'Low NDVI (< 0.3)',
    ndviNoData: 'No data',
    ndviScale: 'NDVI scale',
    ndviScaleFixed: 'Fixed',
    ndviScaleDynamic: 'Dynamic',
    ndviRangeMin: 'NDVI min {min}',
    ndviRangeMax: 'NDVI max {max}',
  },

  chat: {
    title:            'Agrovid Assistant',
    subtitle:         'Read-only · Ask about your farm data',
    clearBtn:         'Clear chat',
    welcomeTitle:     'Hi! I\u2019m the Agrovid Assistant',
    welcomeBody:      'Ask me about your fields, activities, tasks and costs.',
    noRanchoNotice:   'No active ranch: answers may cover all your ranches. It is best to pick one in the ranch selector.',
    inputPlaceholder: 'Type your question... (Enter to send)',
    sendBtn:          'Send',
    errorGeneric:     'Error contacting assistant. Please try again.',
    fallbackWebHint:  'This lives in:',
    fallbackWebGo:    'Go to {label}',
    feedbackUp:       'Helpful answer',
    feedbackDown:     'Not helpful',
    feedbackSaved:    'Feedback saved',
    fallbackBotHint:  'This is logged via Telegram — try {command} with the bot.',
    sidebar: {
      newChat:       'New chat',
      empty:         'No saved conversations',
      rename:        'Rename',
      delete:        'Delete',
      deleteConfirmTitle: 'Delete this conversation?',
      deleteConfirmBody:  'This action cannot be undone.',
      cancel:        'Cancel',
      confirmDelete: 'Delete',
      toggle:        'Conversations',
    },
    suggestions: {
      s1: 'How many active lots do I have?',
      s2: 'What was the last recorded activity?',
      s3: 'Which tasks are pending today?',
      s4: 'What is today\'s accumulated cost?',
    },
    fallbackLabels: {
      reports: 'Reports',
      sales: 'Sales & Prices',
      logistics: 'Logistics',
      traceability: 'Traceability',
      devices: 'Devices',
      permissions: 'Permissions Matrix',
      inventory: 'Inventory',
      tasks: 'Tasks',
    },
  },
  notifications: {
    bell:        'Notifications',
    panelTitle:  'Recent activity',
    markAllRead: 'Mark all read',
    empty:       'No activity in the last 24 h',
  },

  // ── TopBar ──────────────────────────────────────────────────────────────────
  topBar: {
    selectTenant:     'Switch organization',
    tenants:          'Organizations',
    loading:          'Loading...',
    noTenants:        'No organizations assigned',
    switchTenantError: 'Error switching organization',
    roleLabels: {
      SUPER_ADMIN:       'Super Admin',
      ADMIN:             'Admin',
      SUPERVISOR:        'Field Supervisor',
      AGRONOMIST:        'Agronomist',
      FIELD_WORKER:      'Field Worker',
      BODEGUERO:         'Warehouse Keeper',
      WAREHOUSE_MANAGER: 'Warehouse Keeper',
      AUDITOR:           'Auditor',
      HR:           'HR',
      GUARD:           'Guard',
      INVESTOR:          'Investor',
    },
    timeJustNow: 'Just now',
    timeMinutesAgo: '{n} min ago',
    timeHoursAgo: '{n} h ago',
    timeDaysAgo: '{n} d ago',
    startTourTooltip: 'View tutorial',
    restartTourLabel: 'View tutorial again',
    dayCloseTimeLabel: 'Day-close via Telegram',
    assignedAccessLabel: 'Assigned access',
    assignedRanchos: 'Ranch(es)',
    assignedLotes: 'Lot(s)',
    assignedAll: 'All',
    dayCloseTimeHint: "At this time we'll ask if you want to log anything before closing your day (only if you haven't logged anything yet)",
    dayCloseTimeDisable: 'Disable',
    telegramLink: {
      title:          'Telegram',
      linked:         'Telegram linked',
      notLinked:      'Telegram not linked',
      link:           'Link Telegram',
      openTelegram:   'Open in Telegram',
      waiting:        'Open the link in Telegram and confirm the link. Waiting for confirmation...',
      expired:        'The link expired without being confirmed. Generate a new one to try again.',
      unavailable:    'Linking with Telegram is not available right now. Try again later.',
      unlink:         'Unlink',
      unlinkConfirm:  'Are you sure you want to unlink Telegram from your account?',
      unlinkConfirmBtn: 'Yes, unlink',
      cancel:         'Cancel',
    },
  },

  reports: {
    title:               'PDF Reports',
    subtitle:            'Generate and download system reports in PDF format. Files are available for 24 hours.',
    loading:             'Loading report types...',
    generateBtn:         'Generate PDF',
    generating:          'Generating...',
    generateError:       'Error generating report',
    readyTitle:          'Report ready',
    downloadBtn:         'Download PDF',
    expiresNote:         'Available for 24 hours',
    selectLote:          'Select field',
    selectLotePlaceholder: '— Choose a field —',
    badgeTenant:         'Whole farm',
    badgeLote:           'Per field',
    recentTitle:         'Recent reports',
    noRecent:            'No reports have been generated yet.',
    colName:             'Name',
    colType:             'Type',
    colDate:             'Date',
    scheduledTitle:      'Scheduled reports',
    noScheduled:         'No scheduled reports.',
    frequencyWEEKLY:     'Weekly',
    frequencyMONTHLY:    'Monthly',
    nextRun:             'Next run',
    lastRun:             'Last run',
    colFrequency:        'Frequency',
    createSchedule:      'Schedule report',
    deleteSchedule:      'Delete',
    scheduleTypeLabel:   'Report type',
    scheduleFrequencyLabel: 'Frequency',
    scheduleCreateBtn:   'Create',
    pushToTelegramLabel: 'Send via Telegram to administrators',
    pushToTelegramHint:  'ADMIN, SUPERVISOR and AGRONOMIST for the farm get the PDF via Telegram every week.',
    scheduleCreateError: 'Could not create the scheduled report.',
    scheduleDeleteError: 'Could not delete the scheduled report.',
  },

  sales: {
    title:          'Sales & Prices',
    newOrder:       'New order',
    newOrderNoPermission: 'You do not have permission to create sale orders',
    newCustomer: '+ New customer',
    newCustomerNoPermission: 'You do not have permission to create customers',
    newCustomerTitle: 'New customer',
    customerName: 'Name',
    customerCountry: 'Country',
    customerContact: 'Contact',
    customerEmail: 'Email',
    customerPhone: 'Phone',
    customerTermsDays: 'Credit days',
    customerCreateError: 'Could not create the customer. Check the fields.',
    customerInvalidEmail: 'Invalid email',
    noCustomers: 'No customers yet. Create one with "+ New customer".',
    customer:       'Customer',
    crop:           'Crop',
    grade:          'Grade',
    qty:            'Qty',
    targetPrice:    'Target price',
    lote:           'Lot',
    loteOptional:   'No lot',
    stockNotice:    (over: number, lote: string) => `Notice: the sale exceeds the available stock of lot ${lote} by ${over} kg. The sale was saved.`,
    unitPrice:      'Actual price',
    variance:       'Variance',
    status:         'Status',
    total:          'Total',
    statusDraft:     'Draft',
    statusConfirmed: 'Confirmed',
    statusFulfilled: 'Fulfilled',
    statusCancelled: 'Cancelled',
    createError:    'Could not create the order. Check the fields.',
    kpiRevenue: 'Total revenue',
    kpiOrders: 'Orders',
    kpiAvgPrice: 'Weighted avg. price',
    kpiAvgVariance: 'Average variance',
    kpiTopCustomer: 'Top customer',
    kpiTopCrop: 'Top crop',
    priceHistoryTitle: 'Historical price by crop',
    priceHistorySubtitle: 'Weighted monthly average, observed from recorded orders.',
    priceRangeTitle: 'Projected price range',
    priceRange: 'Observed range',
    performanceTitle: 'Sales performance by customer',
  },

  harvest: {
    title:           'Harvest',
    selectLot:       'Select field',
    kpiTotalKg:      'Total harvested (kg)',
    kpiPackout:      'Average packout',
    kpiPackoutSub:   'Grade A / total',
    kpiCycles:       'Recorded cycles',
    kpiCostPerKg:    'Cost/kg',
    kpiCostPerKgSub: 'MXN/kg',
    chartByLot:      'Kg by field',
    chartGradeBreakdown: 'Grade breakdown (last 20)',
    chartGradeA:     'Grade A',
    chartGradeB:     'Grade B',
    chartGradeC:     'Grade C',
    tableTitle:      'Harvest history',
    tableLot:        'Field',
    tableCycle:      'Cycle',
    tableDate:       'Date',
    tableKg:         'Total kg',
    tableGradeA:     'Grade A (kg)',
    tablePackout:    'Packout %',
    tableCostPerKg:  'Cost/kg',
    tableRecordedBy: 'Recorded by',
    noHarvests:      'No harvests recorded.',
    loadingHarvests: 'Loading harvests...',
    // Merma / Waste
    mermaTitle:           'Waste / Losses',
    mermaKpiRejectedKg:   'Total rejected (kg)',
    mermaKpiImpact:       'Economic impact',
    mermaKpiRecords:      'Registered events',
    mermaTableDate:       'Date',
    mermaTableReason:     'Reason',
    mermaTableKg:         'Rejected kg',
    mermaTableBoxes:      'Boxes',
    mermaTableLabel:      'Label',
    mermaTableImpact:     'Impact ($)',
    mermaTableStatus:     'Status',
    mermaNoRecords:       'No waste recorded.',
    mermaLoading:         'Loading waste data...',
    mermaReasonCLIMA:           'Weather damage',
    mermaReasonPLAGA:           'Pest/disease damage',
    mermaReasonMECANICO:        'Mechanical damage',
    mermaReasonRECHAZO_CALIDAD: 'Quality/size rejection',
    mermaReasonPUDRICION:       'Rot/decay',
    mermaReasonOTRO:            'Other',
    mermaChartTitle:      'Waste by reason (kg)',
    newHarvestBtn:      '+ Log harvest',
    newHarvestTitle:    'Log harvest / packing',
    fieldLoteId:        'Lot *',
    fieldLoteZone:      'Lot or zone',
    fieldBatchId:       'Cycle (batchId) *',
    fieldContainerCode: 'Container code',
    fieldBoxesTitle:    'Boxes',
    fieldCalibre:       'Size',
    fieldBoxCount:      'Box count',
    fieldWeightKg:      'Weight (kg)',
    fieldGrade:         'Grade',
    addBoxRow:          '+ Add row',
    removeBoxRow:       'Remove',
    fieldNotes:         'Notes',
    loteRequired:       'Lot is required',
    batchRequired:      'Cycle (batchId) is required',
    boxesRequired:      'Add at least one box row',
    createError:        'Error logging harvest',
    create:             'Log',
  },

  inventory: {
    title:          'Inventory',
    tabStock:       'Stock',
    tabMovements:   'Movements',
    selectWarehouse: 'Select warehouse',
    warehouseShared: 'Shared (whole company)',
    lowStockAlert:  'Low-stock products',
    tableProduct:   'Product',
    tableCategory:  'Category',
    tableQuantity:  'Quantity',
    tableUnit:      'Unit',
    tableExpiry:    'Expiry',
    tableCost:      'Cost',
    tableDate:      'Date',
    tableType:      'Type',
    tableReason:    'Reason',
    tableRef:       'Reference',
    tableLote: 'Field',
    tableEquipment: 'Equipment',
    tableRecipient: 'Received by',
    tableUser: 'User',
    productDetailTitle: 'Product details',
    productDetailTabSummary: 'Summary',
    productDetailTabMovements: 'Movements',
    productDetailClose: 'Close',
    productDetailOpen: 'View details',
    productDetailNoMovements: 'This product has no movements.',
    productDetailLoading: 'Loading movements...',
    productDetailError: 'Could not load movements.',
    productDetailCurrentStock: 'Current stock',
    productDetailMovementCount: 'Recorded movements',
    productDetailLastMovement: 'Last movement',
    noStock:        'No products in stock.',
    noMovements:    'No movements recorded.',
    loadingStock:   'Loading stock...',
    loadingMovements: 'Loading movements...',
    movementTypes: {
      ENTRY:      'Entry',
      EXIT:       'Exit',
      RETURN:     'Return',
      ADJUSTMENT: 'Adjustment',
      EXPIRY:     'Expiry',
    },
    reasonLabels: {
      PURCHASE:   'Purchase',
      FIELD_USE:  'Field use',
      RETURN:     'Return',
      ADJUSTMENT: 'Adjustment',
      EXPIRY:     'Expiry',
    },
    stockLow:     'Low stock',
    stockExpiring: 'Expiring',
    stockOk:      'OK',
    kpiTotalValue:    'Total warehouse value',
    kpiLowStock:      'Products below minimum',
    kpiExpiringSoon:  'Expiring (30 days)',
    // ── Catalog (products/supplies tab) ──────────────────────────────────
    tabCatalog:         'Catalog',
    catalogTitle:       'Product / supply catalog',
    productSingular:    'product',
    productPlural:      'products',
    allCategories:      'All categories',
    loadingCatalog:     'Loading catalog...',
    noProductsPrefix:   'No products registered',
    noProductsFilterSuffix: ' in this category',
    noProductsSuffix:   '.',
    addFirstProduct:    'Add first product',
    newProduct:         'New product',
    editProductTitlePrefix: 'Edit — ',
    newProductDialogTitle: 'New product / supply',
    createError:        'Error creating product',
    updateError:        'Error updating product',
    nameRequired:       'Name is required',
    invalidCost:        'Invalid unit cost',
    fieldName:          'Name *',
    fieldNamePlaceholder: 'Abamectin, Novatec...',
    fieldCategory:      'Category *',
    fieldUnit:          'Unit *',
    fieldUnitCost:      'Unit cost ($) *',
    fieldUnitCostPlaceholder: '0.00',
    fieldMinStock:      'Minimum stock',
    fieldMinStockPlaceholder: '10',
    fieldActiveIngredient: 'Active ingredient',
    fieldActiveIngredientPlaceholder: 'Abamectin 18g/L...',
    fieldSenasica:      'SENASICA registration',
    fieldSenasicaPlaceholder: 'SENASICA-12345',
    fieldDescription:   'Description',
    fieldDescriptionPlaceholder: 'Additional notes...',
    fieldNotifyUsers:    'Notify (users)',
    fieldNotifyUsersHint: 'They get a Telegram message when stock crosses the minimum downward.',
    fieldNotifyUsersPlaceholder: 'Select users',
    fieldNotifyRoles:    'Notify (roles)',
    fieldNotifyRolesPlaceholder: 'Select roles',
    cancel:             'Cancel',
    saveChanges:        'Save changes',
    createProduct:      'Create product',
    deleteProductTitle: 'Delete product',
    deleteProductConfirmPrefix: 'Delete ',
    deleteProductConfirmSuffix: '? This action cannot be undone. If there is active stock in warehouse, reports may be affected.',
    delete:             'Delete',
    colName:            'Name',
    colCategory:        'Category',
    colUnit:            'Unit',
    colUnitCost:        'Unit cost',
    colMinStock:        'Min. stock',
    colActiveIngredient: 'Active ingredient',
    colImage:           'Image',
    productImage:       'Product image',
    uploadImage:        'Upload image',
    imageError:         'Could not upload the image. Try again.',
    colSenasica:        'SENASICA',
    colActions:         'Actions',
    catAgroquimico:     '🧪 Chemical',
    catFertilizante:    '🌱 Fertilizer',
    catSemilla:         '🌾 Seed',
    catCombustible:     '⛽ Fuel',
    catOtro:            '📦 Other',
    unitLitros:         'Liters',
    unitKg:             'Kilograms',
    unitPiezas:         'Units',
    unitToneladas:      'Tons',
    unitCajas:          'Boxes',
    newMovement:            'New movement',
    newMovementDialogTitle: 'Register entry / exit',
    fieldMovementType:      'Type *',
    movementTypeIn:         'Entry',
    movementTypeOut:        'Exit',
    fieldProduct:           'Product *',
    selectProduct:          'Select a product',
    fieldQty:               'Quantity *',
    fieldQtyPlaceholder:    '0',
    fieldReason:            'Reason *',
    fieldReasonPlaceholder: 'Purchase from supplier, applied on Lot 4...',
    fieldExpiryDate:        'Expiry date',
    fieldLoteNumber:        'Manufacturer lot number',
    fieldLoteNumberPlaceholder: 'L-2026-001',
    productRequired:        'Select a product',
    qtyRequired:            'Quantity must be greater than 0',
    reasonRequired:         'Reason is required',
    createMovementError:    'Error registering movement',
    registerMovement:       'Register',
    transferButton:         'Transfer',
    transferDialogTitle:    'Transfer between warehouses',
    fieldFromWarehouse:     'Source warehouse',
    fieldToWarehouse:       'Destination warehouse',
    fieldTransferNote:      'Note',
    fieldTransferNotePlaceholder: 'Reason or reference for the transfer',
    transferWarehouseRequired: 'Select a warehouse',
    transferSameWarehouse:  'Source and destination must be different',
    transferNeedTwoWarehouses: 'You need at least two warehouses to transfer',
    transferSubmit:         'Transfer',
    transferError:          'Error recording the transfer',
    // ── Negative stock alert queue ──────────────────────────────────────────
    tabAlerts:              'Alerts',
    tabTransfers: "Transfers",
    transfersColDate: "Date",
    transfersColOrigin: "From",
    transfersColDestination: "To",
    transfersColProduct: "Product",
    transfersColQty: "Quantity",
    transfersColBy: "Done by",
    transfersBadgeReversed: "Reversed",
    transfersBadgeReverse: "Reversal",
    transfersRefresh: "Refresh",
    transfersLoadMore: "Load more",
    transfersEmpty: "No transfers recorded.",
    transfersLoading: "Loading transfers...",
    transfersLoadError: "Could not load transfers.",
    noAlerts:               'No negative stock alerts.',
    alertSince:             'Since',
    resolveAlert:           'Resolve',
    ignoreAlert:            'Ignore',
    ignoreAllAlerts:        'Ignore All',
    ignoreDialogTitle:      'Ignore alert',
    ignoreAllDialogTitle:   'Ignore all alerts',
    ignoreReasonLabel:      'Reason',
    ignoreReasonPlaceholder: 'E.g. cause already known, physical stock confirmed...',
    ignoreAllConfirmWarehouse: 'Ignore all alerts for this warehouse?',
    ignoreAllConfirmTenant: 'Ignore all alerts across the whole company?',
    confirmIgnore:          'Ignore',
    // ── Per-product diagnostics panel ───────────────────────────────────────
    diagnosticsTitle:       'Stock diagnostics',
    deleteMovement:         'Delete',
    editMovement:           'Edit',
    editMovementDialogTitle: 'Edit movement',
    confirmDeleteMovement:  'Delete this movement? Stock will be recalculated automatically.',
    systemMovementLocked:   'Automatic',
    systemMovementLockedTooltip: 'This movement was generated automatically by a field operation and cannot be edited or deleted.',
  },

  settings: {
    title:        'Settings',
    usersTitle:   'Settings — Users',
    noPermission: 'You do not have permission to access this section.',
    loadingUsers: 'Loading users...',
    languageTitle: 'System language',
    languageLabel: 'Language',
    languageEs:   'Español',
    languageEn:   'English',
    languageZh:   '中文',
    ranchoTitle:      'Ranch configuration',
    ranchoName:       'Ranch name',
    ranchoLocation:   'Location',
    ranchoSave:       'Save changes',
    ranchoSaved:      'Changes saved',
    behaviorTitle:    'System behavior',
    warehouseMode:    'Swine warehouse mode',
    warehouseModeDesc: 'Configure whether swine lots use shared, separate, or both warehouses.',
    warehouseShared:  'Shared',
    warehouseSeparate: 'Separate',
    warehouseBoth:    'Both',
    reportMode:       'Swine report mode',
    reportModeDesc:   'Define whether the weekly swine report is integrated into the Farm Report or sent separately.',
    reportIntegrated: 'Integrated',
    reportSeparate:   'Separate',
    budgetBlock:      'Budget block',
    budgetBlockDesc:  'Soft block at 100% — requires Admin authorization.',
    budgetBlockSoft:  'Soft (request authorization)',
    budgetBlockHard:  'Hard (always block)',
    // ── AI providers ──────────────────────────────────────────────────────────
    aiProvidersTitle:    'AI providers (Field Bot)',
    aiProvidersDesc:     'Fallback order when an AI provider fails. Disabled providers are skipped entirely.',
    aiPresetLabel:       'Priority',
    aiPresetCost:        'Cost (free providers first)',
    aiPresetSpeed:       'Speed (quality first)',
    aiPresetCustom:      'Custom',
    aiProviderEnabled:   'Enabled',
    aiMoveUp:            'Move up',
    aiMoveDown:          'Move down',
    aiProvidersSave:     'Save order',
    aiProvidersSaved:    'Order saved',
    // ── Admin Config — dynamic tabs (Phase 3.2-3.4) ───────────────────────────
    adminConfig: {
      tabProviders: 'AI Providers', tabScouting: 'Scouting', tabBudget: 'Budget',
      tabMachinery: 'Machinery', tabHarvest: 'Harvest', tabLocalization: 'Localization',
      tabBotBehavior: 'Bot Behavior', tabSchedules: 'Schedules', tabCompliance: 'Compliance',
      tabFeatureFlags: 'Feature Flags', tabScope: 'Per-ranch scope', tabInventory: 'Inventory & warehouses', tabCashflow: 'Cashflow & anomalies', tabAgromonitoring: 'Agromonitoring (NDVI)', tabInvoicing: 'Invoicing (CFDI)', tabSystem: 'System', tabAgroModules: 'System Modules',
      tabSelfRegister: 'Self-Registration',
      tabRoutines: 'Routines',
      tabGateRules: 'Gate rules',
      routinesTitle: 'Automatic routines',
      routinesDesc: 'Tasks the system runs by itself at a fixed hour of your company. All are off until you turn them on.',
      routinesEmpty: 'There are no routines available yet.',
      routinesColRoutine: 'Routine', routinesColSchedule: 'Schedule', routinesColEnabled: 'Active', routinesColLastRun: 'Last run',
      routinesNever: 'Never',
      routinesScheduleDaily: 'Every day at {hour}:00',
      routinesScheduleWeekly: '{day} at {hour}:00',
      routinesDay1: 'Monday', routinesDay2: 'Tuesday', routinesDay3: 'Wednesday', routinesDay4: 'Thursday',
      routinesDay5: 'Friday', routinesDay6: 'Saturday', routinesDay7: 'Sunday',
      routinesLogTitle: 'Run history',
      routinesLogEmpty: 'No runs have been recorded yet.',
      routinesColDate: 'Date', routinesColCompany: 'Company', routinesColResult: 'Result', routinesColError: 'Error',
      routinesStatusRUNNING: 'Running', routinesStatusOK: 'Succeeded', routinesStatusERROR: 'Failed',
      routinesStatusSKIPPED: 'No changes', routinesStatusAWAITING_CONFIRMATION: 'Awaiting confirmation',
      routinesStatusCONFIRMED: 'Confirmed', routinesStatusCANCELLED: 'Cancelled', routinesStatusEXPIRED: 'Expired',
      routinesToggleError: 'Could not change the routine. Please try again.',
      groupScope: 'Ranches & scope', groupInventory: 'Inventory & warehouses', groupFinance: 'Finance', groupIntegrations: 'Integrations',
      groupField: 'Field operations & costs', groupBot: 'AI & bot', groupSystem: 'System & access', groupsLabel: 'Configuration groups',
      pageTitle: 'Settings',
      systemInfoTitle: 'System Information',
      backendLabel: 'Backend', frontendLabel: 'Frontend',
      botBehaviorDesc: 'Response, timeout, retry, and logging configuration.',
      schedulesDesc: 'Alerts, forecast snapshots, session expiration, and other scheduled jobs.',
      complianceDesc: 'PHI/REI/MRL rules, restriction windows, and harvest safety alerts.',
      featureFlagsDesc: 'Enable/disable system modules and features.',
      saveButton: 'Save', savedBadge: 'Saved',
      scoutingTitle: 'Pest Severity Thresholds',
      scoutingLowLabel: 'LOW Severity (%)', scoutingLowDesc: 'Minimum damage to flag scouting as LOW',
      scoutingMediumLabel: 'MEDIUM Severity (%)', scoutingMediumDesc: 'Minimum damage to flag scouting as MEDIUM',
      scoutingHighLabel: 'HIGH Severity (%)', scoutingHighDesc: 'Minimum damage to flag scouting as HIGH',
      budgetTitle: 'Budget Thresholds',
      budgetWarnLabel: 'Warning (%)', budgetWarnDesc: 'Consumption percentage that triggers the yellow alert',
      budgetBlockLabel: 'Block (%)', budgetBlockDesc: 'Consumption percentage that triggers the critical alert',
      budgetModeLabel: 'Mode', budgetModeDesc: 'What to do when the budget is exceeded',
      machineryTitle: 'Machinery Alerts',
      machineryYellowLabel: 'Yellow Alert (%)', machineryYellowDesc: 'Budget % that triggers the alert',
      machineryOrangeLabel: 'Orange Alert (%)', machineryOrangeDesc: 'Budget % that triggers the alert',
      machineryRedLabel: 'Red Alert (%)', machineryRedDesc: 'Budget % that triggers the alert',
      harvestTitle: 'Harvest Thresholds',
      harvestGoodLabel: 'Good Packout (%)', harvestGoodDesc: 'Packout from which the result counts as good',
      harvestWarnLabel: 'Warning Packout (%)', harvestWarnDesc: 'Packout below which it is flagged as warning',
      harvestBadLabel: 'Critical Packout (%)', harvestBadDesc: 'Packout below which it is flagged as critical',
      harvestGradesLabel: 'Grade Labels', harvestGradesDesc: 'Comma separated (e.g. A,B,C)',
      localizationTitle: 'Regional Settings',
      currencyLabel: 'Currency', currencyDesc: 'Currency used for costs and prices',
      timezoneLabel: 'Timezone', timezoneDesc: "Defines what 'today' means in reminders",
      defaultLangLabel: 'Default Language', defaultLangDesc: 'Company language for users without their own language',
      phonePrefixLabel: 'Phone Prefix', phonePrefixDesc: 'E.g. +52 for Mexico',
      workdayHoursLabel: 'Workday Hours', workdayHoursDesc: 'Hours in a full workday',
      dateFormatLabel: 'Date Format', dateFormatDesc: 'Pattern for displaying dates (dd/MM/yyyy, MM/dd/yyyy, or yyyy-MM-dd).',
      decimalSeparatorLabel: 'Decimal Separator', decimalSeparatorDesc: 'Character used for decimals in numbers (dot or comma).',
      speedUnitLabel: 'Speed Units', speedUnitDesc: 'Units for speed in machinery and transport (km/h or mi/h).',
      botBehaviorTitle: 'Bot Behavior',
      scheduledNoticeEnabledLabel: 'Scheduled task notices', scheduledNoticeEnabledDesc: 'Off: scheduled notices wait and go out when turned back on',
      scheduledNoticeIntervalLabel: 'Scheduled notice check (min)', scheduledNoticeIntervalDesc: 'How often, in minutes, they are checked (multiples of 5)',
      reminderHourLabel: 'Daily Reminder Hour', reminderHourDesc: 'Local hour at which next-day task reminders are sent',
      schedulesTitle: 'Forecast Schedules',
      spread7Label: '7-Day Spread (±%)', spread7Desc: 'Spread between conservative and ideal scenario at 7 days',
      spread15Label: '15-Day Spread (±%)', spread15Desc: 'Spread between conservative and ideal scenario at 15 days',
      spread60Label: '60-Day Spread (±%)', spread60Desc: 'Spread between conservative and ideal scenario at 60 days',
      confidence7Label: '7-Day Confidence (%)', confidence7Desc: 'Confidence reported with the 7-day forecast',
      confidence15Label: '15-Day Confidence (%)', confidence15Desc: 'Confidence reported with the 15-day forecast',
      confidence60Label: '60-Day Confidence (%)', confidence60Desc: 'Confidence reported with the 60-day forecast',
      complianceTitle: 'Cold Chain',
      coldMinLabel: 'Minimum Temperature (°C)', coldMinDesc: 'Minimum temperature allowed in transit before flagging an excursion',
      coldMaxLabel: 'Maximum Temperature (°C)', coldMaxDesc: 'Maximum temperature allowed in transit before flagging an excursion',
      featureFlagsTitle: 'Feature Flags',
      trackingEnabledLabel: 'Shipment Tracking', trackingEnabledDesc: 'Enables telemetry-based tracking (route and temperature)',
      autoDeductLabel: 'Auto Warehouse Deduction', autoDeductDesc: 'Automatically deducts inventory when confirming an activity',
      negativeStockIgnoreScopeLabel: '"Ignore All" scope (negative stock)', negativeStockIgnoreScopeDesc: 'When ignoring all negative-stock alerts from the Inventory queue: this warehouse only, or the whole company.',
      inventoryScopeByRanchoLabel: 'Inventory by active ranch', inventoryScopeByRanchoDesc: 'Filters warehouses and products by the selected ranch. Off: the whole company is shown. Records with no ranch assigned are always visible.',
      machineryScopeByRanchoLabel: 'Machinery by active ranch', machineryScopeByRanchoDesc: 'Filters machinery by the selected ranch. Off: the whole company is shown. Equipment with no ranch assigned is always visible.',
      invoicingEnabledLabel: 'Electronic invoicing (CFDI)', invoicingEnabledDesc: 'Lets the bot issue CFDI 4.0 invoices via Facty. Off by default. The Facty connection is configured separately.',
      invoicingRfcLabel: 'Issuer RFC', invoicingRfcDesc: 'RFC of the company issuing invoices (12 or 13 characters). Empty blocks stamping.',
      invoicingRegimenLabel: 'Issuer tax regime', invoicingRegimenDesc: 'SAT code, 3 digits (e.g. 601). Empty blocks stamping.',
      invoicingModeLabel: 'Stamping mode', invoicingModeDesc: 'Sandbox for testing; production only once the Facty connection is validated.',
      factyCredentialsTitle: 'Facty connection', factyCredentialsConfigured: 'Connection configured', factyCredentialsMissing: 'Not connected: stamping is blocked', factyOrgLabel: 'Facty organization (org)', factyApiKeyLabel: 'Facty API key', factyCredentialsSave: 'Save connection', factyCredentialsRemove: 'Remove connection', factyCredentialsError: 'Could not save. Check the values and try again.',
      ndviEnabledLabel: 'NDVI satellite imagery', ndviEnabledDesc: 'Enables the NDVI module (Agromonitoring satellite imagery, consumes plan quota). Off by default. Each ranch can override this value.',
      ndviRanchosTitle: 'NDVI per ranch', ndviRanchosDesc: 'Overrides the company setting for each ranch. Checked = NDVI enabled for that ranch.',
      mrlMissingDoseLabel: 'Harvest without a recorded dose (MRL)', mrlMissingDoseDesc: 'When a pesticide application has no dose recorded, the MRL cannot be evaluated. Block: the harvest is stopped until the dose is recorded. Warn (default): only a warning. PHI and REI always block.',
      mrlMissingDoseBlock: 'Block harvest', mrlMissingDoseWarn: 'Warn only',
      mrlMissingDoseRanchosTitle: 'Harvest dose rule per ranch', mrlMissingDoseRanchosDesc: 'Overrides the company setting for each ranch. Block is a per-ranch decision: leave it on Warn unless the ranch needs it.',
      anomalyAutoLabel: 'Automatic Anomaly Detection', anomalyAutoDesc: 'Automatically raises anomalies when plan deviations are detected',
      cashflowBudgetLabel: 'Budget in Cashflow', cashflowBudgetDesc: 'Includes the cycle budget as outflows in the projected cashflow',
      cashflowForecastLabel: 'Forecast in Cashflow', cashflowForecastDesc: 'Projects forecast harvest as future inflow',
      useFarmModulesLabel: 'Use Zones', useFarmModulesDesc: 'If disabled, ranches are only managed at the Lote (plot) level',
      agroModulesTab: 'System Modules',
      agroModulesHelp: 'Enable or disable the bot capabilities for this company.',
      agroModulesHelpWithCompany: 'Enable or disable the bot capabilities for {company}.',
      notifyDisabledSwitch: 'Notify disabled modules without using AI',
      saveAgroModules: 'Save modules',
      selfRegisterTitle: 'Self-Registration Link',
      selfRegisterDesc: 'Generate a link with a unique code so the agronomist or company owner can create their own account without an individual invitation.',
      generateSelfRegisterLink: 'Generate self-registration link',
      regenerateSelfRegisterLink: 'Regenerate link',
      selfRegisterLinkHint: 'Share this link — the previous code stops working as soon as you generate a new one:',
      selfRegisterCopyLink: 'Copy link',
      selfRegisterCopied: '✓ Copied',
      selfRegisterError: 'Could not generate the self-registration link',
    },
    roles: {
      SUPER_ADMIN:       'Super Admin',
      ADMIN:             'Admin',
      AGRONOMIST:        'Agronomist',
      SUPERVISOR:        'Supervisor',
      WAREHOUSE_MANAGER: 'Warehouse Manager',
      FIELD_WORKER:      'Field Worker',
      AUDITOR:           'Auditor',
      HR:           'HR',
      GUARD:           'Guard',
      INVESTOR:          'Investor',
    },
    systemTab:        'System',
    systemStatus:     'System status',
    checking:         'Checking...',
    refresh:          'Refresh',
    checkingProviders: 'Checking providers...',
    aiProviders:      'AI Providers',
    telegramBots:     'Telegram Bots',
    rotateTokenTitle: 'Rotate {name}',
    rotateTokenHint: 'Paste the new value. It is saved on the server (never shown again) and may take a few minutes to apply on every instance. For the Field Bot it is validated with Telegram and the webhook is registered again.',
    rotateTokenLabel: 'New value',
    rotateTokenSubmit: 'Rotate',
    rotateTokenOk: 'Key rotated.',
    rotateTokenError: 'Could not rotate the key.',
    rotateTokenBtn: 'Rotate',
    rotateTokenOkBot: 'Token rotated. Bot {bot} now has its webhook registered.',
    zoneCoordsTitle: 'Zone location',
    zoneCoordsHint: 'Calculates latitude and longitude (center of the boundary) for lots and zones that have a boundary but no coordinates. Used for exact location and weather. Existing coordinates are not changed.',
    zoneCoordsBtn: 'Calculate missing coordinates',
    zoneCoordsRunning: 'Calculating…',
    zoneCoordsDone: 'Done: {lotes} lots and {modules} zones updated ({noBoundary} without a boundary).',
    zoneCoordsNothing: 'Nothing to calculate: every zone with a boundary already has coordinates ({noBoundary} without a boundary).',
    zoneCoordsError: 'Could not calculate the coordinates.',
    secretsTitle: 'Keys and tokens',
    secretsHint: 'AI keys, Field Bot and GitHub. Values are never shown.',
    secretConfigured: 'Configured',
    secretNotConfigured: 'Not configured',
    frontendSection:  'Frontend (Dashboard)',
    miniappSection:   'Camera Mini App',
    miniappLabel:     'Version',
    backendSection:   'Backend (Lambda API)',
    checkingVersion:  'Checking version...',
    versionError:     'Could not fetch backend version.',
    version:          'Version',
    cached:           '(cached)',
    fieldBotLabel:    'API · Field Bot',
    statusOk:            'OK',
    statusRateLimited:   'Rate limit',
    statusNoCredits:     'No credits',
    statusNoWebhook:     'No webhook',
    statusPrivacyOn:     'Privacy mode ON',
    statusError:         'Error',
    statusUnconfigured:  'Not configured',
    statusDisabled:      'Disabled',
    statusTimeout:       'Timeout',
    aiUsageTitle:        'Usage by model',
    aiUsageDays7:        'Last 7 days',
    aiUsageDays30:       'Last 30 days',
    aiUsageShareTitle:   '% of calls by model',
    aiUsageFailuresTitle: 'Failures by cause',
    aiUsageNoData:       'No usage recorded in this period yet.',
    aiUsageCalls:        'calls',
    aiUsageOk:           'OK',
    aiUsageFailed:       'Failures',
    aiUsageAvgLatency:   'Avg latency',
    aiUsageTokens:       'Tokens (in / out)',
    aiUsageLoadError:    'Could not load AI usage.',
    chatFeedbackTitle:   'Chat ratings (👍 / 👎)',
    chatFeedbackUp:      'Helpful (👍)',
    chatFeedbackDown:    'Not helpful (👎)',
    chatFeedbackFrom:    'From',
    chatFeedbackTo:      'To',
    chatFeedbackLoadError: 'Could not load the ratings count.',
    aiCause_timeout:     'Timeout',
    aiCause_rate_limited: 'Rate limited',
    aiCause_no_credits:  'No credit/quota',
    aiCause_auth:        'Authentication',
    aiCause_server_error: 'Provider error',
    aiCause_other:       'Other',
    webhookActive:       'webhook active',
    privacyOnHint:       'Privacy mode ON — go to BotFather to disable it',
    noWebhookRegistered: 'No webhook registered',
    pendingUpdates:      'pending updates',
  },

  config: {
    title:                'System Configuration',
    tabRancho:            'Ranch',
    tabComportamiento:    'Behavior',
    tabProveedores:       'AI Providers',
  },

  // ── Inactive Account ────────────────────────────────────────────────────────────
  accountInactive: {
    title: 'Inactive Account',
    message: 'Your account has been deactivated. Please contact the administrator for more information.',
    backBtn: 'Back to Login',
  },

  // ── System Page ─────────────────────────────────────────────────────────────────
  systemPage: {
    title: 'System Configuration',
    botBehavior: 'Bot Behavior',
    botBehaviorDesc: 'Configure AI model, response style, default language.',
    schedules: 'Schedules and Notifications',
    schedulesDesc: 'Reminder times, alert frequency, activity windows.',
    compliance: 'Compliance',
    complianceDesc: 'PHI/REI/MRL guard strictness, harvest rules.',
    versioning: 'Versioning',
    versioningDesc: 'App version, build info, recent changes.',
    featureFlags: 'Feature Flags',
    featureFlagsDesc: 'Experimental features, beta testing.',
    underConstruction: '⚠️ Page under construction — coming soon',
  },

  // ── System Guide ────────────────────────────────────────────────────────────
  a11yLabels: {
    telegram: 'Telegram',
    ndviImage: 'NDVI image',
    logoPreview: 'Logo preview',
    close: 'Close',
  },
  sharedUi: {
    noAlerts: 'No alerts',
  },

  systemGuide: {
    title: 'Agrovid System Guide & Help Center',
    subtitle: 'Learn how to use the Telegram conversational bot and the Web Dashboard.',
    searchPlaceholder: 'Search module, voice command, compliance rule or term...',
    helpTooltip: 'System Guide / Help',
    openGuideBtn: 'View System Guide',
    tabs: {
      architecture: 'How It Works',
      roles: 'Role Guides',
      modules: 'Modules Manual',
      voiceCommands: 'Telegram Voice Commands',
      faq: 'FAQ & Glossary',
    },
    dualEntry: {
      telegramTitle: '📱 Conversational Bot (Field)',
      telegramDesc: 'Allows field workers to send voice notes, photos, and text in natural language, with Draft Gate confirmation before saving.',
      webTitle: '💻 Web Dashboard',
      webDesc: 'Complete admin console with role-based permissions for catalogs, inventory approvals, payroll, and P&L reports.',
      commonDbTitle: '⚙️ All Your Information in One Place',
      commonDbDesc: 'Both channels converge into the same system, keeping information secure and always in sync.',
    },
    copyBtn: 'Copy command',
    content: {
      archTitle: 'Two ways to work: Field (Telegram) + Office (Web Dashboard)',
      archIntro: 'Agrovid offers two independent paths to record and query farm information. Both channels converge in the same place, so the information always stays consistent and up to date.',
      draftGateBefore: 'It includes a',
      draftGateTerm: 'Draft Gate',
      draftGateAsk: 'the bot asks',
      draftGateQuestion: 'Do you confirm the record?',
      draftGateAfter: 'before saving.',
      webHint: '📊 Advanced filters by ranch/plot, PDF/Excel reports and interactive charts.',
      glossaryHeading: 'Agricultural Terms Glossary and Business Rules',
      glossaryQuestions: {
        PHI: '🧪 PHI (Pre-Harvest Interval)',
        REI: '🚫 REI (Restricted Entry Interval)',
        FEFO: '📦 FEFO (First Expired, First Out)',
        Packout: '📊 Packout % (Pack-out Percentage)',
        DraftGate: '🛑 Draft Gate (Bot Confirmation Brake)',
      },
      phiBlockNote: 'The system automatically blocks harvesting in the plot while the PHI has not expired.',
      botLabel: 'Bot:',
      mainActions: 'Main actions:',
      cheatsheetIntroPre: 'Send voice notes or natural text to the Telegram Bot. Click',
      cheatsheetIntroBtn: 'Copy',
      cheatsheetIntroPost: 'to try these phrases in your bot:',
      voiceExamples: [
        { title: '🌾 Crop Protection Application', phrase: 'I applied 25 liters of Kumulus DF in Plot 3 with tractor 01', note: 'The bot will detect the input, dose and plot, and will ask for your confirmation.', role: 'Field / Agronomist' },
        { title: '💧 Irrigation by Hours', phrase: 'Irrigated 5 hours in Plot 1 using deep well pump', note: 'Automatically calculates applied depth in mm and m3.', role: 'Field' },
        { title: '📦 Harvest and Pack-out', phrase: 'Packed 80 boxes of 4kg Size 40 Hass Avocado in Plot 2', note: 'Generates the packing traceability folio and updates the yield.', role: 'Field / Supervisor' },
        { title: '🚜 Machinery & Hour Meter', phrase: 'Started subsoiler with John Deere Tractor in Plot 4 hour meter 2150', note: 'Opens the machinery session for cost per hectare.', role: 'Field / Tractor operator' },
        { title: '🐛 Pest Monitoring', phrase: 'Detected medium-level thrips in Plot 5 with 4 points checked', note: 'Updates the plot phytosanitary status and alerts the agronomist.', role: 'Agronomist' },
        { title: '👥 Attendance and Piece Rate', phrase: 'Attendance 6 workers in Plot 1 piece rate 50 crates', note: 'Records progress for the biweekly payroll calculation.', role: 'Supervisor' },
      ],
      modules: {
        'rancho-lote': { name: 'Ranch and Plot Management', description: 'Hierarchical management of farm Ranches and Plots. Hectare and crop delimitation.', example: 'Register ranch Slp plot 4' },
        operations: { name: 'Crop Protection Operations and Labors', description: 'Voice/text capture of farm labors, applied inputs, doses and PHI/REI validation.', example: 'I applied 20L of Kumulus in Plot 4 with tractor 2' },
        irrigation: { name: 'Irrigation and Fertigation', description: 'Irrigation records by hours, well cubic meters or reservoir mm, calculating water efficiency and N-P-K.', example: 'Irrigated 4 hours in Plot 1 with 15kg of Nitrate' },
        harvest: { name: 'Harvest, Packing and Pack-out', description: 'Record of boxes harvested by size/grade, traceability folio closing and yield t/ha.', example: 'Packed 120 boxes of 4kg Size 40 Avocado Plot 3' },
        'crop-management': { name: 'Agronomic Monitoring and Pests', description: 'Pest/disease scouting, phenology, nutritional balance and safe harvest date calculation.', example: 'Detected light red spider mite in Plot 2 with 5 sampling points' },
        inventory: { name: 'Inventory and Warehouse (FEFO)', description: 'Catalog of agrochemicals and fertilizers. Invoice entries, FEFO dispatch outputs and reorder alerts.', example: 'Received 50 sacks of Urea at central warehouse' },
        machinery: { name: 'Machinery and Hour Meters', description: 'Timer for mechanical labors, initial/final hour meter, diesel consumption and operating cost per hectare.', example: 'Started harrow with Tractor 01 in Plot 2 hour meter 1450' },
        payroll: { name: 'Payroll and Labor', description: 'Attendance records and biweekly settlement by Day rate, Piece rate (crates) and Contract.', example: 'Recorded 8 piece-rate workers 40 crates in Plot 1' },
        finance: { name: 'Finance and P&L by Plot', description: 'Consolidation of direct costs (chemicals, water, diesel, payroll) vs harvest income to determine $/ha margin.', example: 'Check accumulated cost of Plot 3' },
        protocol: { name: 'Agronomic Protocol Upload', description: 'Upload and parsing of agronomic documents/recipes to plan the season\'s crop protection applications.', example: 'Load avocado crop protection protocol 2026' },
        merma: { name: 'Shrink and Reject Control', description: 'Record of fruit rejected or lost in the field/packing, tagging the cause (wind, pest, handling).', example: 'Report 120kg shrink fruit fallen in Plot 4 due to hail' },
        export: { name: 'Export Logistics', description: 'Management of international shipments, FDA/Phyto certification, seals and temperature monitoring.', example: 'Close container EXP-9901 with seal 44810' },
        transport: { name: 'Domestic Transport', description: 'Creation of outbound manifests, driver/plate assignment and field-to-packing haulage.', example: 'Truck 04 departure with 15 tons heading to central packing' },
        traceability: { name: 'Field-to-Packing Traceability', description: 'Full tracking of folios from the plot and agrochemical application to the final consumer.', example: 'Traceability folio TR-2026-0044' },
        porcino: { name: 'Pig Fattening', description: 'Pig fattening farm management: piglet intake, weighing, feed, conversion (FCR) and sales.', example: 'Record feed consumption 500kg fattening feed barns 2' },
        resources: { name: 'Resources and Crews', description: 'Catalog of machinery, DJI Agras drones, picker crews and contractors.', example: 'Assign crew 3 to harvest Plot 1' },
        drone: { name: 'Drone Application Flights', description: 'Records of DJI Agras T40 drone flights: liters/ha, covered hectares and flight time.', example: 'Drone T40 flight in Plot 5 applied 100L over 4 hectares' },
        calendar: { name: 'Calendar and Farm Tasks', description: 'Scheduling of labors by plot and worker with automatic Telegram reminders.', example: 'Create irrigation task Plot 2 for tomorrow 8am assigned to Juan' },
        weather: { name: 'Weather and Open-Meteo Forecast', description: 'Temperature, accumulated rainfall and 7-day forecast by plot geolocation.', example: 'What is the rain forecast for Plot 1 this week?' },
        users: { name: 'Users and Pre-registration', description: 'Staff onboarding by phone number, role assignment and Telegram linking.', example: 'Pre-register user 8112345678 role AGRONOMIST' },
        vocabulary: { name: 'Vocabulary and Voice Aliases', description: 'Company-level alias catalog to correct voice transcription errors (e.g. "Kumulo" -> "Kumulus DF").', example: 'Add alias "lote cuatro" for Lote-04' },
        sales: { name: 'Commercial Cycle and Sales', description: 'Recording of sales orders, invoicing and customer collection per harvest.', example: 'Sale 500 boxes avocado to Frutas del Norte customer' },
        support: { name: 'Support and Bot Tickets', description: 'Automatic technical support tickets when the Telegram Bot detects an error or confusion.', example: '/soporte The bot does not recognize input X' },
        developer: { name: 'Technical Diagnostics', description: 'Internal support and monitoring tools to review the overall system status.' },
      },
    },
    copied: 'Copied!',
    filterRole: 'Filter by Role:',
    allRoles: 'All Roles',
    rolesList: {
      FIELD_WORKER: 'Field Worker',
      AGRONOMIST: 'Agronomist',
      SUPERVISOR: 'Field Supervisor',
      ADMIN: 'Admin / Manager',
      INVESTOR: 'Investor (Read-only)',
    },
    rolesDesc: {
      FIELD_WORKER: 'Logs chemical applications, irrigation, harvest batches, and attendance via Telegram voice notes.',
      AGRONOMIST: 'Monitors pests/scouting, prescribes chemicals, validates PHI safety windows, and loads protocols.',
      SUPERVISOR: 'Starts machinery timers, manages crews, requests inventory stock, and audits packout.',
      ADMIN: 'Manages ranches/lots, approves inventory releases, approves biweekly payroll, and audits finances.',
      INVESTOR: 'Executive read-only access to P&L, production progress, yield t/ha, and traceability.',
    },
    glossaryTitle: 'Agricultural Glossary',
    glossary: {
      PHI: 'PHI (Pre-Harvest Interval): Required safety days after applying a chemical before harvest.',
      REI: 'REI (Restricted Entry Interval): Hours to wait before workers can re-enter treated field.',
      FEFO: 'FEFO (First Expired, First Out): Inventory dispatching rule prioritizing nearest expiration date.',
      Packout: 'Packout %: Percentage of harvested fruit meeting quality standards for export/packaging.',
      DraftGate: 'Draft Gate: Confirmation step where the bot asks "Confirm log X?" before saving the record.',
    },
    cards: {
      architecture: {
        title: '🚀 How Agrovid Works',
        desc: 'Learn the two ways to record information: by voice with the Telegram Bot or from the Web Dashboard.',
      },
      modules: {
        title: '📦 Module Guide',
        desc: 'Step-by-step manual for Irrigation, Harvest, Phytosanitary, FEFO Inventory, Machinery, Payroll and Finance.',
      },
      voice: {
        title: '🎙️ Telegram Voice Commands',
        desc: 'Copyable examples of voice notes to send to the Telegram Bot (applications, irrigation, harvests).',
      },
      roles: {
        title: '👤 Guides by Role',
        desc: 'Learn the responsibilities and key screens for Workers, Agronomists, Supervisors and Investors.',
      },
      faq: {
        title: '❓ Glossary & Business Rules',
        desc: 'Understand what PHI, REI, FEFO, Packout % and Draft Gate mean, plus answers to frequent questions.',
      },
    },
  },

  // ── Global Components ──────────────────────────────────────────────────────────
  errorBoundary: {
    title: 'Something went wrong',
    message: 'An unexpected error occurred in the application.',
    reloadBtn: 'Reload page',
  },

  protectedRoute: {
    title: 'Access denied',
    message: 'You do not have permission to view this section. Contact an administrator if you believe this is an error.',
    dashboardBtn: 'Back to Dashboard',
  },

  demoGate: {
    loading: 'Loading demo…',
    error: 'Could not start demo session. Verify that the server is active.',
  },

  // ── Permission Modules ──────────────────────────────────────────────────────────
  permissionModules: {
    ASISTENCIA: 'Attendance',
    GESTIÓN: 'Management',
    OPERACIONES: 'Operations',
    CAMPO: 'Field',
    COSECHA: 'Harvest',
    ANÁLISIS: 'Analytics',
    SISTEMA: 'System',
  },

  permissionsMatrix: {
    roleEditHint: 'Click a cell to give or remove a permission for a role in your company. It applies when each person signs in again. Super Admin cannot be changed.',
    saveRoles: 'Save role changes',
    discardRoles: 'Discard',
    resetRoles: 'Reset to defaults',
    rolesSaved: 'Role permissions saved',
    rolesSaveError: 'Could not save role permissions',
    rolesDirty: 'Unsaved changes',
    title:        'Permissions Matrix',
    subtitle:     'Permissions assigned by role. Permissions apply based on the session\'s active role.',
    noAccess:     'You do not have permission to access this section.',
    permissionCol: 'Permission',
    tabMatrix:    'Matrix by Role',
    tabOverrides: 'Additional Permissions by User',
    allowed:      'Allowed',
    denied:       'Not allowed',
    searchPlaceholder: 'Search by name or phone',
    permissions: {
      'users:read': 'View users',
      'users:write': 'Edit users',
      'admin:ranchos': 'Manage ranches',
      'admin:rbac': 'Manage permissions',
      'admin:super': 'Super Admin',
      'inventory:read': 'View inventory',
      'inventory:write': 'Move inventory',
      'warehouse:transfer': 'Transfer between warehouses',
      'payroll:read': 'View payroll',
      'payroll:write': 'Edit payroll',
      'machinery:read': 'View machinery',
      'lotes:read': 'View plots',
      'lotes:write': 'Edit plots',
      'lotes:approve': 'Approve or delete plot records',
      'tasks:write': 'Edit tasks',
      'tasks:manage': 'Manage tasks (create/edit/delete)',
      'crop:write': 'Record crop data',
      'irrigation:write': 'Record irrigation',
      'harvest:read': 'View harvest',
      'harvest:write': 'Record harvest',
      'merma:read': 'View waste',
      'finance:read': 'View finance',
      'finance:write': 'Edit finance',
      'reports:read': 'View reports',
      'reports:configure': 'Configure reports',
      'audit:read': 'View audit log',
      'vocabulary:write': 'Manage vocabulary',
      'config:write': 'Edit configuration',
      'attendance:read': 'View attendance',
      'attendance:take': 'Take attendance',
      'attendance:enroll': 'Enroll people (face)',
      'attendance:policy': 'Configure attendance policy',
      'attendance:prebaja_decide': 'Decide pre-departures',
      'attendance:delete_biometrics': 'Delete biometric data',
      'attendance:gate': 'Use gate (access pass)',
      'attendance:gate_authorize': 'Authorize exit of offboarded people',
    },
    userNotFound: 'User not found',
    saved: 'Permissions saved successfully',
    saveError: 'Error saving permissions',
    userLabel: 'User',
    roleLabel: 'Role',
    phoneLabel: 'Phone',
    rolePermsTitle: 'Permissions by role',
    noBasePerms: 'No base permissions',
    extraPermsTitle: 'Additional permissions (optional)',
    saveChanges: 'Save changes',
    searchPrompt: 'Search for a user to assign additional permissions',
  },

  porcino: {
    title:          '🐷 Swine Farm — Fattening',
    corralTitle:    'Pig pens',
    corralLoading:  'Loading pig pens...',
    corralEmpty:    'No pig pens yet. Use "+ New pig pen" to create one.',
    corralCapLabel: 'Cap.',
    corralCapUnit:  'heads',
    kpisLoading:    'Loading KPIs...',
    noActiveLot:    'No active lot in this pig pen. Use "+ Log entry" to register a pig entry.',
    eventsTitle:    'Event history',
    eventsLoading:  'Loading events...',
    eventsEmpty:    'No events recorded',
    colDate:        'Date',
    colType:        'Type',
    colDetail:      'Detail',
    colRecordedBy:  'Recorded by',
    detailCabezas:  'heads',
    detailKgCab:    'kg/head',
    detailKgFood:   'kg feed',
    detailCausa:    'Cause',
    kpiHeads:       'Current heads',
    kpiDaysUnit:    'days in pig pen',
    kpiEntryPrefix: 'Entry',
    kpiWeight:      'Average weight',
    kpiGdp:         'ADG',
    kpiGdpSub:      'Average daily gain',
    kpiFcr:         'Feed conversion',
    kpiFcrUnit:     'kg consumed',
    kpiMortality:   'Mortality',
    kpiMortalityUnit: 'losses recorded',
    kpiCostKg:      'Cost/kg live',
    kpiCostKgSub:   'Total cost / current live kg',
    statusInUse:    'In use',
    statusActive:   'Active',
    statusEmpty:    'Empty',
    statusInactive: 'Inactive',
    eventoEntrada:  '🐷 Entry',
    eventoPesaje:   '⚖️ Weighing',
    eventoAlimento: '🌽 Feed',
    eventoMortalidad: '💀 Loss',
    eventoMedicacion: '💊 Medication',
    eventoVenta:    '💰 Sale',
    newCorralBtn:      '+ New pig pen',
    newCorralTitle:    'New pig pen',
    fieldRanchoId:     'Ranch *',
    searchRanchoPlaceholder: 'Search ranch...',
    fieldCorralName:   'Name *',
    fieldCapacity:     'Capacity (heads) *',
    newEntradaBtn:     '+ Log entry',
    newEntradaTitle:   'Log batch entry',
    fieldCabezas:      'Heads *',
    fieldPesoPromedio: 'Average weight (kg) *',
    fieldCostoPorCabeza: 'Cost per head ($, optional)',
    fieldProveedor:    'Supplier',
    newEventBtn:       '+ Log event',
    newEventTitle:     'Log event',
    fieldEventType:    'Event type *',
    fieldKgAlimento:   'Feed (kg) *',
    fieldTipoAlimento: 'Feed type',
    fieldCausa:        'Cause',
    fieldPesoTotal:    'Total weight sold (kg) *',
    fieldPrecioKg:     'Price per kg ($) *',
    fieldDestino:      'Destination',
    fieldNotes:        'Notes',
    ranchoRequired:    'Ranch is required',
    nameRequired:      'Name is required',
    capacityRequired:  'Capacity must be greater than 0',
    cabezasRequired:   'Heads must be greater than 0',
    pesoRequired:      'Average weight must be greater than 0',
    kgAlimentoRequired: 'Feed kg must be greater than 0',
    pesoTotalRequired: 'Total weight is required',
    precioKgRequired: 'Price per kg is required',
    createError:       'Error logging',
    create:            'Log',
    gdpUnit: 'g/day',
  },

  payroll: {
    title:            'Payroll',
    loading:          'Loading payroll...',
    openPeriod:       'Open period',
    noActivePeriod:   'No active period',
    kpiTotal:         'Total accumulated',
    kpiWorkers:       'Workers',
    kpiWorkersSub:    'In the active period',
    kpiJornal:        'Day rate',
    kpiJornalSub:     'Per day worked',
    kpiDestajo:       'Piece rate',
    kpiDestajoCub:    'Per unit produced',
    kpiContrato:      'Contract',
    kpiContratoSub:   'Fixed amount agreed',
    tabActive:        'Active Period',
    tabByLot:         'By Field',
    tabHistory:       'History',
    recordsLabel:     'Records',
    noActivePeriodOpen: 'No open period',
    noEntries:        'No records in the active period.',
    noEntriesSub:     'Use the add-entry button to add workers to this period.',
    colWorker:        'Worker',
    colLot:           'Field',
    colActivity:      'Activity',
    colType:          'Type',
    colDetail:        'Detail',
    colTotal:         'Total',
    jornalDays:       'days',
    contratoLabel:    'Fixed contract',
    chartTitle:       'Labor Cost by Field (MXN)',
    chartLegend:      'Total Labor',
    noData:           'No data',
    distTitle:        'Pay Type Distribution',
    distColType:      'Type',
    distColAmount:    'Amount',
    distTotal:        'Total',
    historyTitle:     'Period History',
    historyLoading:   'Loading history...',
    historyEmpty:     'No periods registered.',
    histColPeriod:    'Period',
    histColDates:     'Dates',
    histColStatus:    'Status',
    histColCostBasis: 'Cost Basis',
    histColTotal:     'Total',
    statusOpen:       'Open',
    statusClosed:     'Closed',
    costBasisTotal:   'Total production',
    costBasisSellable: 'Sellable',
    openPeriodBtn:    '+ Open period',
    closePeriodBtn:   'Close period',
    closePeriodConfirm: 'Close this payroll period? The final total will be calculated and no more entries can be added.',
    addEntryBtn:      '+ Log payment',
    openPeriodTitle:  'Open payroll period',
    fieldLabel:       'Label *',
    fieldLabelPlaceholder: '1st Fortnight July 2026',
    fieldStartDate:   'Start date *',
    fieldEndDate:     'End date *',
    fieldCostBasis:   'Cost basis',
    addEntryTitle:    'Log payment',
    fieldWorkerName:  'Worker name *',
    unknownLote: 'Plot unavailable',
    workerPlaceholder: 'Search or type a name',
    workerFreeText: 'The typed name will be used',
    lotePlaceholder: 'Search plot',
    fieldWorkerPhone: 'Phone (optional)',
    fieldLoteId:      'Lot *',
    payTypeJORNAL:  'Day rate',
    payTypeDESTAJO: 'Piece rate',
    payTypeCONTRATO: 'Contract',
    fieldPayType:     'Pay type *',
    fieldActivity:    'Activity *',
    fieldDays:        'Days worked',
    fieldUnits:       'Units produced',
    fieldUnitLabel:   'Unit (kg, boxes...)',
    fieldRate:        'Rate ($) *',
    fieldNotes:       'Notes',
    labelRequired:    'Label is required',
    datesRequired:    'Start and end date are required',
    workerRequired:   'Worker name is required',
    loteRequired:     'Select a lot',
    activityRequired: 'Activity is required',
    rateRequired:     'Rate must be greater than 0',
    openPeriodError:  'Error opening period',
    closePeriodError: 'Error closing period',
    addEntryError:    'Error logging payment',
    create:           'Create',
  },

  activityTypes: {
    PREPARACION:   'Soil prep',
    SIEMBRA:       'Planting',
    RIEGO:         'Irrigation',
    FERTILIZACION: 'Fertilization',
    FITOSANITARIO: 'Pest control',
    COSECHA:       'Harvest',
    MAQUINARIA:    'Machinery',
    LABORES:       'Labor',
    OTRO:          'Other',
    NOVEDAD:       'Field note',
    CLIMA:         'Weather',
  },

  activityFeed: {
    loading: 'Loading activities...',
    empty:   'No recent activities.',
    unknownPerson: 'Someone',
    verb: {
      PREPARACION:   'prepared the field',
      SIEMBRA:       'planted',
      RIEGO:         'irrigated',
      FERTILIZACION: 'applied fertilizer',
      FITOSANITARIO: 'applied a chemical',
      COSECHA:       'harvested',
      MAQUINARIA:    'used machinery',
      LABORES:       'did field labor',
      OTRO:          'logged an operation',
      NOVEDAD:       'reported a field note',
      CLIMA:         'logged a weather event',
    },
    allTab:           'All',
    searchPlaceholder: 'Search notes and materials...',
    noResults:        'No results for this search.',
    loadMore:         'Load more',
    editNotes:        'Edit note',
    sowingDensity:    'Planting density',
    sowingRowSpacing: 'Row spacing',
    weatherRain: 'Rain',
    weatherFrost: 'Frost',
    sowingPlantSpacing: 'Plant spacing',
    sowingGenetics:   'Genetics',
    notesLabel:       'Note',
    notesPlaceholder: 'Write a note...',
    saveNotes:        'Save',
    cancelEdit:       'Cancel',
    saveError:        'Could not save the note. Try again.',
    newBadge:         '{count} new items',
    delete:           'Delete',
    deleteConfirmTitle: 'Delete this record?',
    deleteConfirmBody: 'This record from "{name}" will be removed from the history and the related inventory deduction will be reversed.',
    deleteError:      'The record could not be deleted. Another item may depend on it.',
    stockUsed:        'Stock deducted: {product} {qty} {unit}',
    mineTab:          'My data',
    materialsLabel:   'Materials',
  },

  // ── Investor Portal — daily overview ──────────────────────────────────────────
  investor: {
    title:               'Daily overview',
    subtitle:             'Project overview — read only',
    loading:              'Loading overview...',
    kpiLotes:             'Active plots',
    kpiActivities:        "Today's activities",
    kpiActivitiesSub:     'Recorded today',
    kpiCostToday:         "Today's cost",
    kpiPendingTasks:      'Pending tasks',
    kpiOverdueTasks:      'Overdue tasks',
    financeTitle:         'Investment and costs',
    kpiTotalCost:         'Total accumulated cost',
    kpiLotesConCosto:     'Plots with recorded cost',
    logisticsTitle:       'Logistics and export',
    kpiShipments:         'Total shipments',
    kpiInTransit:         'In transit',
    kpiHeld:              'On hold',
    kpiDelivered:         'Delivered',
    kpiTempAnomalies:     'Temperature excursions',
    lotesTitle:           'Plot map',
    modulesTitle:         'Zone map',
    noCrop:               'No crop assigned',
    activityTitle:        'Recent activity log',
    // F6 — Zone detail with sensors and drone
    plantingDate:         'Planting date',
    area:                 'Area',
    phenology:            'Phenology',
    phenologyStub:        'Plan vs. actual chart (F2)',
    soilSensors:          'Soil sensors',
    sensorDataStub:       'Moisture, EC, pH charts (F5)',
    droneVideo:           'Drone video',
    videoPlaceholder:     'Video thumbnail',
    openAnomalies:        'Open anomalies',
  },

  // ── Investor Portal — Yield and Harvest ───────────────────────────────────────
  investorYield: {
    title:       'Yield and Harvest',
    subtitle:    'Production, quality and waste for the project — read only',
    harvestTitle: 'Harvest and packing',
    mermaTitle:   'Waste and losses',
    tabResumen:  'Summary',
    tabAnalisis: 'Analysis',
    analysisTitle: 'Forecast scenarios by zone',
    analysisSubtitle: 'Conservative / base / ideal comparison at 7, 15 and 60 days — same deterministic forecast as Production.',
    analysisNoProject: 'Select a project in the top bar to see the analysis.',
    analysisNoModules: 'The active project has no zones with a forecast.',
    analysisForecastTitle: 'Expected production (next 15 days)',
    harvestMapTitle:    'Zone map by harvest status',
    harvestMapSubtitle: 'Operational status of each zone (F1) — used as a harvest-progress proxy until a per-zone harvest % exists.',
    lossReasonsTitle:   'Loss causes',
    analysisColModule: 'Zone',
    analysisColCrop:   'Crop',
    analysisScenarioLegend: 'Each cell: conservative / base / ideal.',
  },

  // ── Investor Portal — Logistics and Collections ───────────────────────────────
  investorLogistics: {
    title:              'Logistics and Collections',
    subtitle:            'Shipments, temperature and export status — read only',
    kpiTempAnomalies:    'Temperature excursions',
    kpiDelays:           'Delayed shipments',
    shipmentsTitle:      'Recent shipments',
    collectionsTitle:    'Collections',
    agingCurrent:        'Current',
    agingBucketPrefix:   'Past due',
    agingTotal:          'Total receivable',
    tableCustomer:       'Customer',
    telemetryTitle:      'Shipment telemetry',
    routeTitle:          'Route',
    temperatureTitle:    'Temperature in transit',
    inRangePct:          '% of time in range',
    noTelemetry:         'This shipment has no telemetry device assigned.',
    telemetryError:      'Could not load this shipment\'s telemetry. Try again later.',
    selectShipment:      'Select a shipment to see its telemetry.',
    currentPosition:     'Current position',
    tempRange:           'Allowed range',
    lastReading:         'Last reading',
  },

  // ── Investor Portal — Zone Detail (F1, no map yet) ──────────────────────────
  investorModules: {
    title:              'Zones',
    subtitle:            'Project zone map — read only',
    loading:             'Loading zones...',
    noModules:           'No zones registered yet.',
    noCrop:              'No crop assigned',
    allLotes: 'All lotes',
    loteFilter: 'Filter by lote',
    kmlInvalid: 'The KML has no valid polygon',
    kmlOutside: 'The KML boundary falls outside the parent zone or lote',
    kmlSaved: 'Boundary updated from KML',
    kmlError: 'Could not save the boundary',
    viewOnMap:           'View on map',
    daysSincePlanting:   'Days since planting',
    statusPREPARACION:   'Preparation',
    statusNORMAL:        'Normal',
    statusATENCION:      'Attention',
    statusCRITICO:       'Critical',
    statusCOSECHANDO:    'Harvesting',
    loadingVideo:        'Loading video…',
    videoLoadError:      "Couldn't load the video",
    notFound:            'Zone not found',
    back:                'Back',
    plantingDate:        'Planting date',
    area:                'Area',
    fenologyTitle:       'Phenology',
    soilSensorsTitle:    'Soil sensors',
    videoTitle:          'Drone video',
    anomaliesTitle:      'Open anomalies',
    telemetryTemp:         'Temperature (°C)',
    telemetryHumidity:     'Ambient humidity (%)',
    telemetrySoilMoisture: 'Soil moisture (%)',
    telemetryError:        'Could not load telemetry for this zone.',
    noTelemetry:           'No sensor readings for this zone yet.',
    historyTitle:          'History',
    historyFrom:           'From',
    historyTo:             'To',
    historyActivity:       'Activity',
    historyStatus:         'Status',
    historyAll:            'All',
    historyCurrent:        'Current',
    historyClosed:         'Closed',
    historyReasonCreate:   'Zone created',
    historyReasonUpdate:   'Crop change',
    historyReasonBackfill: 'Migration',
    historyEmpty:          'No history records match these filters.',
    historySince:          'Since',
    historyUntil:          'until',
    childrenTitle:         'Zones inside this lot',
    childrenEmpty:         'No subzones.',
  },

  // ── Investor Portal — Production (F2) ─────────────────────────────────────────
  investorProduction: {
    title:                'Production',
    subtitle:              'Plan vs. actual, forecast and anomalies for the zone — read only',
    noProjectSelected:     'Select a project in the top bar to see its production.',
    tabResumen:  'Summary',
    tabProgreso: 'Progress',
    progressTitle:    'Progress by parcel',
    progressSubtitle: 'All parcels of the active project — % complete and average deviation.',
    progressColLote:         'Parcel',
    progressColCrop:         'Crop',
    progressColCurrentStage: 'Current stage',
    progressColPct:          '% complete',
    progressColDeviation:    'Deviation (days)',
    progressCropTitle:       'Plan by crop',
    progressCropLoteCount:   'plots',
    progressGanttTitle:      'Plot matrix — plan vs. actual',
    progressGanttSubtitle:   'Every stage of every plot in the active project, sorted by plot.',
    progressForecastTitle:    'Production forecast by zone',
    progressForecastSubtitle: 'Base scenario at 15 days, by zone in the active project.',
    loadingModules:        'Loading zones...',
    noModules:             'No zones registered in this project.',
    noModulesHint:         'The Production view is built on Zone (F1) tracking. To see plan, forecast and anomalies for this project, register its zones in the GIS Map first.',
    moduleSelector:        'Zone',
    loadingPlan:           'Loading production plan...',
    noPlan:                'This zone has no production plan yet.',
    ganttTitle:            'Plan vs. actual by stage',
    ganttSubtitle:         'Gray bar = planned. Colored bar = actual (green on time, red delayed, blue ahead of schedule).',
    ganttPlanned:          'Planned',
    ganttActual:           'Actual',
    ganttOnTime:           'on time',
    ganttLate:             'delayed',
    ganttEarly:            'ahead of schedule',
    funnelTitle:           'Cumulative yield curve',
    funnelSubtitle:        '% of the cycle\'s total yield accumulated by the end of each stage',
    forecastTitle:         'Harvest forecast',
    forecastDisclaimer:    'The kg in each scenario are EXPECTED WITHIN the next N days (incremental), not the cycle total. Confidence drops as the horizon grows.',
    loadingForecast:       'Loading forecast...',
    noForecast:            'No forecast generated yet for this horizon.',
    horizon7:              'Next 7 days',
    horizon15:             'Next 15 days',
    horizon60:             'Next 60 days',
    confidence:            'Confidence',
    scenarioConservador:   'Conservative',
    scenarioBase:          'Base',
    scenarioIdeal:         'Ideal',
    anomaliesTitle:        'Open anomalies',
    loadingAnomalies:      'Loading anomalies...',
    noAnomalies:           'No open anomalies.',
    responsible:           'Responsible',
    severityBAJA:          'Low',
    severityMEDIA:         'Medium',
    severityALTA:          'High',
    severityCRITICA:       'Critical',
    anomalyTypeDESVIACION_FENOLOGICA: 'Phenological deviation',
    anomalyTypePLAGA:      'Pest',
    anomalyTypeRIEGO:      'Irrigation',
    anomalyTypeMAQUINARIA: 'Machinery',
    anomalyTypePERSONAL:   'Labor',
    anomalyTypeCLIMA:      'Weather',
    anomalyTypeCALIDAD:    'Quality',
    anomalyTypeSENSOR:     'Field sensor',
    anomalyTypeOTRO:       'Other',
  },

  investorFunding: {
    title:              'Investment and Funding',
    subtitle:            'Capital, drawdowns and fund requests — read only',
    loading:             'Loading...',
    noData:              'No data yet.',
    months:              'months',
    kpiCommitted:        'Committed capital',
    kpiDrawn:            'Drawn capital',
    kpiPending:          'Pending to draw',
    kpiCollected:        'Collected to date',
    kpiRoi:              'ROI',
    kpiIrr:              'Annual IRR',
    kpiPayback:          'Payback period',
    byCurrencyTitle:    'Metrics by currency',
    sourcesTitle:        'Capital sources',
    drawdownsTitle:      'Drawdowns',
    fundRequestsTitle:   'Fund requests',
    fundCategories: { INSUMOS: 'Supplies', MAQUINARIA: 'Machinery', MANO_DE_OBRA: 'Labor', INFRAESTRUCTURA: 'Infrastructure', OTRO: 'Other' } as Record<string, string>,
    tableName:           'Name',
    tableType:           'Type',
    tableCommitted:      'Committed',
    tableDrawn:          'Drawn',
    tablePending:        'Pending',
    tableDate:           'Date',
    tableAmount:         'Amount',
    tableStatus:         'Status',
    sourceTypeINVESTOR: 'Investor',
    sourceTypeBANK: 'Bank',
    sourceTypeFIRA: 'FIRA',
    sourceTypeOWN: 'Own',
    tableCategory:       'Category',
    drawdownPLANNED:     'Planned',
    drawdownRECEIVED:    'Received',
    drawdownCANCELLED:   'Cancelled',
    fundRequestPENDING:  'Pending',
    fundRequestAPPROVED: 'Approved',
    fundRequestREJECTED: 'Rejected',
    fundRequestCANCELLED:'Cancelled',
  },

  filterPanel: {
    selectAll:       'Select All',
    clear:           'Clear',
    clearAll:        'Clear filters',
    apply:           'Apply',
    applyFilterFor:  'Apply {label} filter',
    dateFrom:        'From',
    dateTo:          'To',
  },
  loteDetail: {
    loading:     'Loading lot...',
    notFound:    'Lot not found.',
    crop:        'Crop',
    hectares:    'Hectares',
    accCost:     'Accumulated cost',
    recentActs:  'Recent activities',
    costsByType: 'Costs by category',
    noCosts:     'No costs recorded.',
    dataTitle:       'Lot data',
    dataFilterDate:  'Date',
    dataFilterType:  'Type',
    dataFilterUser:  'User',
    dataEmpty:       'No data matches the filters.',
    dataOriginActivity: 'Activity',
    dataOriginMovement: 'Movement',
    budgetTitle:      'Base input budget',
    budgetEmpty:      'No base budget. Use "+ New budget" or apply a protocol to this lot.',
    budgetProtocol:   'Protocol',
    budgetCycle:      'Cycle',
    budgetChemicals:  'Chemicals / Pesticides',
    budgetFert:       'Fertilizers',
    budgetCostHaChem: 'Cost/ha chemicals',
    budgetCostHaFert: 'Cost/ha fertilizers',
    budgetTotal:      'Total budgeted',

    // NDVI
    ndviTitle:        'Vegetation Index (NDVI)',
    ndviMean:         'Average',
    ndviMin:          'Minimum',
    ndviMax:          'Maximum',
    ndviCloud:        'Cloud coverage',
    ndviDate:         'Image date',
    ndviTimeline:     'NDVI Trend (90 days)',
    ndviNoBoundary:   'No boundary defined. Set the lot polygon in the Map to enable NDVI.',
    ndviNoApiKey:     'Agromonitoring integration not configured.',
    ndviDisabled:     'NDVI is not enabled for this ranch; ask an administrator to enable it.',
    ndviNoData:       'No satellite images available for this period.',
    ndviLoading:      'Fetching satellite image...',
    ndviHealthGood:   'Healthy vegetation',
    ndviHealthMid:    'Moderate vegetation',
    ndviHealthLow:    'Low vegetation / stress',

    // Phase B — map layers, price history, risk panel
    mapLayersTitle:   'Map layers',
    layerNdvi:        'NDVI',
    systemTitle: 'System changes',
    systemEmpty: 'No changes recorded yet.',
    systemEvents: { LOTE_UPDATED: 'Lote edited', LOTE_BOUNDARY_CHANGED: 'Lote boundary', MODULE_CREATED: 'Zone created', MODULE_UPDATED: 'Zone edited', MODULE_BOUNDARY_CHANGED: 'Zone boundary', MODULE_DELETED: 'Zone deleted' },
    layerOrtho:       'Orthomosaic',
    orthoPlaceholder: 'No orthomosaic pipeline yet — this map will connect once a real georeferenced drone/satellite imagery source exists for this parcel.',
    priceTitle:       'Price history',
    priceSubtitle:    "Unit price of the company's sale orders for the same crop — approximate, the data model does not link sales to this specific parcel yet.",
    priceSubtitleWithCompany: "Unit price of {company}'s sale orders for the same crop — approximate, the data model does not link sales to this specific parcel yet.",
    priceNoData:      'No sale orders for this crop yet.',
    priceLatest:      'Latest price',
    riskTitle:        'Risks for this parcel',
    riskNoData:       'No open anomalies for this parcel.',

    // Detail tabs (Summary / Map / Activities / Finance)
    tabSummary:       'Summary',
    tabMap:           'Map',
    tabActivities:    'Activities',
    tabFinance:       'Finance',
    financeEmpty:     'No financial data for this lot yet.',
  },

  machinery: {
    labor: {
      title:           'Labor',
      lote:            'Lot',
      selectLote:      'Choose a lot',
      register:        'Register labor day',
      loading:         'Loading labor...',
      empty:           'No labor days recorded in this lot.',
      date:            'Date',
      laborType:       'Type',
      activity:        'Activity',
      workers:         'Workers',
      hoursPerWorker:  'Hours per worker',
      dailyRate:       'Daily rate per worker (MXN)',
      notes:           'Notes',
      totalCost:       'Cost',
      totalLabel:      'Total',
      save:            'Save',
      cancel:          'Cancel',
      saveError:       'Could not register the labor day. Check the data and try again.',
      types: { JORNAL: 'Day wage', DESTAJO: 'Piecework', CONTRATO: 'Contract' },
    },
    title:           'Machinery',
    loading:         'Loading machinery...',
    machinesUnit:    'machines',
    inUseUnit:       'in use',
    rentedUnit:      'rented',
    kpiTotal:        'Total machinery',
    kpiTotalSub:     'in use now',
    kpiRentals:      'Rentals',
    kpiRentalsSub:   'third-party',
    kpiDiesel:       'Diesel (current price)',
    kpiDieselFrom:   'since',
    kpiDieselNone:   'Not configured',
    kpiSessions:     'Recent sessions',
    kpiSessionsSub:  'last',
    fleet:           'Fleet',
    recentSessions:  'Recent sessions',
    catalog:         'Field activity catalog',
    rental:          'Rental',
    noSessions:      'No sessions recorded.',
    noCatalog:       'No catalog. Run the seed script to populate activities.',
    tableDevice:     'Machine',
    tableActivity:   'Activity',
    tableStart:      'Start',
    tableHours:      'Hours',
    tableDiesel:     'Diesel (L)',
    tableTotalCost:  'Total cost',
    tableCostPerHa:  'Cost/ha',
    tableStatus:     'Status',
    sessionClosed:   'Closed',
    sessionActive:   'Active',
    sessionOverrun:  'Overrun',
    overrunTooltip:  'Hour overrun without justification',
    timerStart: 'Start',
    timerStop: 'Stop',
    timerStartTitle: 'Start operation',
    timerStopTitle: 'Stop operation',
    timerLote: 'Lot',
    timerActivity: 'Activity',
    timerFuelLiters: 'Fuel (L)',
    timerFuelPrice: 'Price per liter',
    timerHourlyRate: 'Hourly rate (optional)',
    timerRunning: 'Running',
    timerErrStart: 'Could not start the operation',
    timerErrStop: 'Could not stop the operation',
    timerHectares: 'Hectares completed (optional)',
    timerBudgetCap: 'Max budget per ha (optional)',
    timerRentalCost: 'Daily rental (optional)',
    timerResultTitle: 'Operation stopped',
    timerCostTotal: 'Total cost',
    timerCostPerHa: 'Cost per ha',
    timerCostPerHaNA: 'Cost/ha not calculated: enter the hectares completed when stopping.',
    timerAlertLabel: 'Budget alert',
    timerAlertNONE: 'Within budget',
    timerAlertYELLOW: 'Yellow: close to budget',
    timerAlertORANGE: 'Orange: high',
    timerAlertRED: 'Red: critical',
    timerAlertEXCEEDED: 'Budget exceeded',
    timerNotPersisted: 'Informational summary: the expense was not posted to accounting (the bot confirms it through a draft).',
    timerClose: 'Close',
    timerRegister: 'Post to accounting',
    timerRegistered: 'Posted to accounting.',
    timerErrRegister: 'The cost could not be posted to accounting.',
    timerSelectLote: 'Select a lot',
    timerActivityRequired: 'Enter the activity',
    timerDuration: 'Duration',
    catalogActivity: 'Activity',
    catalogHrsHa:    'Hrs/ha',
    catalogDieselHa: 'Diesel/ha',
    catalogAlertPct: 'Alert %',
    types: {
      TRACTOR: 'Tractor',
      FUMIGADORA: 'Sprayer',
      DRON: 'Drone',
      CAMION: 'Truck',
      OTRO: 'Other',
    },
    fuels: {
      DIESEL: 'Diesel',
      GASOLINE: 'Gasoline',
      ELECTRIC: 'Electric',
    },
    newMachine: 'New machinery',
    emptyFleet: 'No machinery registered.',
    addFirst: 'Add first machine',
    rentedDefault: 'Rented',
    perDay: '{amount}/day',
    plateLabel: 'Plates: {plate}',
    form: {
      editTitle: 'Edit machinery',
      name: 'Name *',
      type: 'Type *',
      fuel: 'Fuel',
      model: 'Model',
      year: 'Year',
      plate: 'Plates',
      status: 'Status',
      isRental: 'This is rented machinery',
      rentalOwner: 'Owner / Lessor',
      rentalCost: 'Cost/day (MXN)',
      droneSection: '🚁 Drone details',
      droneModel: 'Make and model *',
      serial: 'Serial number *',
      tank: 'Tank capacity (L) *',
      coverage: 'Coverage per load (ha) *',
      pilotSection: '👨‍✈️ Certified pilot',
      pilotName: 'Full name *',
      pilotPhone: 'Phone *',
      license: 'AFAC/SICT license *',
      licenseExpiry: 'License expiry *',
      registration: 'Aircraft registration number',
      optional: 'Optional',
      saveChanges: 'Save changes',
      create: 'Create machinery',
      errCreate: 'Error creating machinery',
      errUpdate: 'Error updating machinery',
      droneModelRequired: 'The drone make/model is required',
      serialRequired: 'The serial number is required',
      tankRequired: 'The tank capacity is required',
      pilotNameRequired: 'The pilot\'s name is required',
      pilotPhoneRequired: 'The pilot\'s phone is required',
      licenseRequired: 'The license number is required',
      licenseExpiryRequired: 'The license expiry date is required',
      deleteTitle: 'Delete machinery',
      deleteConfirm: 'Delete {name}? This action cannot be undone.',
    },
  },

  machineryStatus: {
    AVAILABLE:      'Available',
    IN_USE:         'In use',
    MAINTENANCE:    'Maintenance',
    OUT_OF_SERVICE: 'Out of service',
  },

  merma: {
    title:              'Waste / Losses',
    loading:            'Loading waste...',
    kpiRejectedKg:      'Total rejected (kg)',
    kpiImpact:          'Economic impact',
    kpiRecords:         'Registered events',
    tableTitle:         'Waste history',
    tableDate:          'Date',
    tableLote:          'Field',
    tableBatch:         'Cycle',
    tableReason:        'Reason',
    tableKg:            'Rejected kg',
    tableBoxes:         'Boxes',
    tableImpact:        'Impact ($)',
    tableStatus:        'Status',
    noRecords:          'No waste recorded.',
    registerBtn:        '+ Register waste',
    closeBtn:           'Close report',
    closeConfirm:       'Close the waste report for this cycle? All records will be marked CLOSED.',
    registerTitle:      'Register waste',
    fieldLote:          'Field',
    fieldLoteZone:      'Lot or zone',
    fieldBatch:         'Cycle (batchId)',
    fieldReason:        'Reason',
    fieldKg:            'Rejected kg',
    fieldBoxes:         'Rejected boxes (optional)',
    fieldCostKg:        'Cost/kg ($ optional)',
    fieldNotes:         'Notes',
    filterAllLotes:     'All fields',
    filterAllBatches:   'All cycles',
    chartTitle:         'Waste by reason (kg)',
    successCreate:      'Waste recorded.',
    successClose:       'Report closed.',
    errorCreate:        'Error recording waste.',
    errorClose:         'Error closing report.',
    reasonCLIMA:           'Weather damage',
    reasonPLAGA:           'Pest/disease damage',
    reasonMECANICO:        'Mechanical damage',
    reasonRECHAZO_CALIDAD: 'Quality/size rejection',
    reasonPUDRICION:       'Rot/decay',
    reasonOTRO:            'Other',
    statusREGISTRADA:   'Recorded',
    statusVERIFICADA:   'Verified',
    statusCERRADA:      'Closed',
  },

  crop: {
    title:              'Crop Management',
    loading:            'Loading crop data...',
    tabPhenology:       'Phenology',
    tabScouting:        'Scouting / Pests',
    loteLabel:          'Lot',
    cicloLabel:         'Cycle',
    cicloPlaceholder:   'e.g. 2026-A',
    cicloSearch:        'Search',
    cicloUse:          'Use "{value}"',
    noCiclo:            'Enter the cycle to view history.',
    phenoTitle:         'Phenology history',
    phenoAddBtn:        '+ Record stage',
    phenoNoRecords:     'No stages recorded for this cycle.',
    phenoTableDate:     'Date',
    phenoTableStage:    'Stage',
    phenoTableCrop:     'Crop',
    phenoTableDap:      'DAP',
    phenoTableBy:       'Recorded by',
    phenoDialogTitle:   'Record phenological stage',
    assignTitle: 'Crop and planting',
    assignHintZones: 'This field has zones: set the crop and planting date on each one.',
    assignHintLote: 'This field has no zones: crop and planting are set for the whole field.',
    assignCropLabel: 'Crop',
    assignDateLabel: 'Planting date',
    assignZoneCol: 'Zone',
    assignNoCatalog: 'There are no crops in the catalog yet. Add one in the Catalog tab.',
    assignGoCatalog: 'Go to Catalog',
    needAssignment: 'This zone or lot has no crop in the selected cycle. Start the cycle in the Assignment tab.',
    startCycle: 'Start cycle',
    closeCycle: 'Close cycle',
    closeCycleTitle: 'Close the cycle',
    closeCycleConfirm: 'Close cycle {ciclo} of {zone}? The map will keep showing the crop faded as the last closed cycle until you start another; history is kept.',
    cycleOf: 'Cycle of {zone}',
    historyTitle: 'Cycle history',
    historyEmpty: 'This lot has no cycles yet.',
    historyZone: 'Zone',
    historyStatus: 'Status',
    historyClosedAt: 'Closed on',
    historyClosedBy: 'Closed by',
    cycleOpen: 'Open',
    cycleClosed: 'Closed',
    needAssignmentGo: 'Go to Assignment',
    assignCropType: 'Agronomic type (optional)',
    cropTypeLeaves: 'Leaves', cropTypeFruit: 'Fruit', cropTypeOther: 'Other', cropTypeAdd: 'add {text}',
    assignError: 'Could not save',
    removeCropError: 'Could not remove the crop',
    assignCatalog: 'Crop catalog',
    assignDeleteCrop: 'Delete crop',
    assignDeleteInUse: 'Cannot delete "{name}": it is in use ({usage}).',
    assignDeleteError: 'Could not delete the crop',
    tabAssignment: 'Assignment',
    tabCatalog: 'Catalog',
    allLotesTitle: 'All lots',
    loteAll: '<All>',
    allLotesHint: 'Pick a lot to assign a crop and see its cycle.',
    colLote: 'Lot',
    colHa: 'Ha',
    colCrop: 'Crop',
    colCycle: 'Cycle',
    cycleNone: 'No cycle',
    catalogTitle: 'Crop catalog',
    catalogNew: '+ New crop',
    catalogColName: 'Name',
    catalogColColor: 'Color',
    catalogEmpty: 'No crops in the catalog.',
    catalogEdit: 'Edit',
    catalogCreateTitle: 'New crop',
    catalogEditTitle: 'Edit crop',
    fsmaCovered: 'FSMA 204: covered',
    fsmaNotCovered: 'FSMA 204: not covered',
    fsmaUnknown: 'FSMA 204: not defined',
    catalogSaveError: 'Could not save the crop',
    catalogDeleteConfirm: 'Delete "{name}" from the catalog? Only possible if it is not in use.',
    phenoTableZone: 'Zone',
    zoneFilterLabel: 'Zone',
    zoneAll: 'All zones',
    usageLotes: '{n} lot(s)',
    usageModules: '{n} zone(s)',
    usageHistory: '{n} zone history record(s)',
    usagePhenology: '{n} phenology record(s)',
    phenoFieldCultivo:  'Crop type',
    phenoFieldStage:    'Stage',
    phenoFieldDap:      'DAP (days since planting)',
    phenoFieldNotes:    'Notes',
    phenoSuccess:       'Stage recorded.',
    phenoError:         'Error recording stage.',
    scoutTitle:         'Scouting — Pests & Diseases',
    scoutAddBtn:        '+ Record detection',
    scoutNoRecords:     'No detections recorded for this cycle.',
    scoutTableDate:     'Date',
    scoutTableName:     'Pest / Disease',
    scoutTableType:     'Type',
    scoutTablePressure: 'Pressure',
    scoutTablePct:      '% Area affected',
    scoutTableAction:   'Action taken',
    scoutDialogTitle:   'Record pest / disease',
    scoutFieldPestType: 'Type',
    scoutFieldName:     'Pest / disease name',
    scoutFieldPressure: 'Pressure level',
    scoutFieldPct:      '% Area affected (0-100)',
    scoutFieldAction:   'Action taken',
    scoutFieldNotes:    'Notes',
    scoutSuccess:       'Detection recorded.',
    scoutError:         'Error recording detection.',
    alertsTitle:        'Active alerts',
    alertsNone:         'No active alerts.',
    stagePREPARACION_SUELO: 'Soil preparation',
    stageSIEMBRA:           'Planting',
    stageGERMINACION:       'Germination',
    stageEMERGENCIA:        'Emergence',
    stageESTABLECIMIENTO:   'Establishment',
    stageVEGETATIVO:        'Vegetative',
    stageFLORACION:         'Flowering',
    stageFRUCTIFICACION:    'Fruiting',
    stageMADURACION:        'Maturation',
    stageCOSECHA:           'Harvest',
    stagePOST_COSECHA:      'Post-harvest',
    pressureBAJO:   'Low',
    pressureMEDIO:  'Medium',
    pressureALTO:   'High',
    presureCRITICO: 'Critical',
    pestPLAGA:       'Pest',
    pestENFERMEDAD:  'Disease',
    pestMALEZA:      'Weed',
    scoutEvidenceTitle: 'Photo evidence',
    scoutPhotoAlt: 'Scouting evidence',
    scoutPhotoAltZoom: 'Scouting evidence (enlarged)',
  },

  export: {
    kdeTitle: 'Traceability key data',
    kdeRecipientLocation: 'Recipient location',
    kdeOriginLocation: 'Origin location',
    kdeShipDate: 'Ship date',
    kdeRefDoc: 'Reference document',
    kdeTlcs: 'Lot codes',
    title:            'Exports',
    loading:          'Loading shipments...',
    kpiTotal:         'Total shipments',
    kpiInTransit:     'In transit',
    kpiHeld:          'Held',
    kpiDelivered:     'Delivered',
    kpiKg:            'KG shipped',
    kpiTransitDays:   'Avg transit days',
    tableContainer:   'Container',
    tableProduct:     'Product',
    tableDestination: 'Destination',
    tableKg:          'KG',
    tableBoxes:       'Boxes',
    tableStatus:      'Status',
    tableDeparted:    'Departed',
    tableArrived:     'Arrived',
    noData:           'No export shipments found.',
    detailTitle:      'Shipment detail',
    detailEventsTitle:'Event chain',
    docDownload:      'Download',
    statusDRAFT:                'Draft',
    statusPACKING:              'Packing',
    statusSEALED:               'Sealed',
    statusDEPARTED:             'Departed',
    statusCUSTOMS_SUBMITTED:    'At customs',
    statusCUSTOMS_CLEARED:      'Customs cleared',
    statusCUSTOMS_HOLD:         'Customs hold',
    statusFDA_SUBMITTED:        'At FDA',
    statusFDA_CLEARED:          'FDA cleared',
    statusFDA_HOLD:             'FDA hold',
    statusARRIVED:              'Arrived',
    statusDELIVERED:            'Delivered',
    statusCANCELLED:            'Cancelled',
    daysUnit: 'days',
  },

  transport: {
    title:            'Transport',
    loading:          'Loading shipments...',
    addBtn:           '+ Register shipment',
    noData:           'No shipments found.',
    filterPeriod:     'Period',
    filterLote:       'Lot',
    filterAllLote:    'All lots',
    tableDate:        'Date',
    tableDestination: 'Destination',
    tableDriver:      'Driver',
    tablePlate:       'Plate',
    tableKg:          'Total KG',
    tableBoxes:       'Boxes',
    tableStatus:      'Status',
    tableGradeA:      'Grade A',
    tableGradeB:      'Grade B',
    tableGradeC:      'Grade C',
    statusPENDING:    'Pending',
    statusEN_RUTA:    'En route',
    statusENTREGADO:  'Delivered',
    statusCANCELADO:  'Cancelled',
    markEnRuta:       'Mark en route',
    markEntregado:    'Mark delivered',
    markCancelado:    'Cancel',
    confirmAction:    'Confirm status change?',
    newShipmentTitle: 'Register shipment',
    fieldLoteId:      'Lot *',
    fieldLoteIdPlaceholder: 'lote_simiente_04',
    fieldDestination: 'Destination *',
    fieldDestinationPlaceholder: 'Central packing house, McAllen TX...',
    fieldDriver:      'Driver',
    fieldTruckPlate:  'Plate',
    fieldTransportCompany: 'Transport company',
    fieldScheduledAt: 'Scheduled date',
    fieldBoxesTitle:  'Boxes',
    fieldCalibre:     'Size',
    fieldBoxCount:    'Box count',
    fieldWeightKg:    'Weight (kg)',
    fieldGrade:       'Grade',
    addBoxRow:        '+ Add row',
    removeBoxRow:     'Remove',
    loteRequired:     'Lot is required',
    destinationRequired: 'Destination is required',
    boxesRequired:    'Add at least one box row',
    createShipmentError: 'Error registering shipment',
    create:           'Register',
  },

  fsmaExport: {
    title:           'FSMA 204 delivery',
    subtitle:        'KDE sheet by traceability lot code (TLC) for delivery within 24 hours, tracing and recall drill.',
    disabled:        'FSMA 204 traceability is not active for this company.',
    exportTitle:     'KDE sheet',
    rangeFrom:       'From',
    rangeTo:         'To',
    tlcOptional:     'TLC (optional)',
    exportXlsx:      'Download .xlsx',
    exportCsv:       'Download .csv',
    exportError:     'Could not generate the sheet',
    exportTruncated: 'The sheet has more packaging records than the export limit, so the export is incomplete.',
    traceTitle:      'Trace by TLC',
    traceTlc:        'Traceability lot code (TLC)',
    traceForward:    'Forward (where it went)',
    traceBackward:   'Backward (where it came from)',
    traceError:      'Could not run the trace',
    lotesLabel:      'Source lots',
    foliosLabel:     'Folios',
    unmatchedLabel:  'Unreconciled kg',
    noShipments:     'No shipments for this TLC.',
    none:            '—',
    drillTitle:      'Recall drill',
    drillStart:      'Drill start',
    drillFinish:     'Drill end',
    drillMerma:      'Loss (kg, optional)',
    drillNotes:      'Notes (optional)',
    drillSubmit:     'Record drill',
    drillError:      'Could not record the drill',
    drillMinutes:    'Elapsed time',
    drillPassed:     'Passed',
    drillFailed:     'Failed',
    drillNoThreshold: 'No verdict: the mass balance threshold is not configured',
    drillsHistory:   'Recorded drills',
  },

  traceabilityPlan: {
    title:              'Traceability plan',
    subtitle:           'Plan for the 21 CFR 1.1315 traceability rule (FSMA 204). Saved per company and exportable.',
    recordsLocation:    'Where records are kept',
    recordsFormat:      'Record format',
    foodIdentification: 'How foods on the Food Traceability List are identified',
    tlcProcedure:       'How traceability lot codes (TLC) are assigned',
    contactName:        'Point of contact: name',
    contactPhone:       'Point of contact: phone',
    contactEmail:       'Point of contact: email (optional)',
    version:            'Version',
    save:               'Save plan',
    saving:             'Saving...',
    saved:              'Plan saved',
    saveError:          'The plan could not be saved',
    exportXlsx:         'Export .xlsx (plan and farm map)',
    exportGeoJson:      'Export GeoJSON (field boundaries)',
    exportError:        'Export failed',
    noPlan:             'No plan saved yet.',
    readOnly:           'Read only: you need traceability write permission to edit.',
  },
  traceability: {
    destinationLabel: 'Destination:',
    buyerLabel: 'Buyer:',
    eventCOOLING: 'Cooling',
    kdeComplete: 'Key data complete',
    kdeMissingPrefix: 'Missing',
    kdeFields: {
      coolingLocation: 'Cooling location',
      coolingDate: 'Cooling date',
      product: 'Product',
      quantityKg: 'Quantity (kg)',
      recipientLocation: 'Recipient location',
      referenceDocType: 'Reference document type',
      referenceDocNumber: 'Reference document number',
      packingLocation: 'Initial packing location',
      harvesterName: 'Harvester',
      harvesterPhone: 'Harvester phone',
      tlc: 'Traceability lot code',
      harvestedAt: 'Harvest date',
      originLocation: 'Shipping origin',
      shipDate: 'Ship date',
    },
    foliosTitle: 'Folios — {lote}',
    title:              'Traceability',
    loading:            'Loading folios...',
    addFolioBtn:        '+ New folio',
    noData:             'No folios. Select a lot.',
    loteLabel:          'Lot',
    tableFolioId:       'Folio',
    tableProduct:       'Product',
    tableVariety:       'Variety',
    tableEstKg:         'Est. KG',
    tableStatus:        'Status',
    tableCreated:       'Created',
    detailTitle:        'Chain of custody',
    detailNoEvents:     'No events on this folio.',
    eventType:          'Event type',
    eventDesc:          'Description',
    eventTemp:          'Temperature',
    eventKg:            'KG',
    eventBy:            'Operator',
    addEventBtn:        '+ Add event',
    statusOPEN:         'Open',
    statusIN_TRANSIT:   'In transit',
    statusDELIVERED:    'Delivered',
    statusCLOSED:       'Closed',
    eventFIELD_OP:      'Field op',
    eventHARVEST:       'Harvest',
    eventPACKING:       'Packing',
    eventCONTAINER:     'Container',
    eventCUSTOMS:       'Customs',
    eventEXPORT:        'Export',
    formProduct:        'Product',
    formVariety:        'Variety',
    formEstKg:          'Estimated KG',
    formDestination:    'Destination',
    formBuyer:          'Buyer',
    folioCreated:       'Folio created.',
    eventAdded:         'Event added.',
    errorCreate:        'Error creating folio.',
    errorEvent:         'Error adding event.',
  },

  protocol: {
    title:              'Input Protocols',
    loading:            'Loading protocols...',
    noData:             'No protocols yet. Use "+ New protocol" to create one.',
    filterCrop:         'Crop',
    filterRancho:       'Ranch',
    filterAll:          'All',
    tableId:            'ID',
    tableCrop:          'Crop',
    tableCiclo:         'Cycle',
    tableType:          'Type',
    tableCostHa:        'Cost/ha',
    tableCreated:       'Created',
    tableSource:        'Source',
    detailTitle:        'Protocol detail',
    detailApps:         'Application calendar',
    detailDay:          'Day',
    detailProducts:     'Products',
    detailCostHa:       'Cost/ha',
    detailCatalog:      'Price catalog',
    detailName:         'Product',
    detailPresentation: 'Presentation',
    detailPrice:        'Unit price',
    detailCategory:     'Category',
    typeQUIMICO:        'Chemical',
    typeFERTILIZANTE:   'Fertilizer',
    typeAMBOS:          'Both',
    sourceTELEGRAM_DOC:   'Document',
    sourceTELEGRAM_PHOTO: 'Photo',
    sourceTELEGRAM_TEXT:  'Text',
    newProtocol:        '+ New protocol',
    newProtocolTitle:   'New protocol',
    editProtocolTitle:  'Edit protocol',
    deleteBtn:          'Delete',
    deleteTitle:        'Delete protocol',
    deleteConfirm:      'Delete this protocol? This action cannot be undone.',
    applyBtn:           'Apply to lot',
    applyTitle:         'Apply protocol as budget',
    applyDesc:          'The protocol cost will be added to the budget for the given lot and cycle.',
    applyModeLabel: 'What do you want to create?',
    applyModeBudget: 'Budget only',
    applyModeTasks: 'Budget with tasks',
    applyModeTasksHint: 'Besides the budget, the production plan and one task per application are created, dated from planting.',
    fieldLoteOrZone: 'Lot or zone *',
    loteOrZoneRequired: 'Select a lot or zone',
    applySuccessTasks: 'Protocol applied: budget and tasks created.',
    fieldZone:          'Zone *',
    fieldProject:       'Project *',
    fieldPlantingDate:  'Planting date *',
    zoneRequired:       'Select a zone',
    projectRequired:    'Select a project',
    plantingDateRequired: 'Planting date is required',
    fieldCrop:          'Crop',
    fieldRanchoId:      'Ranch ID',
    fieldCiclo:         'Cycle',
    fieldType:          'Type *',
    fieldCostoQuimico:  'Chemical cost ($/ha)',
    fieldCostoFert:     'Fertilizer cost ($/ha)',
    fieldCostoTotal:    'Total cost ($/ha) *',
    applicationsTitle:  'Applications',
    fieldDias:          'Days (e.g. 9-10)',
    fieldProductName:   'Product',
    fieldDose:          'Dose (e.g. 800ml)',
    fieldCostoHaApp:    'Cost/ha',
    addProduct:         '+ Product',
    removeProduct:      'Remove',
    addApplication:     '+ Add application',
    removeApplication:  'Remove application',
    catalogTitle:       'Price catalog',
    fieldPresentacion:  'Package size',
    fieldPrecioUnitario: 'Unit price',
    fieldCategory:      'Category',
    addCatalogItem:     '+ Add catalog item',
    removeCatalogItem:  'Remove',
    fieldLoteId:        'Lot *',
    fieldHectareas:     'Hectares *',
    typeRequired:       'Select a type',
    costoTotalRequired: 'Total cost is required',
    applicationsRequired: 'Add at least one application',
    loteRequired:       'Lot is required',
    hectareasRequired:  'Hectares must be greater than 0',
    cicloRequired:      'Cycle is required',
    createError:        'Error saving protocol',
    applyError:         'Error applying protocol',
    applySuccess:       'Protocol applied to budget.',
    create:             'Save',
    typeMARCO_FERTILIZACION: 'Fertilization frame',
    typeMARCO_PULVERIZACION: 'Spraying frame',
    sourceWEB: 'Web entry',
    sourceIMPORT_XLSX: 'Excel',
    newMarco: '+ New frame',
    newMarcoTitle: 'New frame',
    editMarcoTitle: 'Edit frame',
    tableName: 'Name',
    marcoName: 'Frame name *',
    marcoNamePlaceholder: 'E.g. YUCHOY 90',
    marcoKind: 'Frame type',
    marcoColumns: 'Products (columns)',
    marcoColumnName: 'Product',
    marcoColumnUnit: 'Unit',
    marcoAddColumn: '+ Product',
    marcoRemoveColumn: 'Remove column',
    marcoRows: 'Fertilization times',
    marcoAddRow: '+ Add time',
    marcoDuplicateRow: 'Duplicate row',
    marcoRemoveRow: 'Remove row',
    marcoTiempo: 'Time',
    marcoDias: 'Days after sowing',
    marcoTipoFert: 'Fertilizer type',
    marcoObs: 'Notes',
    marcoEtapa: 'Phenological stage',
    marcoFertHint: 'Amount per hectare in each column\'s unit. Use -1 for the base fertilizer.',
    marcoPulvHint: 'Products and doses per application (e.g. BELEAF 300g). Use Notes for pH or irrigation.',
    marcoNameRequired: 'The frame name is required',
    marcoColumnsRequired: 'Add at least one named product',
    marcoRowsRequired: 'Add at least one time',
    marcoDiasInvalid: 'Every row needs the days after sowing (a number)',
    marcoCantidadInvalid: 'Amounts must be numbers greater than or equal to 0',
    marcoSaveError: 'Error saving the frame',
    detailMarcoFert: 'Fertilization matrix',
    marcoNoApply: 'Frames carry no cost and are not applied as a budget.',
    duplicateBtn: 'Duplicate',
    duplicateTitle: 'Duplicate frame',
    duplicateDesc: 'A copy you can edit will be created. Change the name, crop or cycle if needed.',
    duplicateConfirm: 'Create copy',
    duplicateError: 'Error duplicating',
    duplicateCopySuffix: 'copy',
    importBtn: 'Import Excel',
    importTitle: 'Import frames from Excel',
    importIntro: 'Upload an .xlsx file with fertilization or spraying frames. Each sheet\'s type is detected and you will see a preview before saving.',
    importChoose: 'Choose file',
    importValidating: 'Validating...',
    importReadError: 'The file could not be read. Check that it is an .xlsx with the frame format.',
    importError: 'The frames could not be imported',
    importErrorsTitle: 'Check the file',
    importColSheet: 'Sheet',
    importColMessage: 'Problem',
    importColDetail: 'Content',
    importColStatus: 'Status',
    importDetected: 'frame(s) detected',
    importProductos: 'products',
    importTiempos: 'times',
    importAplicaciones: 'applications',
    importStatusNew: 'New',
    importReplace: 'Replace',
    importExistsHint: 'Frames that already exist are not changed unless you tick "Replace". Replacing keeps their crop, cycle and ranch.',
    importUnitNote: 'Fertilization amounts are loaded as kg/ha. If a column uses another unit, edit it afterwards in the frame.',
    importConfirm: 'Import',
    importImporting: 'Importing...',
    importDone: 'Import complete',
    importCreated: 'created',
    importReplaced: 'replaced',
    importSkipped: 'skipped',
    importClose: 'Close',
  },

  activityCategories: {
    PREPARACION_TERRENO: 'Land preparation',
    SIEMBRA:             'Planting & transplanting',
    FUMIGACION:          'Fumigation',
    FERTILIZACION:       'Fertilization',
    RIEGO:               'Irrigation',
    COSECHA:             'Harvest',
    TRANSPORTE:          'Transport',
    LABORES:             'General labor',
  },

  // ── Super Admin (tenant/company management) ───────────────────────────────────
  superAdmin: {
    accessDenied:       'Access denied — only available for SUPER_ADMIN',
    tenantCreated:      'Company created',
    tenantCreateError:  'Error creating company',
    tenantUpdated:      'Company updated',
    tenantUpdateError:  'Error updating',
    editTenant:         'Edit company',
    newTenant:          'New company',
    fieldName:          'Company name',
    fieldNamePlaceholder: 'E.g.: My Company',
    fieldCode:          'Code / URL slug',
    fieldCodeHint:      'E.g.: my-company → /my-company/login',
    fieldCodePlaceholder: 'my-company',
    fieldLogo:          'Logo URL',
    fieldLogoHint:      'Public image URL (PNG/SVG)',
    fieldLogoPlaceholder: 'https://cdn.example.com/logo.png',
    cancel:             'Cancel',
    saveChanges:        'Save changes',
    createTenant:       'Create company',
    title:              'Companies',
    tenantsRegisteredSingular: 'company registered',
    tenantsRegisteredPlural: 'companies registered',
    newTenantBtn:       'New company',
    searchPlaceholder:  'Search company or code...',
    loadingTenants:     'Loading companies...',
    noResults:          'No results',
    noTenants:          'No companies registered',
    colCompany:         'Company',
    colCode:            'Code',
    colStatus:          'Status',
    colCreated:         'Created',
    colActions:         'Actions',
    statusActive:       'Active',
    statusInactive:     'Inactive',
    login:              'Login',
    edit:               'Edit',
    deactivate:         'Deactivate',
    activate:           'Activate',
    loginError:         'Error entering',
    loginErrorBody:     'Could not access the company.',
    tenantDeactivated:  'Company deactivated',
    tenantActivated:    'Company activated',
    statusChangeError:  'Could not change status',
    activateNeedsCode:  'This company has no code (URL slug). Open Edit, assign a code and activate it again.',
    deactivateTitle:    'Deactivate company',
    deactivateBodySuffix: 'will become inactive: its users will not be able to log in, but all its data (lots, activities, finances) is preserved. You can reactivate it at any time.',
    // Security and configuration
    accessHoursTitle:   'Access time window',
    accessHoursHint:    'Access restriction by UTC hour. SUPER_ADMIN is exempt.',
    accessHoursStart:   'Start (HH:MM UTC)',
    accessHoursEnd:     'End (HH:MM UTC)',
    passwordPolicyTitle: 'Password policy',
    passwordPolicyHint: 'Minimum requirements for new passwords.',
    passwordMinLength:  'Minimum length',
    passwordMinLengthPlaceholder: '8',
    passwordMaxAgeDays: 'Expiration (days)',
    passwordMaxAgeDaysPlaceholder: '90 (optional)',
    featureFlagsTitle: 'Enabled features',
    featureFlagsHint: 'Control which modules/features this company can use.',
    demoAccessTitle:    'Demo access',
    demoAccessEnabled:  'Enabled — demo role selector is shown',
    demoAccessDisabled: 'Disabled',
    otpTitle:           'Two-step verification (OTP)',
    otpHint:            "Code sent through Telegram at sign-in (Google or password). With \"If linked\", people without Telegram sign in with just their password or Google.",
    otpModeRequired:    "Required — everyone needs Telegram to receive the code",
    otpModeIfLinked:    "If linked — the code only for people with Telegram linked",
    otpModeNever:       "Never — no code for anyone (useful in development)",
    otpEnabled:         'Enabled — requires OTP via Telegram',
    otpDisabled:        'Disabled — no OTP',
    enabled:            'Enabled',
    disabled:           'Disabled',
    featureLabels: {
      dashboard:    'Dashboard',
      field:        'Field & Plots',
      tasks:        'Tasks',
      chat:         'AI Chat',
      finance:      'Finance',
      users:        'User Management',
      settings:     'Settings',
      irrigation:   'Irrigation & Fertigation',
      harvest:      'Harvest & Packing',
      inventory:    'Inventory & Warehouse',
      machinery:    'Machinery',
      spray:        'Phytosanitary (Pests)',
      merma:        'Waste (Loss)',
      crop:         'Crop & Phenology',
      payroll:      'Payroll',
      porcino:      'Swine Module',
      export:       'Export',
      transport:    'Transport',
      traceability: 'Traceability',
      map:          'GIS Map',
      reports:      'Reports',
      protocol:     'Input Protocols',
      bulkImport:   'Bulk Import',
    },
    tenantDeleted: 'Company deleted',
    tenantDeleteError: 'Could not delete the company',
    deleteTenantTitle: 'Delete company',
    deleteTenantBody: 'Delete {name}? This action hides the company from the list. The data (users, lots, activities) is not erased and can be recovered manually if needed.',
    changeBtn: 'Switch',
    currentBtn: 'Current',
    currentTenantHint: 'You are already in this company',
    activateToLogin: 'Activate the company to be able to sign in',
  },

  // ── Ranchos Admin (rancho/warehouse CRUD) ──────────────────────────────────────
  pageTitle: {
    login: 'Sign in', demoLogin: 'Demo access', acceptInvite: 'Complete your registration', register: 'Sign up',
    dashboard: 'Dashboard', users: 'Users', ranchos: 'Ranchos', field: 'Field', map: 'Map',
    irrigation: 'Irrigation', inventory: 'Inventory',
  },
  ranchosAdmin: {
    pageTitle:          'Ranchos Administration',
    noPermission:       'You do not have permission to view this page.',
    editRancho:         'Edit',
    newRancho:          'New rancho',
    fieldName:          'Name *',
    fieldNamePlaceholder: 'Rancho La Simiente',
    fieldLocation:      'Location',
    fieldLocationPlaceholder: 'Ébano, SLP',
    fieldHectareas:     'Total hectares',
    fieldHectareasPlaceholder: '150.5',
    fieldLatitude:      'Latitude',
    fieldLongitude:     'Longitude',
    useMapBtn:          'View on map',
    pickPointHint:      'Click on the map to set the point',
    confirmPoint:       'Confirm point',
    useMyLocation: 'Use my location',
    locLocating: 'Locating…',
    locDenied: 'Location permission denied.',
    locUnavailable: 'Could not get your location.',
    locTimeout: 'Timed out getting your location.',
    locUnsupported: 'Your browser does not support geolocation.',
    usesFarmModulesSwitch: 'Uses Zone subdivision',
    usesFarmModulesHelp: 'If disabled, this rancho is managed at the Lote level only.',
    nameRequired:       'Name required',
    createError:        'Error creating rancho',
    statusActive: 'Active',
    updateError:        'Error updating rancho',
    cancel:             'Cancel',
    saveChanges:        'Save changes',
    createRancho:       'Create rancho',
    newWarehouse:       'New warehouse',
    fieldWarehouseName: 'Name *',
    fieldWarehouseNamePlaceholder: 'Main warehouse',
    fieldWarehouseLocationPlaceholder: 'Rancho Simiente — bay 2',
    warehouseCreateError: 'Error creating warehouse',
    editWarehouse: 'Edit warehouse',
    saveWarehouse: 'Save changes',
    warehouseUpdateError: 'Error updating warehouse',
    fieldWarehouseRancho: 'Scope',
    warehouseSharedOption: 'Shared (whole company)',
    warehouseSharedHint: 'A shared warehouse shows up in every ranch; the cost of each withdrawal is charged to the ranch of the field that consumes it.',
    colWarehouseScope: 'Scope',
    createWarehouse:    'Create warehouse',
    ranchosTitle:       'Ranchos',
    tabRanchos:         'Ranchos',
    tabWarehouses:      'Warehouses',
    tabTelegramGroups:  'Telegram groups',
    lotesManagedInField: 'Lots and their zones are managed in Field',
    colName:            'Name',
    colLocation:        'Location',
    colHectareas:       'Hectares',
    colActions:         'Actions',
    noRanchos:          'No ranchos registered',
    warehousesTitle:    'Warehouses',
    colId:              'ID',
    noWarehouses:       'No warehouses registered',
    deleteRanchoTitle:  'Delete rancho',
    deleteRanchoConfirmPrefix: 'Delete ',
    deleteRanchoConfirmSuffix: '? Associated lots will not be deleted but will be left without a rancho.',
    deleteWarehouseTitle: 'Delete warehouse',
    deleteWarehouseConfirmPrefix: 'Delete ',
    deleteWarehouseConfirmSuffix: '? Existing stock in this warehouse will not be deleted.',
    delete:             'Delete',
  },

  // ── Telegram groups linked to a rancho ────────────────────────────────────────
  telegramGroupsAdmin: {
    title:              'Telegram Groups',
    subtitle:           'A linked group determines the rancho/lote for every message sent there, regardless of the user\'s saved default.',
    newGroup:            '+ Link group',
    colLabel:            'Name',
    colChatId:           'Chat ID',
    colRancho:           'Rancho',
    colLote:             'Lote',
    colLinkedBy:         'Linked by',
    colLinkedAt:         'Date',
    colStatus:           'Status',
    colActions:          'Actions',
    noGroups:            'No linked groups',
    wholeRancho:         'Whole rancho',
    statusActive:        'Active',
    statusInactive:      'Inactive',
    deactivate:          'Deactivate',
    activate:            'Reactivate',
    colRole: 'Group role',
    roleAny: 'Any (by who writes)',
    delete: 'Delete',
    deleteTitle: 'Delete group',
    deleteBody: 'Delete the link of "{name}"? The Telegram group is not deleted: it just stops being tied to a ranch and the bot says goodbye. You can link it again later.',
    deleteError: 'Could not delete the group',
    ranchoDeleted: 'Ranch deleted',
    fieldChatId:         'Chat ID *',
    fieldChatIdPlaceholder: '-4123456789',
    addBotHint:          'Automatic linking: when you add the bot {bot} to a group (or to a channel, as an administrator), the bot asks you privately which ranch to link it to; this form is a backup.\n\nBefore linking: 1) add the bot {bot} to the Telegram group; 2) make it an administrator (otherwise Telegram does not deliver free text to it); 3) every member who writes there must have linked their own Telegram in Users.',
    fieldChatIdHint:     'Manual backup: you can also run /vincular_grupo inside the group to link it from the bot, or temporarily add @RawDataBot to see its Chat ID.',
    fieldRancho:         'Rancho *',
    fieldRanchoPlaceholder: 'Select a rancho',
    fieldLote:           'Lote (optional)',
    fieldLotePlaceholder: 'Whole rancho',
    fieldLabel:          'Group name *',
    fieldLabelPlaceholder: 'Rancho Simiente — General',
    fieldLanguage:       'Notice language',
    fieldLanguageHint:   'Language the bot uses for notices posted in this group.',
    colLanguage:         'Language',
    languageDefault:     'Default',
    chatIdRequired:      'Chat ID required',
    ranchoRequired:      'Select a rancho',
    labelRequired:       'Name required',
    createError:         'Error linking group',
    updateError:         'Error updating group',
    cancel:              'Cancel',
    save:                'Link',
    colPurpose:          'Purpose',
    colMode:             'Mode',
    purposeCapture:      'Capture',
    purposeSupervision:  'Supervision',
    modeAll:             'All',
    modeMention:         'Mentions only',
    configure:           'Configure',
    purposeDialogTitle:  'Group purpose — {name}',
    purposeDialogBody:   'Capture: the bot replies to what people write in the group. Supervision: the group only receives copies of users; the bot does not reply there.',
    purposeLabel:        'Purpose',
    modeLabel:           'How the bot replies',
    modeHint:            'All: replies to every message. Mentions only: replies only when mentioned.',
    scopeLabel:          'Copy scope',
    scopeRole:           'By role',
    scopeRancho:         'By rancho',
    roleLabel:           'Role copied',
    rolePlaceholder:     'Choose a role',
    roleRequired:        'Select a role',
    purposeSaveError:    'Could not save the group purpose',
    inviteSupervisor:    'Invite supervisor',
    inviteSent:          'Link sent to your private Telegram chat. Each person who uses it waits until you approve them.',
    inviteError:         'Could not create the invite link',
    watchedToggle:       'Watched',
    watchedTitle:        'Watched users and supervisors — {name}',
    watchedHint:         'Watched users are those whose copy goes to this group. Each has its own topic, created automatically.',
    colTopic:            'Topic',
    colLevel:            'Level',
    noWatched:           'No user receives copies in this group.',
    topicOpen:           'Open',
    topicClosed:         'Closed',
    topicNone:           'No topic yet',
    supervisorsTitle:    'Supervisors',
    noSupervisors:       'No supervisor list yet.',
    verified:            'Verified',
    unverified:          'Not verified',
    watchedLoadError:    'Could not load the watched list',
  },

  // ── Bulk Import ────────────────────────────────────────────────────────────────
  bulkImport: {
    pageTitle:          'Bulk Data Import',
    sectionTitle:       'Import from Excel',
    sectionDesc:        'Bulk upload ranchos, lotes, users, products, catalogs.',
    dropText:           'Drag the Excel file here or click to select',
    selectFile:         'Select file',
    processing:         'Processing...',
    formatsSupported:   'Supported formats: .xlsx, .xls, .csv',
    analyzing:          'Analyzing file...',
    validationErrorTitle: 'Validation error',
    validationErrorsPrefix: 'Found ',
    validationErrorsSuffix: ' validation error(s). Please review the data and try again.',
    readError:          'Could not read the file. Make sure it is a valid .xlsx/.csv with the Ranchos, Lotes, Usuarios, Catalogo sheets.',
    validationSuccess:  'Validation successful - No errors',
    importSuccessPrefix: 'Import completed successfully. ',
    importSuccessSuffix: ' records created.',
    importError:        'Error importing data.',
    summaryTitle:       'Import Summary',
    summaryRanchos:     'Ranchos',
    summaryLotes:       'Lotes',
    summaryUsuarios:    'Users',
    summaryProductos:   'Products',
    previewTitle:       'Preview (first 5 rows)',
    previewShowingPrefix: 'Showing 5 of ',
    previewShowingSuffix: ' rows',
    cancel:             'Cancel',
    confirmImport:      'Confirm and Import',
    importing:          'Importing...',
    templatesTitle:     'Available Templates',
    templatesDesc:      'Download the templates to fill them out correctly:',
    templateRanchos:    'Ranchos Template',
    templateLotes:      'Lotes Template',
    templateUsuarios:   'Users Template',
    templateProductos:  'Products Template',
    confirmDialogTitle: 'Confirm Import',
    confirmDialogBody:  'Are you sure you want to import this data?',
    confirmDialogNotePrefix: 'This action will create ',
    confirmDialogNoteSuffix: ' new records in the system.',
    confirm:            'Confirm',
    rowLabelPrefix:     'Row ',
    errorsMorePrefix:   '... and ',
    errorsMoreSuffix:   ' more error(s)',
    filaLabel:          'row',
    filasLabel:         'rows',
    downloadComplete:   'Download Complete Template',
  },

  // ── Register ──────────────────────────────────────────────────────────────
  register: {
    title: 'Sign up',
    titleFor: (tenantName: string) => `Sign up to ${tenantName}`,
    needInviteLink: 'You need an invite link from your ranch to sign up.',
    loading: 'Loading...',
    invalidCode: 'This registration code is invalid or expired.',
    stepTelegram: 'Step 1 of 2 · Verify your Telegram',
    stepAccess: 'Step 2 of 2 · Choose how you will sign in',
    telegramIntro: 'Open the Telegram bot, tap “Start” and share your contact with the button. Your number is taken from Telegram: you do not type it here.',
    openTelegram: 'Open Telegram',
    waitingTelegram: 'Waiting for you to share your contact in Telegram…',
    telegramLinkedWaitingPhone: 'Telegram linked. You still need to share your contact in the chat with the bot.',
    telegramTimeout: 'The verification wait timed out.',
    retry: 'Generate a new link',
    phoneVerified: (masked: string) => `Telegram verified ✓ ${masked}`,
    nameLabel: 'First name',
    lastNameLabel: 'Last name',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    confirmPasswordLabel: 'Confirm password',
    methodGoogle: 'Continue with Google',
    methodOr: 'or',
    methodPassword: 'Use email and password',
    submit: 'Send request',
    nameRequired: 'Enter your first name.',
    emailInvalid: 'Enter a valid email.',
    passwordTooShort: 'The password must be at least 8 characters.',
    passwordMismatch: 'Passwords do not match.',
    googleFailed: 'We could not verify your Google account. Please try again.',
    tooManyAttempts: 'Too many attempts. Wait a moment and try again.',
    genericError: 'Something went wrong, please try again.',
    pendingTitle: 'Request sent',
    pendingMessage: 'Your request is pending. An admin will review it and we will tell you on Telegram once it is approved.',
    webModeLink: 'Register without Telegram',
    webTitle: 'Request access',
    phoneLabel: 'Phone (optional)',
    phoneInvalid: 'Invalid phone. Use international format, for example +52 442 123 4567.',
    webPendingTitle: 'Request under review',
    webPendingMessage: 'Your request is under review. A company administrator will review it and give you access once it is approved.',
  },

  // ── Users ─────────────────────────────────────────────────────────────────
  users: {
    inviteTitle:            'Invite by Link',
    inviteTelegramLink:     'Link Telegram',
    deleteError:            'Could not delete the user',
    roleWarnSuperAdmin:     '⚠️ Super Admin gives full control of the platform, including other companies, and allows free database queries. Use it only for platform administrators.',
    roleWarnAdmin:          '⚠️ Admin can manage users, ranchos, finances and settings. Assign it only to whoever must administer.',
    roleWarnMany:           'This user has many roles. A field worker only needs "Field": extra roles grant access to functions they do not need.',
    ranchoFilterInfo:       'Showing {n} of {total} users with access to {rancho} (includes users with access to all ranchos).',
    allRanchosBadge:        'Access to all ranchos',
    unlinkTelegramButton:   'Unlink',
    unlinkTelegramTitle:    'Unlink Telegram',
    unlinkTelegramBody:     "Unlink {name}'s Telegram? They will stop being able to use the bot with this account until they link it again; that Telegram can then be linked to another account.",
    unlinkTelegramConfirm:  'Unlink',
    unlinkTelegramError:    'Could not unlink the Telegram',
    unlockResetButton: 'Unlock password',
    resetLockedLabel: 'Recovery locked',
    resetWaitingLabel: 'Recovery on hold',
    unlockResetDone: '✓ Unlocked',
    unlockResetError: 'Could not unlock',
    mergeButton: 'Merge',
    mergeTitle: 'Merge users',
    mergeBody: 'This user ({name}) stays and absorbs another one: any phone, Telegram, email and password it lacks, plus the other user\'s roles and access. The other user will be disabled. This cannot be undone.',
    mergePick: 'User to absorb',
    mergePickPlaceholder: 'Pick a user',
    mergeConfirm: 'Merge',
    mergeError: 'Could not merge',
    companyLinkTitle: "Company link",
    companyLinkHint: "Lets anyone request access on their own. They stay pending until an administrator approves them under Requests.",
    companyLinkGenerate: "Generate company link",
    companyLinkRegenerate: "Regenerate",
    companyLinkRegenerateHint: "Regenerating disables the previous link.",
    companyLinkError: "Could not generate the link",
    accountButton: 'Account',
    accountTitle: 'Account and access',
    accountHint: 'Phone, email, Telegram and password are this person\'s ways in.',
    accessNotSet: 'Not set',
    accessAdd: 'Add',
    accessChange: 'Change',
    accessRemove: 'Remove',
    accessSave: 'Save',
    accessPhone: 'Phone',
    accessEmail: 'Email',
    accessTelegram: 'Telegram',
    accessPassword: 'Password',
    accessTelegramLinked: 'Linked',
    accessTelegramLink: 'Link by invite',
    accessPasswordSet: 'Has a password',
    accessPasswordNone: 'No password',
    accessPasswordPlaceholder: 'New password (min. 8)',
    accessPasswordNeedsEmail: 'Add an email to be able to use a password.',
    accessError: 'Could not save the change',
    cannotDisableSelf:      'You cannot disable your own account',
    cannotRemoveOwnAdminRole: 'You cannot remove your own administrator role',
    selfRolesSessionWarning: 'Your session will be closed when your roles change is saved. Sign in again to see the changes.',
    confirmAndSave: 'Confirm and save',
    cannotDeleteSelf:       'You cannot delete your own account',
    inviteTelegramHint:     'Open this from the Telegram account you will use, then share your contact. If the phone already has an account, Telegram is linked to it.',
    addTitle:               'Add User',
    editTitle:              'Edit User',
    copyLabel:              'Supervision copy',
    copyNone:               'No copy',
    copyLevel1:             'Level 1: everything',
    copyLevel2:             'Level 2: filtered',
    copyLevel3:             'Level 3: alerts only',
    mainGroupLabel:         'Main copy group',
    mainGroupAuto:          'Automatic (by role or rancho)',
    copyNeedsUserId:        'This user has no identifier for copies; it cannot be changed here.',
    deleteTitle:            'Delete User',
    phoneLabel:             'Phone (WhatsApp / Telegram) *',
    phoneOnly:              'Phone',
    languageLabel:          'Language',
    inviteButton:           'Invite by Link',
    addButton:              '+ Add User',
    pendingRegistrationsTab:'Requests',
    pendingEmpty: 'There are no pending requests',
    pendingLoading: 'Loading requests...',
    pendingMethodGoogle: 'Google',
    pendingMethodPassword: 'Email and password',
    pendingRanchosLabel: 'Allowed ranchos',
    pendingRanchosPlaceholder: 'All ranchos',
    pendingLotesLabel: 'Allowed lots',
    pendingLotesPlaceholder: 'Select lots',
    pendingNeedLote: 'A field worker needs at least one lot; without lots they will have no access to any.',
    grantExpensesApprove: 'Can approve general expenses',
    pendingApproveError: 'The action could not be completed. Please try again.',
    pendingAlreadyResolved: 'This request was already resolved by someone else.',
    pendingRejectTitle: 'Reject request',
    pendingRejectBody: 'Reject the request from {name}? They will be told on Telegram.',
    activityTab: 'Activity',
    activityColName: 'Name',
    activityColPhone: 'Phone',
    activityColTelegram: 'Telegram',
    activityColLastSeen: 'Last activity',
    activityNever: 'Never',
    activityToday: 'Today',
    activityDaysAgo: '{n} days ago',
    activityEmpty: 'No activity data',
    approve:                'Approve',
    reject:                 'Reject',
    confirm:                'Confirm',
    pageTitle: 'Users',
    systemUsers: 'System users',
    loadingUsers: 'Loading users...',
    telegramLinked: '✓ Telegram linked',
    enable: 'Enable',
    disable: 'Disable',
    rolesLabel: 'Roles *',
    emailOptional: 'Email (optional)',
    emailPlaceholder: 'user@example.com',
    namePlaceholder: 'e.g. John Smith',
    nameTooLong: 'The name cannot be longer than {max} characters',
    phoneRequired: 'The phone number is required',
    rolesRequired: 'Select at least one role',
    createError: 'Error creating user',
    saveError: 'Error saving changes',
    inviteError: 'Could not generate the invitation',
    inviteShareMessage: 'I\'m inviting you to join Agrovid — use this link to create your account:',
    inviteEmailSubject: 'Invitation to Agrovid',
    inviteShareHint: 'Share this link — it expires in 7 days and can only be used once:',
    copied: '✓ Copied',
    copyLink: 'Copy link',
    shareTelegram: 'Send via Telegram',
    shareWhatsapp: 'Send via WhatsApp',
    shareEmail: 'Send via email',
    shareEmailHint: 'Add an email above to prefill the recipient',
    generateLink: 'Generate link',
    deleteActiveWarning: '{name} is active. You must disable them first before deleting — this prevents accidentally removing someone who is still working.',
    deleteConfirmBody: 'Delete {name}? This action cannot be undone.',
    ranchosPlaceholder: 'Select ranchos',
    accessAllRanchos: '✓ Access to all ranchos (click to restrict)',
    accessRestrictRanchos: 'Restrict to selected ranchos (click to grant full access)',
    defaultRanchoLabel: 'Default active rancho',
    defaultRanchoNone: 'No ranchos available',
    defaultRanchoUnset: 'Not set — uses the first of "Allowed ranchos"',
    defaultRanchoHint: 'This is the rancho the Telegram bot uses when the user does not explicitly pick one with /rancho.',
    accessAllLotes: '✓ Access to all lots (click to restrict)',
    accessRestrictLotes: 'Restrict to selected lots (click to grant full access)',
    saveAccess: 'Save access',
  },

  // ── Vocabulary ─────────────────────────────────────────────────────────────
  vocabulary: {
    title:                  'Company Vocabulary',
    titleWithCompany:       '{company} Vocabulary',
    description:            'Terms and expressions the bot learns to correct voice transcriptions.',
    pendingTitle:           'Pending Review Suggestions',
    activeTerms:            'Active Terms',
    aliasDetected:          'Detected Alias',
    termSuggested:          'Suggested Term',
    origin:                 'Source',
    actions:                'Actions',
    approve:                'Approve',
    reject:                 'Reject',
    addTerm:                'Add Term',
    term:                   'Term',
    aliases:                'Aliases',
    category:               'Category',
    scope:                  'Scope',
    noTerms:                'No terms registered',
    delete:                 'Delete',
    loading:                'Loading...',
    cancel:                 'Cancel',
    save:                   'Save',
    user:                   'User',
    tenant:                 'Company',
    autoLearned:            'Auto-learned',
    manual:                 'Manual',
    dialogTitle:            'Add Vocabulary Term',
    canonicalTerm:          'Canonical Term',
    placeCanonical:         'e.g. Abamectin',
    aliasesComma:           'Aliases (comma-separated)',
    placeAliases:           'e.g. abama, avamectin',
    placeCategory:          'e.g. chemical',
  },

  // ── Devices (F5) ────────────────────────────────────────────────────────────
  drones: {
    title:       'Drone flights',
    zoneFilter:  'Lot or zone',
    subtitle:    'Drone spraying: area, liters and replay of every flight',
    flights:     'Flights',
    hectares:    'Hectares treated',
    liters:      'Liters applied',
    flightHours: 'Flight hours',
    replay:      'Flight replay',
    play:        'Play',
    pause:       'Pause',
    restart:     'Restart',
    speed:       'Speed',
    elapsed:     'Time',
    sprayed:     'Treated',
    battery:     'Battery',
    colDate:     'Date',
    colLote:     'Plot',
    colHa:       'Hectares',
    colLiters:   'Liters',
    colDuration: 'Duration',
    colBattery:  'Battery',
    colAircraft: 'Aircraft',
    empty:       'No flights recorded.',
    simulated:   "Illustrative path using the flight's area, liters and row spacing.",
    flightOf:    'Flight of',
  },
  droneImport: {
    button:         'Import flights (Excel)',
    title:          'Import drone flights',
    intro:          'Upload an Excel file with one flight per row. It is validated first and you will see a preview; nothing is saved until you confirm.',
    template:       'Download template',
    chooseFile:     'Choose .xlsx file',
    validating:     'Validating…',
    summary:        'valid rows',
    errorsTitle:    'Fix these errors and upload the file again',
    colRow:         'Row',
    colField:       'Field',
    colMessage:     'Problem',
    colDate:        'Date',
    colType:        'Type',
    colLote:        'Lote',
    colValves:      'Valves',
    colHa:          'Ha',
    colLiters:      'Liters',
    confirm:        'Import',
    importing:      'Importing…',
    close:          'Close',
    readError:      'The file could not be read. Use the template and make sure it has the fecha and lote columns.',
    importError:    'Import failed. Check the file and try again.',
    done:           'Flights created',
    skipped:        'already existed',
    typeAPPLICATION: 'Application',
    typeVIDEO:       'Video',
    typeSCOUTING:    'Scouting',
  },
  devices: {
    title:            'Devices',
    subtitle:          'Sensors, trackers and drones reporting to the platform',
    empty:             'No devices registered. Add the first one to start receiving telemetry.',
    add:               'New device',
    name:              'Name',
    type:              'Type',
    capabilities:      'Capabilities',
    binding:           'Bound to',
    unbound:           'Unbound',
    bindingIdPlaceholder: 'Shipment/zone/lote ID',
    bindingTargetPlaceholder: 'Select the linked target',
    status:            'Status',
    lastSeen:          'Last seen',
    never:             'Never',
    apiKeyPrefix:      'Key',
    rotateKey:         'Rotate key',
    rotateConfirm:     'Rotating makes the current key stop working immediately. The device will need to be reconfigured with the new one. Continue?',
    keyShownOnce:      'Copy this key now — it cannot be shown again. If you lose it, you will have to rotate it.',
    keyCopied:         'Key copied',
    copy:              'Copy',
    statusACTIVE:      'Active',
    statusINACTIVE:    'Inactive',
    statusREVOKED:     'Revoked',
    typeGPS_TRACKER:   'GPS tracker',
    typeTHERMOGRAPH:   'Thermograph',
    typeSOIL_SENSOR:   'Soil sensor',
    typeWEATHER_STATION: 'Weather station',
    typeDRONE:         'Drone',
    typeCARRIER_API:   'Carrier API',
    typeRAIN_GAUGE:    'Rain gauge',
    save:              'Save',
    cancel:            'Cancel',
  },

  // ── Demo Login (demo role selector) ────────────────────────────────────────────
  demoLogin: {
    unavailable: 'The demo is not available in this environment.',
    goToLogin: 'Go to sign in',
    roleLabels: {
      ADMIN:             'Admin',
      AGRONOMIST:        'Agronomist',
      SUPERVISOR:        'Field Supervisor',
      WAREHOUSE_MANAGER: 'Warehouse Keeper',
      FIELD_WORKER:      'Field Worker',
      SUPER_ADMIN:       'Super Admin',
      AUDITOR:           'Auditor',
      HR:           'HR',
      GUARD:           'Guard',
      INVESTOR:          'Investor',
    },
    roleDesc: {
      ADMIN:             'Full administrative access: users, finance and settings',
      AGRONOMIST:        'Crop protocols, phenology and pest monitoring',
      SUPERVISOR:        'Field operations and task management',
      WAREHOUSE_MANAGER: 'Inventory, stock and FEFO handling',
      FIELD_WORKER:      'Basic access to log operations and activities',
      SUPER_ADMIN:       'Full platform control',
      AUDITOR:           'Read-only access to the entire system',
      HR:           'Staff, enrollment and face attendance',
      GUARD:           'Gate: checks attendance passes and logs vehicles',
      INVESTOR:          'Investor portal: production, investment and logistics, read-only',
    },
    enterAs: 'Enter as',
    subtitle: 'Select the role you want to explore the demo with',
    connectError: 'Could not connect to the demo server.',
  },

  // ── Phase C — Documents (cross-domain repository) ──────────────────────────
  documents: {
    title:    'Documents',
    subtitle: 'Company document repository, grouped by source.',
    subtitleWithCompany: "{company}'s document repository, grouped by source.",
    loading:  'Loading documents...',
    noDocuments: 'No documents.',
    folderSHIPMENT:       'Shipments',
    folderINVOICE:        'Invoices',
    folderMODULE:         'Zones',
    folderPURCHASE_ORDER: 'Purchase orders',
    folderOTHER:          'Other',
    docCountSuffix: 'documents',
    colType: 'Type',
    colDate: 'Date',
    colGeneratedBy: 'Generated by',
    download: 'Download',
    back: 'Back',
    kpiTotal: 'Total documents',
    kpiTopFolder: 'Largest folder',
    kpiLast30d: 'Uploaded (last 30 days)',
    kpiDocTypes: 'Document types',
    recentTitle: 'Recent documents',
  },

  // ── Phase C — Risks (anomalies + waste aggregation) ─────────────────────────
  riesgos: {
    title:    'Risks',
    subtitle: 'Consolidated open-risk view: production anomalies and recent waste events.',
    anomaliesTitle: 'Open anomalies',
    loadingAnomalies: 'Loading anomalies...',
    noAnomalies: 'No open anomalies.',
    mermaTitle: 'Highest-impact recent waste',
    loadingMerma: 'Loading waste records...',
    noMerma: 'No recent waste records.',
    complianceTitle: 'Compliance (PHI/REI/MRL)',
    complianceNote: 'The system blocks out-of-compliance operations (PHI/REI/MRL) in real time, but does not yet keep a queryable history of blocks — this section will be enabled once that log exists.',
    responsible: 'Responsible',
    impact: 'Impact',
    kpiOpen: 'Open risks',
    kpiCritical: 'Critical',
    kpiHigh: 'High',
    kpiMedium: 'Medium',
    kpiLow: 'Low',
    kpiWithResponsible: 'With assigned owner',
    kpiMermaImpact: 'Waste impact (total)',
    moduleRiskTitle: 'Risk map by zone',
    noActiveProject: 'Select an active project to see the risk map by zone.',
    noRisk: 'No risk',
    categoryTitle: 'Risks by category',
    tableTitle: 'Risk and alert log',
    colDate: 'Date',
    colCategory: 'Category',
    colDescription: 'Description',
    colLevel: 'Level',
    colResponsible: 'Responsible',
    colStatus: 'Status',
    statusABIERTA: 'Open',
    statusEN_PROCESO: 'In progress',
    statusRESUELTA: 'Resolved',
    statusDESCARTADA: 'Discarded',
  },

  // ── General expenses — approval queue (B2) ──────────────────────────────────
  // ── Gastos generales — dashboard (B3) ───────────────────────────────────────
  calculators: {
    title: 'Calculators',
    subtitle: 'Calculate costs, yield and cash flow with your assumptions.',
    calculate: 'Calculate',
    result: 'Result',
    fromSystem: 'del sistema',
    loadError: 'Could not load the catalog.',
    runError: 'Could not calculate.',
  },
  expensesDashboard: {
    title: 'General expenses',
    subtitle: 'Summary of confirmed expenses in base currency.',
    from: 'From',
    to: 'To',
    rancho: 'Ranch',
    lote: 'Lot',
    all: 'All',
    kpiTotal: 'Period total',
    kpiCount: 'Confirmed expenses',
    kpiPending: 'To approve',
    byCategory: 'By category',
    byCounterparty: 'By counterparty',
    byMonth: 'By month',
    empty: 'No data for the period.',
    loading: 'Loading...',
    error: 'Could not load the summary.',
    excluded: '{n} expense(s) with another base currency were excluded.',
    noCounterparty: 'No counterparty',
    colCategory: 'Category',
    colCounterparty: 'Counterparty',
    colMonth: 'Month',
    colTotal: 'Total',
    viewPending: 'View pending',
  },

  expenseApprovals: {
    title: 'Expenses to approve',
    subtitle: 'General expenses recorded without direct approval. Approve or reject them.',
    loading: 'Loading...',
    empty: 'No expenses waiting for approval.',
    colDate: 'Date',
    colDescription: 'Description',
    colCounterparty: 'Supplier',
    colAmount: 'Amount',
    colScope: 'Assignment',
    colRegisteredBy: 'Registered by',
    scopeTenant: 'Company',
    scopeRancho: 'Ranch',
    scopeLote: 'Lot',
    approve: 'Approve',
    reject: 'Reject',
    rejectTitle: 'Reject expense',
    rejectBody: 'The expense will be rejected and will not count in reports. The reason is optional.',
    reasonLabel: 'Reason (optional)',
    cancel: 'Cancel',
    conflict: 'Someone else already resolved this expense. The list was refreshed.',
    actionError: 'The action could not be completed. Try again.',
  },

  // ── Phase D — Costs (investor portal) ────────────────────────────────────────
  costos: {
    title: 'Costs',
    subtitle: 'Cost control, cash flow and accounts receivable.',
    loading: 'Loading...',
    noData: 'No data.',
    kpiPlanned: 'Total budget',
    kpiExecuted: 'Spent so far',
    kpiExecPct: '% Executed',
    kpiRevenue: 'Estimated revenue',
    kpiMargin: 'Gross margin',
    kpiMarginPct: '% Margin',
    kpiGeneralExpenses: 'General expenses',
    indirectsTitle: 'Allocated overhead by lot',
    colLote: 'Lot',
    colIndirect: 'Overhead',
    colIndirectPerHa: 'Overhead/ha',
    excludedCurrencyNote: 'Some general expenses use a different base currency and are not included in these totals:',
    compositionTitle: 'Cost composition (spent)',
    compositionCenterLabel: 'total cost',
    cashflowTitle: 'Projected cash flow',
    budgetTitle: 'Budget vs. actual by category',
    colCategory: 'Category',
    colPlanned: 'Planned',
    colExecuted: 'Spent',
    colVariance: 'Variance',
    colPct: '% Exec.',
    receivablesTitle: 'Accounts receivable',
    agingCurrent: 'Current',
    agingBucketPrefix: 'Overdue',
    agingTotal: 'Total receivable',
  },
  // Reglas PHI/REI/MRL de la empresa
  complianceRules: {
    title: 'Compliance rules',
    subtitle: 'PHI (days before harvest), REI (hours before re-entry) and MRL limits per agrochemical, crop and market. The system blocks any application or harvest without a rule.',
    newRule: 'New rule',
    importExcel: 'Import Excel',
    filterProduct: 'Product',
    filterCrop: 'Crop',
    filterMarket: 'Market',
    allProducts: 'All products',
    allMarkets: 'All markets',
    colProduct: 'Product',
    colCrop: 'Crop',
    colMarket: 'Market',
    colPhi: 'PHI (days)',
    colRei: 'REI (hours)',
    colMrl: 'MRL',
    colScope: 'Source',
    colActions: 'Actions',
    scopeTenant: 'Company',
    scopeGlobal: 'General (read-only)',
    edit: 'Edit',
    delete: 'Delete',
    deleteTitle: 'Delete rule',
    deleteConfirm: 'Delete the rule for {product} on {crop} ({market})? Without a rule, the system will block applications and harvests with that product.',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    loading: 'Loading rules...',
    emptyTitle: 'There are no compliance rules yet',
    emptyHint: 'Add one rule per agrochemical, crop and market, or import the Excel template. Without a rule, the system blocks the application or harvest for safety.',
    emptyNoProducts: 'First add your agrochemicals in Inventory (Agrochemical category).',
    noMatches: 'No rule matches the filters.',
    dialogNewTitle: 'New compliance rule',
    dialogEditTitle: 'Edit compliance rule',
    fieldProduct: 'Agrochemical',
    fieldCrop: 'Crop',
    fieldCropHint: 'Choose from the catalog or type a new one.',
    fieldMarket: 'Market',
    fieldPhi: 'PHI (days before harvest)',
    fieldRei: 'REI (hours before re-entry)',
    fieldMrl: 'MRL (maximum residue limit)',
    fieldMrlUnit: 'Unit',
    fieldNotes: 'Notes',
    requiredFields: 'Fill in product, crop, market, PHI, REI and MRL.',
    errorGeneric: 'The rule could not be saved.',
    importTitle: 'Import rules from Excel',
    importHelp: 'Download the template, fill one row per rule (Producto can be the agrochemical name or id) and upload it. Review the preview before confirming.',
    downloadTemplate: 'Download template',
    chooseFile: '.xlsx file',
    preview: 'Preview',
    previewing: 'Checking...',
    previewSummary: '{total} rows: {valid} valid ({toCreate} new, {toUpdate} updates), {errors} with errors.',
    colRow: 'Row',
    colStatus: 'Status',
    statusOk: 'OK',
    actionCreate: 'New',
    actionUpdate: 'Update',
    fixErrors: 'Fix the rows with errors and check the file again to be able to confirm.',
    confirmImport: 'Confirm import',
    importing: 'Importing...',
    imported: 'Import done: {created} new, {updated} updated.',
    failedRows: 'These rows were not saved: {rows}.',
    close: 'Close',
  },
};
